import "./styles.css";
import { api, authExpiredEvent, type ApiResult } from "./api";
import { env } from "./env";
import {
  clearSession,
  getToken,
  getUser,
  saveSession,
  type SessionUser,
} from "./auth";
import { applyLang, currentLanguage, t, tFor, type Language } from "./i18n";
import {
  downloadHistoryPdf,
  type HistoryPdfSession,
  type HistoryPdfSettings,
} from "./history-pdf";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("App root was not found");
const root: HTMLDivElement = app;
document.documentElement.lang = currentLanguage();
type Page =
  | "live"
  | "dashboard"
  | "history"
  | "my-history"
  | "manager-report"
  | "schedules"
  | "my-schedule"
  | "users"
  | "platforms"
  | "products"
  | "settings"
  | "audit";
type Role = "admin" | "streamer";
type Employment = "Full-time" | "Part-time";
type Category =
  "Food Supplement 1" | "Food Supplement 2" | "Cosmetic" | "Other Brand";
interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  active: boolean;
  employmentType: Employment;
  activationPending: boolean;
  profileImageKey: string | null;
}
interface Platform {
  id: string;
  name: string;
  url: string;
  color: string;
  icon: string | null;
  profileImageKey: string | null;
  active: boolean;
}
interface Product {
  id: string;
  name: string;
  category: Category;
  active: boolean;
}
interface Reminder {
  title: string;
  items: string[];
}
interface UsersResult extends ApiResult {
  users: User[];
}
interface ActivationResendResult extends ApiResult {
  delivery?: "sent" | "queued";
}
interface PlatformsResult extends ApiResult {
  platforms: Platform[];
}
interface ProductsResult extends ApiResult {
  products: Product[];
  activeCounts: Record<Category, number>;
}
interface ProductActionResult extends ApiResult {
  liveCount?: number;
  liveNames?: string[];
  active?: boolean;
}
interface StatusResult extends ApiResult { active?: boolean; }
interface UserCreateResult extends ApiResult { user?: { id: string; active: boolean }; }
interface PlatformCreateResult extends ApiResult { platformId?: string; }
interface ProfileUploadResult extends ApiResult { key?: string; userId?: string; }
interface PlatformImageUploadResult extends ApiResult { key?: string; platformId?: string; }
interface PasswordChangeResult extends ApiResult { token?: string; }
interface SettingsResult extends ApiResult {
  settings: Record<string, string>;
}
interface RecalculateResult extends ApiResult {
  updated?: number;
  monthStart?: string;
}
interface TelegramTestResult extends ApiResult {
  sent?: number;
  failed?: number;
  reason?: string;
}
interface LoginResult extends ApiResult {
  token: string;
  user: SessionUser;
}
interface MeResult extends ApiResult {
  user: SessionUser;
}
interface StartSessionResult extends ApiResult {
  sessionId?: string;
}
interface EndDraftFile {
  name: string;
  type: string;
  lastModified: number;
  blob: Blob;
}
interface EndDraft {
  key: string;
  sessionId: string;
  userId: string;
  createdAt: number;
  updatedAt: number;
  fields: Record<string, string>;
  face: EndDraftFile | null;
  images: EndDraftFile[];
}
interface StartDraft {
  key: string;
  userId: string;
  createdAt: number;
  updatedAt: number;
  fields: Record<string, string>;
  face: EndDraftFile | null;
  images: EndDraftFile[];
}
interface ActiveSession {
  sessionId: string;
  startTime: string;
  liveUrl: string;
  platformName: string;
  showcasePlan: Showcase;
}
interface ActiveResult extends ApiResult {
  session: ActiveSession | null;
}
interface ValidationPreviewResult extends ApiResult {
  derived?: { durationMins: number; ot: number; commission: number; totalRevenue: number; roi: number };
}
interface Showcase {
  supp1: string[];
  supp2: string[];
  cosmetic: string[];
  other: string[];
}
interface ReportSession {
  sessionId: string;
  date: string;
  staffId: string;
  staffName: string;
  platformId: string;
  platformName: string;
  platformColor: string | null;
  platformIcon: string | null;
  liveUrl: string;
  startTime: string;
  endTime: string | null;
  durationMins: number;
  ot: number;
  verenaSales: number;
  affiliateSales: number;
  commission: number;
  totalRevenue: number;
  roi: number;
  audience: number;
  orderNumbers: string;
  showcase: Showcase;
  showcasePlan: Showcase;
  startImages: string[];
  endImages: string[];
  startFaceImage: string | null;
  endFaceImage: string | null;
  startNote: string;
  endNote: string;
  status: "ACTIVE" | "COMPLETED";
  validated: boolean;
  validatedBy: string | null;
  validatedAt: string | null;
  autoClosed: boolean;
  autoEnded: boolean;
  lastEditedBy: string | null;
  lastEditedAt: string | null;
  substituteForName: string | null;
  employmentType?: Employment;
  payEmploymentType?: Employment | null;
  editable: boolean;
}
interface SessionsResult extends ApiResult {
  sessions: ReportSession[];
  pdfSettings?: HistoryPdfSettings;
}
interface ReportTotals {
  durationMins: number;
  hours: number;
  audience: number;
  verenaSales: number;
  affiliateSales: number;
  commission: number;
  ot: number;
  totalRevenue: number;
  roi: number;
  revenuePerHour: number;
  audiencePerHour: number;
}
interface ManagerResult extends SessionsResult {
  totals: ReportTotals;
}
interface DashboardResult extends SessionsResult {
  stats: ReportTotals & {
    sessionCount: number;
    avgSessionRev: number;
    avgSessionDur: number;
  };
  platformStats: Array<{
    platform: string;
    sessions: number;
    revenue: number;
    roi: number;
    revPerHour: number;
  }>;
  staffStats: Array<{
    name: string;
    sessions: number;
    revenue: number;
    roi: number;
    revPerHour: number;
    issueRate: number;
  }>;
  slotStats: {
    early: { sessions: number; revenue: number; ot: number; roi: number };
    late: { sessions: number; revenue: number; ot: number; roi: number };
  };
}
interface ActionCenterResult extends ApiResult {
  active: Array<{
    sessionId: string;
    staffName: string;
    platformName: string;
    platformIcon: string | null;
    startTime: string;
  }>;
  belowTarget: Array<{ staffName: string; roi: number }>;
  repeatedIssue: Array<{ name: string; issues: number }>;
  bestPlatform: {
    platform: string;
    sessions: number;
    revenue: number;
    roi: number;
  } | null;
  topStreamerToday: { name: string; revenue: number; sessions: number } | null;
}
interface Schedule {
  scheduleId: string;
  staffId: string;
  staffName: string;
  platformId: string;
  platformName: string;
  platformColor: string | null;
  platformIcon: string | null;
  date: string;
  startTime: string;
  endTime: string;
  endsNextDay: boolean;
  note: string;
  locked: boolean;
  hours: number;
}
interface SchedulesResult extends ApiResult {
  schedules: Schedule[];
}
interface AuditLog {
  id: number;
  timestamp: string;
  action: string;
  userId: string | null;
  userName: string;
  detail: string;
  success: boolean;
}
interface AuditResult extends ApiResult {
  logs: AuditLog[];
  actions: string[];
  total: number;
  page: number;
  pageSize: number;
}
interface StreamersResult extends ApiResult {
  streamers: Array<{ id: string; name: string }>;
}
interface SessionPreviewResult extends ApiResult {
  durationMins: number;
  ot: number;
  commission: number;
  totalRevenue: number;
  roi: number;
}
const categories: Category[] = [
  "Food Supplement 1",
  "Food Supplement 2",
  "Cosmetic",
  "Other Brand",
];
let page: Page = "live";
let notice = "";
let dashboardMonth = new Date().toISOString().slice(0, 7);
let historyMonth = new Date().toISOString().slice(0, 7);
let savedReminders: Reminder[] = [];
let savedSettings: Record<string, string> = {};
let savedUsers: User[] = [];
let savedPlatforms: Platform[] = [];
let savedProducts: Product[] = [];
let historyImageUrls: string[] = [];
let imageHydrationPromise: Promise<void> = Promise.resolve();
let historyImageObserver: IntersectionObserver | null = null;
let historyPdfSessions: ReportSession[] = [];
let historyPdfProducts: Product[] = [];
let historyPdfSettings: HistoryPdfSettings = {
  partTimeRate: 0,
  fullTimeRate: 0,
  commRate: 0,
  roiGood: 0,
  roiOk: 0,
};
let liveElapsedTimer: number | undefined;
let calculationTimer: number | undefined;
let validationPreviewTimer: number | undefined;
const idleLastActivityKey = "verena-livetask-last-activity";
const idleWarningMs = 60_000;
const idleSignedOutMs = 10 * 60_000;
const idleLiveSignedOutMs = 4 * 60 * 60_000;
let idleWarningTimer: number | undefined;
let idleLogoutTimer: number | undefined;
let idleActiveSessionId: string | null = null;
let idleLastRecordedAt = 0;
let sessionExitInProgress = false;
const endDraftDbName = "verenaEndDrafts";
const endDraftStoreName = "drafts";
const endDraftMaxAgeMs = 7 * 24 * 60 * 60 * 1000;
const endDraftTimers = new Map<string, number>();
const restoredEndDraftFiles = new Map<string, { face: File[]; images: File[] }>();
let startDraftTimer: number | undefined;
let restoredStartDraftFiles: { face: File[]; images: File[] } | null = null;
const productCacheKey = "verena-livetask-product-cache-v1";
let managerFilters = {
  dateFrom: `${new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok", year: "numeric", month: "2-digit" }).format(new Date())}-01`,
  dateTo: new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok" }).format(new Date()),
  staffId: "",
  platformId: "",
  employmentType: "",
};
let managerReportSnapshot: ManagerResult | null = null;
let managerReportUsers: User[] = [];
let managerReportPlatforms: Platform[] = [];
let managerExportLanguage: Language | null = null;
type HistorySortKey =
  | "date"
  | "platformName"
  | "staffName"
  | "durationMins"
  | "verenaSales"
  | "affiliateSales"
  | "commission"
  | "totalRevenue"
  | "roi";
let historySort: { key: HistorySortKey; direction: "asc" | "desc" } | null = null;
let historyPage = 1;
let historyPageSize: 25 | 50 | 100 = 25;
let historyVisibleSessions = new Map<string, ReportSession>();
let historyDialogProducts: Product[] = [];
let historyFilters = {
  dateFrom: `${historyMonth}-01`,
  dateTo: new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok" }).format(new Date()),
  staffId: "",
  platformId: "",
  employmentType: "",
  status: "",
};
type ScheduleMode = "build" | "planned";
interface BuilderSlot {
  scheduleId?: string;
  platformId: string;
  startTime: string;
  endTime: string;
  endsNextDay: boolean;
  note: string;
  locked: boolean;
}
interface ScheduleBuilder {
  staffId: string;
  month: string;
  days: Record<string, BuilderSlot[]>;
}
const bangkokMonth = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok", year: "numeric", month: "2-digit" }).format(new Date());
const nextScheduleMonth = (() => {
  const [year, month] = bangkokMonth.split("-").map(Number);
  return `${month === 12 ? year + 1 : year}-${String(month === 12 ? 1 : month + 1).padStart(2, "0")}`;
})();
let scheduleMode: ScheduleMode = "build";
let scheduleBuildMonth = nextScheduleMonth;
let scheduleBuildStaffId = "";
let scheduleBuildRequested = false;
let scheduleBuilder: ScheduleBuilder | null = null;
let scheduleBuilderError = "";
let scheduleReferenceRows: Schedule[] = [];
let scheduleWarningsConfirmed = false;
let scheduleTimePicker: { date: string; index: number; field: "startTime" | "endTime"; hour: string; minute: string } | null = null;
let schedulePlannedMonth = bangkokMonth;
let schedulePlannedStaffId = "";
let schedulePlannedPlatformId = "";
const auditToday = new Intl.DateTimeFormat("en-CA", { timeZone:"Asia/Bangkok" }).format(new Date());
let auditFilters = { dateFrom:`${auditToday.slice(0,7)}-01`, dateTo:auditToday, userId:"", action:"", success:"", page:1 };
const esc = (v: string): string =>
  v.replace(
    /[&<>'"]/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[
        c
      ] ?? c,
  );
const field = (f: HTMLFormElement, n: string): string =>
  new FormData(f).get(n)?.toString().trim() ?? "";
const card = (title: string, body: string): string =>
  page === "dashboard" && title === t("history")
    ? ""
    : `<section class="card min-w-0 bg-base-100 shadow"><div class="card-body min-w-0 p-4 sm:p-8"><h2 class="card-title">${title}</h2>${body}</div></section>`;
const btn = (action: string, label: string, id: string): string =>
  `<button type="button" class="btn btn-sm" data-action="${action}" data-id="${id}">${label}</button>`;
const sessionEditAction = (id: string, language: Language = currentLanguage()): string => {
  const label = tFor(language, "editSession");
  return `<button type="button" class="btn btn-sm session-icon-action" data-action="edit-session" data-id="${esc(id)}" title="${esc(label)}" aria-label="${esc(label)}">✏️</button>`;
};
const showcaseFields: Array<[keyof Showcase, Category]> = [
  ["supp1", "Food Supplement 1"],
  ["supp2", "Food Supplement 2"],
  ["cosmetic", "Cosmetic"],
  ["other", "Other Brand"],
];
const categoryLabel = (category: Category): string =>
  t(
    (
      {
        "Food Supplement 1": "categoryFoodSupplement1",
        "Food Supplement 2": "categoryFoodSupplement2",
        Cosmetic: "categoryCosmetic",
        "Other Brand": "categoryOtherBrand",
      } as const
    )[category],
  );
function bangkokBuildTime(): string {
  const buildTime = new Date(env.build);
  if (Number.isNaN(buildTime.getTime())) return env.build;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(buildTime);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")} ${part("hour")}:${part("minute")}:${part("second")}`;
}
const versionLabel = (): string =>
  `${t("version")} V3 Build ${env.buildId} · ${t("updatedOn")} ${bangkokBuildTime()} ${t("bangkokTime")}`;
const languageToggle = (): string =>
  `<div class="join border border-base-300 rounded-btn" aria-label="Language"><button class="join-item btn btn-sm ${currentLanguage() === "th" ? "btn-primary" : "btn-ghost"}" type="button" data-language="th">TH</button><button class="join-item btn btn-sm ${currentLanguage() === "en" ? "btn-primary" : "btn-ghost"}" type="button" data-language="en">EN</button></div>`;
const signOutIcon = (): string =>
  `<svg aria-hidden="true" viewBox="0 0 24 24" class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-3"></path><path d="M10 12h11"></path><path d="m18 9 3 3-3 3"></path></svg>`;
const authFooter = (): string =>
  `<footer class="mt-6 text-center text-xs text-base-content/60">${esc(versionLabel())}</footer>`;

function removeIdleWarning(): void {
  document.querySelector("#idle-logout-warning")?.remove();
}

function stopIdleTimers(): void {
  if (idleWarningTimer !== undefined) window.clearTimeout(idleWarningTimer);
  if (idleLogoutTimer !== undefined) window.clearTimeout(idleLogoutTimer);
  idleWarningTimer = undefined;
  idleLogoutTimer = undefined;
  removeIdleWarning();
}

function showIdleWarning(): void {
  if (!getToken() || document.querySelector("#idle-logout-warning")) return;
  const warning = document.createElement("div");
  warning.id = "idle-logout-warning";
  warning.className = "alert alert-warning fixed inset-x-3 top-3 z-[100] mx-auto max-w-xl shadow-xl";
  warning.setAttribute("role", "alert");
  warning.innerHTML = `<span>${esc(t(idleActiveSessionId ? "idleLiveWarning" : "idleWarning"))}</span><button type="button" class="btn btn-sm">${t("continueWorking")}</button>`;
  warning.querySelector("button")?.addEventListener("click", () => recordIdleActivity(true));
  document.body.append(warning);
}

function idleLimit(): number {
  return idleActiveSessionId ? idleLiveSignedOutMs : idleSignedOutMs;
}

function storedIdleActivity(): number {
  const value = Number(localStorage.getItem(idleLastActivityKey));
  return Number.isFinite(value) && value > 0 ? value : Date.now();
}

function scheduleIdleCheck(): void {
  stopIdleTimers();
  if (!getToken()) return;
  const elapsed = Math.max(0, Date.now() - storedIdleActivity());
  const limit = idleLimit();
  if (elapsed >= limit) {
    void exitSession("idle");
    return;
  }
  const warningDelay = limit - idleWarningMs - elapsed;
  if (warningDelay <= 0) showIdleWarning();
  else idleWarningTimer = window.setTimeout(showIdleWarning, warningDelay);
  idleLogoutTimer = window.setTimeout(() => void exitSession("idle"), limit - elapsed);
}

function recordIdleActivity(force = false): void {
  if (!getToken()) return;
  const now = Date.now();
  if (!force && now - idleLastRecordedAt < 15_000) return;
  idleLastRecordedAt = now;
  localStorage.setItem(idleLastActivityKey, String(now));
  scheduleIdleCheck();
}

function setIdleActiveSession(sessionId: string | null): void {
  if (idleActiveSessionId === sessionId) return;
  idleActiveSessionId = sessionId;
  recordIdleActivity(true);
}

async function exitSession(reason: "manual" | "idle" | "expired", revoke = true): Promise<void> {
  if (sessionExitInProgress) return;
  sessionExitInProgress = true;
  stopIdleTimers();
  try {
    if (revoke && getToken()) {
      try {
        await api<ApiResult>("/auth/logout", { method: "POST" });
      } catch {
        // Local sign-out must still complete if the network is unavailable.
      }
    }
    clearSession();
    restoredEndDraftFiles.clear();
    localStorage.removeItem(idleLastActivityKey);
    idleActiveSessionId = null;
    idleLastRecordedAt = 0;
    notice = reason === "idle" ? t("autoLoggedOut") : reason === "expired" ? t("sessionExpired") : "";
    window.history.replaceState(null, "", "#/login");
    await render();
  } finally {
    sessionExitInProgress = false;
  }
}

function installIdleTracking(): void {
  ["click", "keydown", "touchstart", "scroll", "mousemove"].forEach((eventName) =>
    document.addEventListener(eventName, () => recordIdleActivity(), { passive: true }),
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleIdleCheck();
  });
  window.addEventListener("focus", scheduleIdleCheck);
  window.addEventListener("storage", (event) => {
    if (event.key === idleLastActivityKey) {
      scheduleIdleCheck();
      return;
    }
    if (!getToken() && getUser()) void exitSession("expired", false);
  });
  window.addEventListener(authExpiredEvent, () => void exitSession("expired", false));
}

function endDraftKey(sessionId: string): string | null {
  const user = getUser();
  return user ? `${user.id}:${sessionId}` : null;
}

function startDraftKey(): string | null {
  const user = getUser();
  return user ? `start:${user.id}` : null;
}

function openEndDraftDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(endDraftDbName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(endDraftStoreName))
        request.result.createObjectStore(endDraftStoreName, { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readEndDraft(sessionId: string): Promise<EndDraft | null> {
  const key = endDraftKey(sessionId);
  if (!key) return null;
  try {
    const database = await openEndDraftDatabase();
    const draft = await new Promise<EndDraft | undefined>((resolve, reject) => {
      const request = database.transaction(endDraftStoreName, "readonly").objectStore(endDraftStoreName).get(key);
      request.onsuccess = () => resolve(request.result as EndDraft | undefined);
      request.onerror = () => reject(request.error);
    });
    database.close();
    if (!draft || Date.now() - draft.updatedAt > endDraftMaxAgeMs) {
      if (draft) await deleteEndDraft(sessionId);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

async function deleteEndDraft(sessionId: string): Promise<void> {
  const key = endDraftKey(sessionId);
  if (!key) return;
  restoredEndDraftFiles.delete(key);
  try {
    const database = await openEndDraftDatabase();
    await new Promise<void>((resolve) => {
      const request = database.transaction(endDraftStoreName, "readwrite").objectStore(endDraftStoreName).delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
    database.close();
  } catch {
    // A missing local draft must never block the End form.
  }
}
async function pruneEndDrafts(): Promise<void> {
  try {
    const database = await openEndDraftDatabase();
    const drafts = await new Promise<EndDraft[]>((resolve) => {
      const request = database.transaction(endDraftStoreName, "readonly").objectStore(endDraftStoreName).getAll();
      request.onsuccess = () => resolve(request.result as EndDraft[]);
      request.onerror = () => resolve([]);
    });
    const stale = drafts.filter((draft) => Date.now() - draft.updatedAt > endDraftMaxAgeMs);
    if (stale.length) {
      const transaction = database.transaction(endDraftStoreName, "readwrite");
      stale.forEach((draft) => transaction.objectStore(endDraftStoreName).delete(draft.key));
      await new Promise<void>((resolve) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
      });
    }
    database.close();
  } catch {
    // Storage cleanup is best-effort only.
  }
}

function storedDraftFile(file: File): EndDraftFile {
  return { name: file.name, type: file.type, lastModified: file.lastModified, blob: file };
}

function restoreDraftFile(file: EndDraftFile): File {
  return new File([file.blob], file.name, { type: file.type, lastModified: file.lastModified });
}

function selectedEndDraftFiles(form: HTMLFormElement, name: "endFace" | "endImages"): File[] {
  const input = form.elements.namedItem(name);
  const selected = input instanceof HTMLInputElement ? [...(input.files ?? [])] : [];
  if (selected.length) return selected;
  const restored = restoredEndDraftFiles.get(endDraftKey(form.dataset.sessionId ?? "") ?? "");
  return name === "endFace" ? (restored?.face ?? []) : (restored?.images ?? []);
}

async function saveEndDraft(form: HTMLFormElement): Promise<void> {
  const sessionId = form.dataset.sessionId;
  const user = getUser();
  const key = sessionId ? endDraftKey(sessionId) : null;
  if (!sessionId || !user || !key) return;
  const fields: Record<string, string> = {};
  ["endTime", "verena", "affiliate", "audience", "orderNumbers", "endNote", "showcase-supp1", "showcase-supp2", "showcase-cosmetic", "showcase-other"].forEach((name) => {
    const element = form.elements.namedItem(name);
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement)
      fields[name] = element.value;
  });
  const existing = await readEndDraft(sessionId);
  const draft: EndDraft = {
    key,
    sessionId,
    userId: user.id,
    createdAt: existing?.createdAt ?? Date.now(),
    updatedAt: Date.now(),
    fields,
    face: selectedEndDraftFiles(form, "endFace")[0] ? storedDraftFile(selectedEndDraftFiles(form, "endFace")[0]) : null,
    images: selectedEndDraftFiles(form, "endImages").slice(0, 5).map(storedDraftFile),
  };
  try {
    const database = await openEndDraftDatabase();
    await new Promise<void>((resolve) => {
      const request = database.transaction(endDraftStoreName, "readwrite").objectStore(endDraftStoreName).put(draft);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
    database.close();
  } catch {
    // Keep the form usable when IndexedDB is unavailable.
  }
}

function queueEndDraftSave(form: HTMLFormElement): void {
  const sessionId = form.dataset.sessionId;
  if (!sessionId) return;
  const previous = endDraftTimers.get(sessionId);
  if (previous !== undefined) window.clearTimeout(previous);
  endDraftTimers.set(sessionId, window.setTimeout(() => void saveEndDraft(form), 500));
}

function applyDraftFiles(input: HTMLInputElement, files: File[]): void {
  try {
    const transfer = new DataTransfer();
    files.forEach((file) => transfer.items.add(file));
    input.files = transfer.files;
  } catch {
    // The in-memory fallback is still used during this visit.
  }
}

async function restoreEndDraft(form: HTMLFormElement): Promise<void> {
  const sessionId = form.dataset.sessionId;
  if (!sessionId) return;
  const draft = await readEndDraft(sessionId);
  if (!draft || !form.isConnected) return;
  Object.entries(draft.fields).forEach(([name, value]) => {
    const element = form.elements.namedItem(name);
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement)
      element.value = value;
  });
  const face = draft.face ? [restoreDraftFile(draft.face)] : [];
  const images = draft.images.map(restoreDraftFile);
  const key = endDraftKey(sessionId);
  if (key) restoredEndDraftFiles.set(key, { face, images });
  const faceInput = form.elements.namedItem("endFace");
  const imagesInput = form.elements.namedItem("endImages");
  if (faceInput instanceof HTMLInputElement) {
    applyDraftFiles(faceInput, face);
    const fileName = faceInput.parentElement?.querySelector<HTMLElement>("[data-file-name]");
    if (fileName) fileName.textContent = face[0]?.name ?? t("noFileChosen");
  }
  if (imagesInput instanceof HTMLInputElement) applyDraftFiles(imagesInput, images);
  paintImagePreviews(form.querySelector<HTMLElement>("[data-end-face-preview]"), face);
  paintImagePreviews(form.querySelector<HTMLElement>("[data-end-images-preview]"), images);
  const count = form.querySelector<HTMLElement>("[data-supporting-images-count]");
  if (count) count.textContent = currentLanguage() === "th" ? `เลือกแล้ว ${images.length}/5 รูป` : `${images.length}/5 images selected`;
  const noticeElement = form.querySelector<HTMLElement>("[data-end-draft-notice]");
  if (noticeElement) {
    noticeElement.className = "alert alert-warning text-sm md:col-span-2";
    noticeElement.innerHTML = `<span>${esc(t("endDraftRestored"))}<br><small>${esc(t("endDraftHint"))}</small></span><button type="button" class="btn btn-sm" data-discard-end-draft>${esc(t("discardEndDraft"))}</button>`;
    noticeElement.querySelector<HTMLButtonElement>("[data-discard-end-draft]")?.addEventListener("click", () => {
      void deleteEndDraft(sessionId);
      noticeElement.className = "hidden";
    });
  }
}

async function readStartDraft(): Promise<StartDraft | null> {
  const key = startDraftKey();
  if (!key) return null;
  try {
    const database = await openEndDraftDatabase();
    const draft = await new Promise<StartDraft | undefined>((resolve, reject) => {
      const request = database.transaction(endDraftStoreName, "readonly").objectStore(endDraftStoreName).get(key);
      request.onsuccess = () => resolve(request.result as StartDraft | undefined);
      request.onerror = () => reject(request.error);
    });
    database.close();
    return draft && Date.now() - draft.updatedAt <= endDraftMaxAgeMs ? draft : null;
  } catch { return null; }
}

async function deleteStartDraft(): Promise<void> {
  const key = startDraftKey();
  restoredStartDraftFiles = null;
  if (!key) return;
  try {
    const database = await openEndDraftDatabase();
    await new Promise<void>((resolve) => {
      const request = database.transaction(endDraftStoreName, "readwrite").objectStore(endDraftStoreName).delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
    database.close();
  } catch { /* Local storage is optional. */ }
}

function selectedStartDraftFiles(form: HTMLFormElement, name: "startFace" | "startImages"): File[] {
  const input = form.elements.namedItem(name);
  const selected = input instanceof HTMLInputElement ? [...(input.files ?? [])] : [];
  if (selected.length) return selected;
  return name === "startFace" ? (restoredStartDraftFiles?.face ?? []) : (restoredStartDraftFiles?.images ?? []);
}

async function saveStartDraft(form: HTMLFormElement): Promise<void> {
  const user = getUser(), key = startDraftKey();
  if (!user || !key) return;
  const fields: Record<string, string> = {};
  form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[name]").forEach((element) => {
    if (element instanceof HTMLInputElement && element.type === "file") return;
    if (element instanceof HTMLInputElement && element.type === "checkbox") fields[element.name] = element.checked ? "1" : "0";
    else if (!(element instanceof HTMLInputElement) || element.type !== "radio" || element.checked) fields[element.name] = element.value;
  });
  const existing = await readStartDraft();
  const face = selectedStartDraftFiles(form, "startFace")[0];
  const draft: StartDraft = { key, userId: user.id, createdAt: existing?.createdAt ?? Date.now(), updatedAt: Date.now(), fields, face: face ? storedDraftFile(face) : null, images: selectedStartDraftFiles(form, "startImages").slice(0, 5).map(storedDraftFile) };
  try {
    const database = await openEndDraftDatabase();
    await new Promise<void>((resolve) => {
      const request = database.transaction(endDraftStoreName, "readwrite").objectStore(endDraftStoreName).put(draft);
      request.onsuccess = () => resolve(); request.onerror = () => resolve();
    });
    database.close();
  } catch { /* Local storage is optional. */ }
}

function queueStartDraftSave(form: HTMLFormElement): void {
  if (startDraftTimer !== undefined) window.clearTimeout(startDraftTimer);
  startDraftTimer = window.setTimeout(() => void saveStartDraft(form), 500);
}

async function restoreStartDraft(form: HTMLFormElement): Promise<void> {
  const draft = await readStartDraft();
  if (!draft || !form.isConnected) return;
  form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[name]").forEach((element) => {
    const value = draft.fields[element.name];
    if (value === undefined || (element instanceof HTMLInputElement && element.type === "file")) return;
    if (element instanceof HTMLInputElement && element.type === "checkbox") element.checked = value === "1";
    else if (element instanceof HTMLInputElement && element.type === "radio") element.checked = element.value === value;
    else element.value = value;
  });
  const face = draft.face ? [restoreDraftFile(draft.face)] : [];
  const images = draft.images.map(restoreDraftFile);
  restoredStartDraftFiles = { face, images };
  const faceInput = form.elements.namedItem("startFace"), imagesInput = form.elements.namedItem("startImages");
  if (faceInput instanceof HTMLInputElement) {
    applyDraftFiles(faceInput, face);
    const fileName = faceInput.parentElement?.querySelector<HTMLElement>("[data-file-name]");
    if (fileName) fileName.textContent = face[0]?.name ?? t("noFileChosen");
  }
  if (imagesInput instanceof HTMLInputElement) applyDraftFiles(imagesInput, images);
  paintImagePreviews(form.querySelector<HTMLElement>("[data-start-face-preview]"), face);
  paintImagePreviews(form.querySelector<HTMLElement>("[data-start-images-preview]"), images);
  const count = form.querySelector<HTMLElement>("[data-supporting-images-count]");
  if (count) count.textContent = currentLanguage() === "th" ? `เลือกแล้ว ${images.length}/5 รูป` : `${images.length}/5 images selected`;
  const noteCount = form.querySelector<HTMLElement>("[data-start-note-count]");
  if (noteCount) noteCount.textContent = String((draft.fields.startNote ?? "").length);
  const noticeElement = form.querySelector<HTMLElement>("[data-start-draft-notice]");
  if (noticeElement) {
    noticeElement.className = "alert alert-warning text-sm";
    noticeElement.innerHTML = `<span>${esc(t("startDraftRestored"))}<br><small>${esc(t("startDraftHint"))}</small></span><button type="button" class="btn btn-sm" data-discard-start-draft>${esc(t("discardStartDraft"))}</button>`;
    noticeElement.querySelector<HTMLButtonElement>("[data-discard-start-draft]")?.addEventListener("click", () => { void deleteStartDraft(); noticeElement.className = "hidden"; });
  }
}

async function uploadImage(
  file: File,
  kind: "face" | "session",
): Promise<string | null> {
  if (
    file.size > 2_500_000 ||
    !["image/jpeg", "image/png", "image/webp"].includes(file.type)
  ) {
    return null;
  }
  const result = await uploadFile(`${env.apiUrl}/images/upload?kind=${kind}`, file);
  if (
    typeof result === "object" &&
    result !== null &&
    "ok" in result &&
    result.ok === true &&
    "key" in result &&
    typeof result.key === "string"
  )
    return result.key;
  return null;
}

/** The End form must remain usable when a connection drops after START. */
async function productsWithOfflineCache(activeOnly: boolean): Promise<ProductsResult> {
  const result = await api<ProductsResult>(`/products${activeOnly ? "?activeOnly=true" : ""}`);
  if (result.ok) {
    try { localStorage.setItem(productCacheKey, JSON.stringify({ at: Date.now(), products: result.products, activeCounts: result.activeCounts })); } catch { /* cache is optional */ }
    return result;
  }
  try {
    const cached: unknown = JSON.parse(localStorage.getItem(productCacheKey) ?? "");
    if (typeof cached === "object" && cached !== null && "products" in cached && Array.isArray(cached.products) && "activeCounts" in cached) {
      const products = cached.products as Product[];
      return { ok: true, products: activeOnly ? products.filter((product) => product.active) : products, activeCounts: cached.activeCounts as Record<Category, number> };
    }
  } catch { /* a corrupt cache is ignored */ }
  return result;
}
async function uploadProfileImage(file: File, userId: string): Promise<ProfileUploadResult> {
  const prepared = await prepareProfileImage(file);
  if (!prepared) {
    return { ok: false, error: t("invalidProfileImage") };
  }
  const payload = await uploadFile(
    `${env.apiUrl}/images/upload?kind=profile&userId=${encodeURIComponent(userId)}`,
    prepared,
  );
  if (typeof payload !== "object" || payload === null || !("ok" in payload)) {
    return { ok: false, error: t("requestFailed") };
  }
  return payload as ProfileUploadResult;
}
async function uploadPlatformImage(file: File, platformId: string): Promise<PlatformImageUploadResult> {
  const prepared = await prepareProfileImage(file);
  if (!prepared) return { ok: false, error: t("invalidProfileImage") };
  const payload = await uploadFile(
    `${env.apiUrl}/images/upload?kind=platform&platformId=${encodeURIComponent(platformId)}`,
    prepared,
  );
  if (typeof payload !== "object" || payload === null || !("ok" in payload)) {
    return { ok: false, error: t("requestFailed") };
  }
  return payload as PlatformImageUploadResult;
}

async function prepareProfileImage(file: File): Promise<File | null> {
  if (file.size === 0 || file.size > 20_000_000 || !file.type.startsWith("image/")) return null;
  const source = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Invalid profile image"));
      element.src = source;
    });
    const scale = Math.min(1, 960 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.86, 0.72, 0.58]) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (blob && blob.size <= 2_500_000)
        return new File([blob], `${file.name.replace(/\.[^.]+$/, "") || "profile"}.jpg`, { type: "image/jpeg" });
    }
    return null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(source);
  }
}

function uploadProgressElement(): HTMLDialogElement {
  let overlay = document.querySelector<HTMLDialogElement>("#upload-progress-overlay");
  if (overlay) return overlay;
  overlay = document.createElement("dialog");
  overlay.id = "upload-progress-overlay";
  overlay.className = "modal bg-base-300/70 backdrop-blur-sm";
  overlay.innerHTML = `<div class="modal-box w-auto p-6 text-center" role="status" aria-live="polite"><div class="relative mx-auto h-28 w-28"><svg class="h-28 w-28 -rotate-90" viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" stroke-width="8" class="text-base-300"></circle><circle data-upload-progress-circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" stroke-width="8" stroke-linecap="round" pathLength="100" stroke-dasharray="100" stroke-dashoffset="100" class="text-primary transition-all"></circle></svg><strong data-upload-progress-value class="absolute inset-0 grid place-items-center text-xl">0%</strong></div><p data-upload-progress-label class="mt-3 font-semibold"></p></div>`;
  document.body.append(overlay);
  return overlay;
}

function showUploadProgress(percent: number): void {
  const overlay = uploadProgressElement();
  const value = Math.max(0, Math.min(100, Math.round(percent)));
  if (!overlay.open) overlay.showModal();
  overlay.querySelector<SVGCircleElement>("[data-upload-progress-circle]")?.setAttribute("stroke-dashoffset", String(100 - value));
  const valueElement = overlay.querySelector<HTMLElement>("[data-upload-progress-value]");
  const labelElement = overlay.querySelector<HTMLElement>("[data-upload-progress-label]");
  if (valueElement) valueElement.textContent = `${value}%`;
  if (labelElement) labelElement.textContent = t("uploadingFile");
}

function hideUploadProgress(): void {
  const overlay = document.querySelector<HTMLDialogElement>("#upload-progress-overlay");
  if (overlay?.open) overlay.close();
}

function uploadFile(url: string, file: File): Promise<unknown> {
  return new Promise((resolve) => {
    const request = new XMLHttpRequest();
    let settled = false;
    const finish = (result: unknown): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener("offline", onOffline);
      hideUploadProgress();
      resolve(result);
    };
    const onOffline = (): void => {
      request.abort();
      finish({ ok: false, error: "NETWORK_OFFLINE" });
    };
    if (!navigator.onLine) {
      finish({ ok: false, error: "NETWORK_OFFLINE" });
      return;
    }
    request.open("POST", url);
    request.responseType = "json";
    request.timeout = 30_000;
    request.setRequestHeader("Authorization", `Bearer ${getToken() ?? ""}`);
    request.setRequestHeader("Content-Type", file.type);
    request.upload.addEventListener("progress", (event) => {
      showUploadProgress(event.lengthComputable ? (event.loaded / event.total) * 100 : 0);
    });
    request.addEventListener("load", () => {
      showUploadProgress(100);
      if (request.status === 401 && getToken())
        window.dispatchEvent(new Event(authExpiredEvent));
      finish(request.response ?? { ok: false, error: t("requestFailed"), code: request.status });
    });
    request.addEventListener("error", () => {
      finish({ ok: false, error: navigator.onLine ? "NETWORK_TIMEOUT" : "NETWORK_OFFLINE" });
    });
    request.addEventListener("abort", () => {
      finish({ ok: false, error: navigator.onLine ? "NETWORK_TIMEOUT" : "NETWORK_OFFLINE" });
    });
    request.addEventListener("timeout", () => finish({ ok: false, error: "NETWORK_TIMEOUT" }));
    window.addEventListener("offline", onOffline, { once: true });
    showUploadProgress(0);
    request.send(file);
  });
}
async function stampFacePhoto(file: File): Promise<File | null> {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
    return null;
  const source = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Invalid image"));
      element.src = source;
    });
    const scale = Math.min(1, 1000 / image.naturalWidth);
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(image, 0, 0, width, height);
    const barHeight = Math.max(30, Math.round(height * 0.085));
    const name = getUser()?.name ?? "";
    const timestamp = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Bangkok",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
      .format(new Date())
      .replace(",", "");
    context.fillStyle = "rgba(0,0,0,0.55)";
    context.fillRect(0, height - barHeight, width, barHeight);
    context.fillStyle = "#ffffff";
    context.textBaseline = "middle";
    const fontSize = Math.max(13, Math.round(barHeight * 0.4));
    context.font = `700 ${fontSize}px Arial, sans-serif`;
    context.fillText(timestamp, 10, height - barHeight * (name ? 0.63 : 0.5));
    if (name) {
      context.font = `${Math.round(fontSize * 0.8)}px Arial, sans-serif`;
      context.fillText(name, 10, height - barHeight * 0.27);
    }
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.8),
    );
    return blob ? new File([blob], "face.jpg", { type: "image/jpeg" }) : null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(source);
  }
}
function showcaseInputs(
  prefix: string,
  products: Product[],
  values?: Showcase,
  activeOnly = true,
): string {
  return showcaseFields
    .map(
      ([key, category]) =>
        `<label class="form-control"><span class="label-text">${categoryLabel(category)}</span><select required name="${prefix}-${key}" class="select select-bordered"><option value="">—</option>${products
          .filter((p) => p.category === category && (!activeOnly || p.active))
          .map(
            (p) =>
              `<option value="${p.id}" ${values?.[key].includes(p.id) ? "selected" : ""}>${esc(p.name)}</option>`,
          )
          .join("")}</select></label>`,
    )
    .join("");
}
function collectShowcase(
  form: HTMLFormElement,
  prefix: string,
): Showcase | null {
  const result = {} as Showcase;
  for (const [key] of showcaseFields) {
    const value = field(form, `${prefix}-${key}`);
    if (!value) return null;
    result[key] = [value];
  }
  return result;
}
function facePhotoPicker(name: "startFace" | "endFace", required = true, suffix: string = name, previewAttribute = ""): string {
  const inputId = `${suffix}-input`;
  const optional = required ? "" : currentLanguage() === "th" ? " (ไม่บังคับสำหรับผู้ดูแล)" : " (optional for admin)";
  return `<div class="form-control justify-end"><span class="label-text">${t(name)}${optional}</span><span class="label-text-alt">${t("faceHint")}</span><input id="${inputId}" data-face-picker ${required ? "required" : ""} name="${name}" type="file" accept="image/jpeg,image/png,image/webp" class="sr-only"><label for="${inputId}" class="input input-bordered flex cursor-pointer items-center gap-0 overflow-hidden p-0"><span class="flex h-full shrink-0 items-center bg-base-content px-4 font-semibold text-base-100">${t("chooseFileOrCamera")}</span><span data-file-name class="min-w-0 truncate px-4 text-base-content/70">${t("noFileChosen")}</span></label><div data-image-preview ${previewAttribute} data-image-preview-mode="face" class="mt-3 hidden" aria-live="polite"></div></div>`;
}
function scheduledNowPanel(result: SchedulesResult): string {
  if (!result.ok)
    return card(
      t("scheduledNow"),
      `<p class="text-error">${esc(result.error ?? t("requestFailed"))}</p>`,
    );
  return card(
    t("scheduledNow"),
    result.schedules.length
      ? `<div class="space-y-2">${result.schedules.map((schedule) => `<div class="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-base-200 pt-2"><strong>${esc(schedule.staffName)}</strong><span>${esc(schedule.platformIcon ?? "")} ${esc(schedule.platformName)}</span><strong>${esc(schedule.startTime)}–${esc(schedule.endTime)}</strong><span class="text-sm text-base-content/60">${schedule.hours}h</span>${schedule.note ? `<span class="text-sm text-base-content/60">${esc(schedule.note)}</span>` : ""}</div>`).join("")}</div><p class="mt-3 text-sm text-base-content/60">${t("scheduledNowHint")}</p>`
      : `<p class="rounded-box bg-base-200 p-4 text-sm text-base-content/70">${t("noScheduledNow")}</p>`,
  );
}

function authCard(content: string): string {
  return `<main class="min-h-screen bg-base-200 p-3 sm:p-6"><section class="mx-auto flex min-h-[80vh] max-w-md items-center"><div class="card w-full min-w-0 bg-base-100 shadow-xl"><div class="card-body min-w-0 gap-4 p-5 sm:p-8"><div class="flex justify-end">${languageToggle()}</div>${content}${authFooter()}</div></div></section></main>`;
}
function login(): string {
  return authCard(
    `<form id="login-form" class="grid gap-4"><h1 class="text-3xl font-bold">${t("loginTitle")}</h1><p class="text-base-content/70">${t("loginHint")}</p><label class="form-control"><span class="label-text">${t("email")}</span><input required name="email" type="email" class="input input-bordered" autocomplete="email"></label><label class="form-control"><span class="label-text">${t("password")}</span><input required name="password" type="password" class="input input-bordered" autocomplete="current-password"></label><button class="btn btn-primary" type="submit">${t("signIn")}</button><a href="#/forgot" class="link link-primary text-sm">${t("forgotPassword")}</a>${notice ? `<p class="text-error">${esc(notice)}</p>` : ""}</form>`,
  );
}
function authPage(): string {
  const hash = new URL(window.location.href).hash;
  const token =
    new URLSearchParams(hash.split("?")[1] ?? "").get("token") ?? "";
  if (hash.startsWith("#/forgot"))
    return authCard(
      `<form id="forgot-form" class="grid gap-4"><h1 class="text-3xl font-bold">${t("forgotPassword")}</h1><p>${t("forgotPasswordHint")}</p><input required name="email" type="email" class="input input-bordered" placeholder="${t("email")}"><button class="btn btn-primary" type="submit">${t("sendResetLink")}</button><a href="#/login" class="link">${t("backToSignIn")}</a>${notice ? `<p class="text-info">${esc(notice)}</p>` : ""}</form>`,
    );
  if (hash.startsWith("#/reset"))
    return authCard(
      `<form id="reset-form" class="grid gap-4"><h1 class="text-3xl font-bold">${t("resetPassword")}</h1><input type="hidden" name="token" value="${esc(token)}"><input type="hidden" name="kind" value="${hash.includes("kind=activate") ? "activate" : "reset"}"><label class="form-control"><span class="label-text">${t("newPassword")}</span><input required minlength="8" name="newPassword" type="password" class="input input-bordered" autocomplete="new-password"></label><button class="btn btn-primary" type="submit">${t("save")}</button><a href="#/login" class="link">${t("backToSignIn")}</a>${notice ? `<p class="text-error">${esc(notice)}</p>` : ""}</form>`,
    );
  return login();
}
function navLabel(item: Page): string {
  if (item === "live" && getUser()?.role === "streamer") return t("recordLive");
  if (item === "my-history") return t("myHistory");
  if (item === "manager-report") return t("managerReport");
  if (item === "my-schedule") return t("mySchedule");
  return t(item);
}
function selfProfileDialog(user:SessionUser):string {
  return `<dialog id="self-profile-dialog" class="modal"><div class="modal-box"><h3 class="text-lg font-bold">${t("myAccount")}</h3><form id="self-profile-form" class="mt-4 grid gap-4"><input type="hidden" name="id" value="${esc(user.id)}">${profileImageField(`self-${user.id}`,user.profileImageKey??null)}<p class="hidden rounded-box p-3 text-sm" data-profile-message role="status"></p><div class="flex justify-end"><button type="button" class="btn btn-primary" data-save-self-profile>${t("saveProfileImage")}</button></div></form><div class="divider"></div><form id="change-password-form" class="grid gap-4"><h4 class="font-bold">${t("changePassword")}</h4><label class="form-control"><span class="label-text">${t("currentPassword")}</span><input required name="currentPassword" type="password" class="input input-bordered" autocomplete="current-password"></label><label class="form-control"><span class="label-text">${t("newPassword")}</span><input required minlength="8" name="newPassword" type="password" class="input input-bordered" autocomplete="new-password"></label><label class="form-control"><span class="label-text">${t("confirmNewPassword")}</span><input required minlength="8" name="confirmPassword" type="password" class="input input-bordered" autocomplete="new-password"></label><p class="text-xs text-base-content/60">${t("changePasswordHint")}</p><div class="modal-action"><button type="button" class="btn" data-close-dialog>${t("cancel")}</button><button type="submit" class="btn btn-primary">${t("changePassword")}</button></div></form></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}

function allowedPages(user: SessionUser): Page[] {
  return user.role === "admin"
    ? ["live", "dashboard", "history", "manager-report", "schedules", "users", "platforms", "products", "settings", "audit"]
    : ["live", "my-schedule", "my-history"];
}

function requestedPage(): Page | null {
  const value = window.location.hash.split("?")[0].replace(/^#\//, "");
  const pages: Page[] = ["live", "dashboard", "history", "my-history", "manager-report", "schedules", "my-schedule", "users", "platforms", "products", "settings", "audit"];
  return pages.includes(value as Page) ? value as Page : null;
}
function shell(content: string): string {
  const user = getUser();
  const pages = user ? allowedPages(user) : [];
  if (user?.role === "streamer") {
    const streamerLanguage = `<div class="join rounded-btn border border-primary-content/40" aria-label="Language"><button class="join-item btn btn-sm border-0 ${currentLanguage()==="th"?"bg-primary-content/25":"btn-ghost"} text-primary-content" type="button" data-language="th">TH</button><button class="join-item btn btn-sm border-0 ${currentLanguage()==="en"?"bg-primary-content/25":"btn-ghost"} text-primary-content" type="button" data-language="en">EN</button></div>`;
    const streamerNav = `<nav class="mb-4 flex min-w-0 gap-2 overflow-x-auto rounded-box bg-base-100 p-2 shadow-sm sm:mb-6" aria-label="${esc(t("appName"))}">${pages.map((x) => `<button class="btn shrink-0 whitespace-nowrap ${page === x ? "btn-primary" : "btn-ghost"}" data-page="${x}">${navLabel(x)}</button>`).join("")}</nav>`;
    const streamerContentWidth = page === "my-history" ? "max-w-7xl" : "max-w-5xl";
    return `<main class="min-h-screen min-w-0 overflow-x-hidden bg-base-200"><header class="sticky top-0 z-20 border-b border-primary/30 bg-primary px-3 py-2 text-primary-content shadow-sm"><div class="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-3"><div class="flex shrink-0 items-center gap-3"><span class="grid h-11 w-11 place-items-center rounded-xl border-2 border-primary-content/50 text-xl font-bold">V</span><div><h1 class="font-bold leading-tight">${t("appName")}</h1><p class="text-xs font-semibold tracking-widest opacity-80">VERENA INTERNATIONAL</p></div></div><div class="ml-auto flex shrink-0 items-center gap-2">${streamerLanguage}<button id="open-self-profile" type="button" class="flex items-center gap-2 rounded-full border border-primary-content/40 px-2 py-1 text-left" aria-label="${t("changeProfileImage")}">${profileAvatar(user.profileImageKey,"h-8 w-8")}<span class="hidden sm:block"><strong class="block text-sm leading-tight">${esc(user.name)}</strong><small class="opacity-80">${t("streamer")}</small></span></button><button id="signout" type="button" class="btn btn-ghost btn-square text-primary-content" aria-label="${t("signOut")}" title="${t("signOut")}">${signOutIcon()}</button></div></div></header><div class="mx-auto min-w-0 ${streamerContentWidth} p-3 sm:p-6">${streamerNav}${content}</div>${selfProfileDialog(user)}</main>`;
  }
  return `<main class="min-h-screen min-w-0 overflow-x-hidden bg-base-200"><header class="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-base-300 bg-base-100 px-3 py-2 sm:flex-nowrap sm:px-4"><div class="min-w-0 flex-1"><h1 class="font-bold">${t("appName")}</h1><p class="break-words text-xs text-base-content/60">${versionLabel()}</p></div><div class="flex w-full shrink-0 items-center justify-between gap-2 sm:w-auto sm:justify-end">${languageToggle()}<button id="open-self-profile" type="button" class="btn btn-ghost btn-circle" aria-label="${t("changeProfileImage")}">${profileAvatar(user?.profileImageKey,"h-8 w-8")}</button><button id="signout" type="button" class="btn btn-ghost btn-sm gap-2" aria-label="${t("signOut")}" title="${t("signOut")}">${signOutIcon()}<span class="hidden sm:inline">${t("signOut")}</span></button></div></header><div class="mx-auto min-w-0 max-w-7xl p-3 sm:p-6"><nav class="mb-4 flex min-w-0 gap-2 overflow-x-auto rounded-box bg-base-100 p-2 shadow-sm sm:mb-6" aria-label="${esc(t("appName"))}">${pages.map((x) => `<button class="btn btn-sm shrink-0 whitespace-nowrap ${page === x ? "btn-primary" : "btn-ghost"}" data-page="${x}">${navLabel(x)}</button>`).join("")}</nav>${content}</div>${user?selfProfileDialog(user):""}</main>`;
}

function endSessionForm(
  session: Pick<ActiveSession, "sessionId" | "startTime" | "liveUrl" | "platformName" | "showcasePlan">,
  products: Product[],
  admin: boolean,
): string {
  const endTime = admin
    ? `<label class="form-control md:col-span-2"><span class="label-text">${t("actualEndTime")}</span><input required name="endTime" type="datetime-local" value="${esc(bangkokDateTimeInput(new Date().toISOString()))}" class="input input-bordered"></label>`
    : "";
  const actions = admin
    ? `<div class="modal-action md:col-span-2"><button class="btn" type="button" data-close-dialog>${t("cancel")}</button><button class="btn btn-primary" type="button" data-end-session-submit>⏹ ${t("endLive")}</button></div>`
    : `<button class="btn btn-primary md:col-span-2" type="button" data-end-session-submit>⏹ ${t("endLive")}</button>`;
  return `<form id="${admin ? "admin-end-live-form" : "end-live-form"}" data-session-id="${esc(session.sessionId)}" data-admin="${admin ? "true" : "false"}" class="grid gap-3 md:grid-cols-2">${endTime}<div data-end-draft-notice class="hidden"></div><label class="form-control"><span class="label-text">${t("verenaSales")}</span><input name="verena" type="number" min="0" step="0.01" value="0" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("affiliateSales")}</span><input name="affiliate" type="number" min="0" step="0.01" value="0" class="input input-bordered"></label><div data-end-calculation class="alert md:col-span-2 text-sm">${t("calculationPreview")}</div><label class="form-control"><span class="label-text">${t("audience")}</span><input required name="audience" type="number" min="0" step="1" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("orderNumbers")}</span><textarea name="orderNumbers" maxlength="500" rows="2" class="textarea textarea-bordered"></textarea></label><div class="md:col-span-2 grid gap-3 md:grid-cols-4"><div class="md:col-span-4"><p class="font-semibold">${t("actualShowcase")}</p><p class="text-sm text-base-content/60">${t("actualShowcaseHint")}</p></div>${showcaseInputs("showcase", products, session.showcasePlan, false)}</div><div class="md:col-span-2">${facePhotoPicker("endFace", !admin, `end-${session.sessionId}`, "data-end-face-preview")}</div><div class="form-control md:col-span-2"><span class="label-text">${t("optionalImages")}</span><input name="endImages" type="file" multiple data-supporting-images accept="image/jpeg,image/png,image/webp" class="file-input file-input-bordered"><span class="label-text-alt mt-1" data-supporting-images-count></span><div data-image-preview data-end-images-preview data-image-preview-mode="supporting" class="mt-3 hidden" aria-live="polite"></div></div><label class="form-control md:col-span-2"><span class="label-text">${t("endNote")}</span><textarea name="endNote" maxlength="150" rows="2" class="textarea textarea-bordered"></textarea></label><p data-end-session-message class="hidden rounded-box p-3 text-sm md:col-span-2" role="alert"></p>${actions}</form>`;
}

function adminEndSessionDialog(session: ReportSession, products: Product[]): string {
  return `<dialog id="admin-end-session-${esc(session.sessionId)}" class="modal"><div class="modal-box max-w-4xl"><h3 class="text-lg font-bold">${t("endLive")}</h3><p class="my-3 text-sm text-base-content/70">${esc(session.staffName)} · ${esc(session.platformName)} · ${esc(new Date(session.startTime).toLocaleString())}</p>${endSessionForm({ sessionId:session.sessionId, startTime:session.startTime, liveUrl:session.liveUrl, platformName:session.platformName, showcasePlan:session.showcasePlan }, products, true)}</div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}

async function live(): Promise<string> {
  const [
    active,
    platformsResult,
    productsResult,
    remindersResult,
    scheduledNow,
    streamers,
  ] = await Promise.all([
    api<ActiveResult>("/sessions/active"),
    api<PlatformsResult>("/platforms?activeOnly=true"),
    productsWithOfflineCache(true),
    api<{ ok: boolean; reminders: Reminder[] }>("/reminders"),
    api<SchedulesResult>("/schedules/now"),
    api<StreamersResult>("/sessions/streamers"),
  ]);
  if (active.ok) setIdleActiveSession(active.session?.sessionId ?? null);
  if (
    !active.ok ||
    !platformsResult.ok ||
    !productsResult.ok ||
    !remindersResult.ok
  )
    return card(t("live"), `<p class="text-error">${t("requestFailed")}</p>`);
  if (active.session) {
    const s = active.session;
    return card(
      t("liveInProgress"),
      `<div class="mb-4"><p><strong>${esc(s.platformName)}</strong> · ${t("sessionStartedAt")} ${esc(new Date(s.startTime).toLocaleString())}</p><p class="text-sm text-base-content/60" data-live-elapsed data-start-time="${esc(s.startTime)}">${t("liveElapsed")} —</p><a class="link link-primary text-sm" href="${esc(s.liveUrl)}" target="_blank" rel="noopener noreferrer">${t("liveUrl")}</a></div>${endSessionForm(s, productsResult.products, false)}`,
    );
  }
  const checklist = remindersResult.reminders
    .map(
      (reminder, groupIndex) =>
        `<fieldset class="mb-4"><legend class="font-semibold">${esc(reminder.title)}</legend>${reminder.items.map((item, itemIndex) => `<label class="label cursor-pointer justify-start gap-2"><input required name="reminder-${groupIndex}-${itemIndex}" value="${esc(item)}" type="checkbox" class="checkbox checkbox-primary"><span class="label-text">${esc(item)}</span></label>`).join("")}</fieldset>`,
    )
    .join("");
  const substituteOptions = streamers.ok
    ? streamers.streamers
        .filter((streamer) => streamer.id !== getUser()?.id)
        .map(
          (streamer) =>
            `<option value="${esc(streamer.id)}">${esc(streamer.name)}</option>`,
        )
        .join("")
    : "";
  const section = (icon:string,title:string,body:string,accent=false):string => `<section class="rounded-box border ${accent?"border-primary/40":"border-base-300"} bg-base-100 p-5 shadow-sm sm:p-6"><div class="mb-4 flex items-center gap-3"><span class="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-xl">${icon}</span><h2 class="text-lg font-bold">${title}</h2></div>${body}</section>`;
  const activePlatforms = platformsResult.platforms.filter((platform)=>platform.active);
  const platformCards = activePlatforms.map((platform,index)=>`<label class="relative cursor-pointer"><input class="peer sr-only" required type="radio" name="platformId" value="${platform.id}" ${index===0?"checked":""}><span class="flex min-h-20 items-center gap-3 rounded-box border-2 border-base-300 bg-base-100 p-3 font-semibold transition peer-checked:border-primary peer-checked:bg-primary/5 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-primary"><span class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-base-200 text-xl">${esc(platform.icon??"◉")}</span><span class="break-words">${esc(platform.name)}</span></span></label>`).join("");
  const hero = `<section class="rounded-box border border-base-300 bg-base-100 px-5 py-12 text-center shadow-sm sm:py-16"><span class="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-primary/15 text-primary"><svg width="28" height="28" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><circle cx="10" cy="10" r="3"/><path d="M5 10a5 5 0 0 1 10 0M2 10a8 8 0 0 1 16 0"/></svg></span><h1 class="text-2xl font-bold sm:text-3xl">${t("readyToLive")}</h1><p class="mt-2 text-base-content/60">${t("readyToLiveHint")}</p></section>`;
  return `<div class="space-y-4">${hero}${scheduledNowPanel(scheduledNow)}<form id="start-live-form" class="space-y-4"><div data-start-draft-notice class="hidden"></div>${section("🏪",t("choosePlatform"),`<div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">${platformCards}</div>`)}${section("🔗",`${t("liveUrl")} <span class="badge badge-error badge-outline ml-2">${currentLanguage()==="th"?"จำเป็น":"REQUIRED"}</span>`,`<input required name="liveUrl" type="url" placeholder="https://shopee.co.th/live?id=…" class="input input-bordered input-lg"><p class="mt-2 text-sm text-base-content/60">${t("liveUrlRequiredHint")}</p>`)}${section("🤳",t("startFace"),facePhotoPicker("startFace",true,"start","data-start-face-preview"),true)}${section("📸",t("optionalImages"),`<input name="startImages" type="file" multiple data-supporting-images accept="image/jpeg,image/png,image/webp" class="file-input file-input-bordered"><p class="mt-2 text-sm text-base-content/60">${t("optionalImagesHint")}</p><p class="mt-1 text-sm text-base-content/60" data-supporting-images-count></p><div data-image-preview data-start-images-preview data-image-preview-mode="supporting" class="mt-3 hidden" aria-live="polite"></div>`)}${section("🛍️",t("showcasePlan"),`<div class="grid gap-3 md:grid-cols-4">${showcaseInputs("showcasePlan",productsResult.products)}</div>`)}${section("🔁",t("substituteFor"),`<select name="substituteForId" class="select select-bordered"><option value="">${t("normalLive")}</option>${substituteOptions}</select>`)}${section("📋",t("checklist"),checklist||'<p class="text-sm text-base-content/60">—</p>')}${section("📝",t("issueBeforeLive"),`<textarea name="startNote" maxlength="150" rows="2" class="textarea textarea-bordered" data-start-note></textarea><p class="mt-1 text-right text-xs text-base-content/50"><span data-start-note-count>0</span>/150</p>`)}<p data-start-session-message class="hidden rounded-box p-3 text-sm" role="alert"></p><button class="btn btn-primary btn-lg w-full text-lg" type="submit">🔴 ${t("startLive")}</button></form></div>`;
}

function monthValue(): string {
  if (page === "dashboard") return dashboardMonth;
  if (page === "history" || page === "my-history") return historyMonth;
  return new Date().toISOString().slice(0, 7);
}
function money(value: number): string {
  return `฿${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0)}`;
}
function duration(mins: number): string {
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}
function historyEditMeta(session: ReportSession, language: Language = currentLanguage()): string {
  if (!session.lastEditedBy || !session.lastEditedAt || session.lastEditedBy === "SYSTEM") return "";
  const timestamp = new Intl.DateTimeFormat(language === "th" ? "th-TH" : "en-GB", {
    timeZone: "Asia/Bangkok",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(session.lastEditedAt));
  const message = tFor(language, "editedBy")
    .replace("{name}", session.lastEditedBy)
    .replace("{time}", timestamp);
  return `<small class="history-edit-meta audit-meta" title="${esc(message)}">✏️ ${esc(message)}</small>`;
}
function validationMeta(session: ReportSession, language: Language = currentLanguage()): string {
  if (!session.validatedBy || !session.validatedAt) return "";
  const timestamp = new Intl.DateTimeFormat(language === "th" ? "th-TH" : "en-GB", {
    timeZone: "Asia/Bangkok", day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date(session.validatedAt));
  const message = tFor(language, "validatedBy").replace("{name}", session.validatedBy).replace("{time}", timestamp);
  return `<small class="history-edit-meta audit-meta audit-meta-success" title="${esc(message)}">✓ ${esc(message)}</small>`;
}
function validationAction(session: ReportSession, language: Language = currentLanguage()): string {
  if (!session.editable) return "";
  const label = tFor(language, "validateAndEditData");
  return `<button type="button" class="btn btn-sm session-icon-action manager-validation-action" data-action="validate-session" data-id="${esc(session.sessionId)}" title="${esc(label)}" aria-label="${esc(label)}">🔍</button>`;
}
function validationStatus(session: ReportSession, language: Language = currentLanguage()): string {
  return `<span class="badge ${session.validated ? "badge-success badge-outline" : "badge-ghost"} audit-status-badge">${tFor(language, session.validated ? "validated" : "pendingValidation")}</span>`;
}
function validationSummary(session: ReportSession, language: Language = currentLanguage()): string {
  return `${validationStatus(session, language)}${validationMeta(session, language)}`;
}
function autoEndBadge(session: ReportSession, language: Language = currentLanguage()): string {
  if (!session.autoEnded) return "";
  const label = tFor(language, "autoClosed");
  return `<span class="badge badge-error badge-outline audit-status-badge auto-end-badge" title="${esc(label)}">${esc(label)}</span>`;
}
function imageCell(keys: Array<string | null>, label: string): string {
  const valid = keys.filter((key): key is string => Boolean(key));
  if (!valid.length) return "—";
  return `<div class="flex min-w-20 flex-wrap gap-1">${valid.map((key, index) => `<button type="button" class="btn btn-ghost h-12 w-12 p-0" data-history-image="${esc(key)}" aria-label="${esc(label)} ${index + 1}"><span class="loading loading-spinner loading-xs" aria-hidden="true"></span></button>`).join("")}</div>`;
}
function sessionRows(sessions: ReportSession[]): string {
  return sessions
    .map(
      (row) =>
        `<tr><td>${esc(row.date)}</td><td>${esc(row.platformIcon ?? "")} ${esc(row.platformName)}</td><td>${esc(row.staffName)}${row.substituteForName ? ` <span class="badge badge-outline">↔ ${esc(row.substituteForName)}</span>` : ""}</td><td>${esc(new Date(row.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }))}${row.endTime ? ` – ${esc(new Date(row.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }))}` : ""}</td><td>${duration(row.durationMins)}</td><td>${money(row.totalRevenue)}</td><td>${row.roi.toFixed(2)}×</td><td>${imageCell([row.startFaceImage, ...row.startImages], t("startPhotos"))}</td><td>${imageCell([row.endFaceImage, ...row.endImages], t("endPhotos"))}</td><td>${row.status === "ACTIVE" ? t("activeSession") : t("completed")}${autoEndBadge(row) ? `<br>${autoEndBadge(row)}` : ""}${row.validated ? `<br>${validationSummary(row)}` : ""}</td></tr>`,
    )
    .join("");
}
function sessionTable(sessions: ReportSession[]): string {
  return `<div class="overflow-x-auto"><table class="table table-sm"><thead><tr><th>${t("date")}</th><th>${t("platform")}</th><th>${t("streamer")}</th><th>${t("time")}</th><th>${t("duration")}</th><th>${t("totalRevenue")}</th><th>ROI</th><th>${t("startPhotos")}</th><th>${t("endPhotos")}</th><th>${t("status")}</th></tr></thead><tbody>${sessionRows(sessions) || `<tr><td colspan="10">${t("noData")}</td></tr>`}</tbody></table></div>`;
}
function historyShowcase(session: ReportSession, products: Product[]): string {
  const names = new Map(products.map((product) => [product.id, product.name]));
  const value = (items: string[]): string =>
    items
      .map((item) => names.get(item) ?? item)
      .filter(Boolean)
      .map(esc)
      .join(", ") || "—";
  return `<div class="min-w-44 text-xs leading-5"><div>${t("categoryFoodSupplement1")}: ${value(session.showcase.supp1)}</div><div>${t("categoryFoodSupplement2")}: ${value(session.showcase.supp2)}</div><div>${t("categoryCosmetic")}: ${value(session.showcase.cosmetic)}</div><div>${t("categoryOtherBrand")}: ${value(session.showcase.other)}</div></div>`;
}
function mobileMetric(label: string, value: string): string {
  return `<div class="mobile-session-metric"><p>${esc(label)}</p><strong>${value}</strong></div>`;
}
function sessionTimeRange(session: ReportSession, language: Language = currentLanguage()): string {
  const locale = language === "th" ? "th-TH" : "en-GB";
  const format = (value: string): string => new Date(value).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return session.endTime ? `${esc(format(session.startTime))} – ${esc(format(session.endTime))}` : esc(format(session.startTime));
}
function mobileHistoryCards(sessions: ReportSession[], products: Product[], mine: boolean): string {
  const admin = getUser()?.role === "admin";
  const cards = sessions.map((session) => {
    const status = session.status === "ACTIVE" ? t("activeSession") : t("completedBadge");
    const actions = `${!mine && admin && session.status === "ACTIVE" ? btn("admin-end-session", t("endLive"), session.sessionId) : ""}${session.status === "COMPLETED" && session.editable ? sessionEditAction(session.sessionId) : ""}`;
    return `<article class="mobile-session-card"><header class="mobile-session-header"><div><p class="mobile-session-date">${esc(session.date)}</p><h3>${esc(session.platformIcon ?? "")} ${esc(session.platformName)}</h3></div><div class="mobile-session-badges"><span class="badge ${session.status === "ACTIVE" ? "badge-warning" : "badge-outline"}">${status}</span>${session.validated ? `<span class="badge badge-success badge-outline">${t("validated")}</span>` : ""}${autoEndBadge(session)}</div></header><p class="mobile-session-streamer">${t("streamer")}: <strong>${esc(session.staffName)}</strong>${session.substituteForName ? `<small>${t("managerSubstituteFor")}: ${esc(session.substituteForName)}</small>` : ""}</p><div class="mobile-session-metrics">${mobileMetric(t("time"), sessionTimeRange(session))}${mobileMetric(t("duration"), duration(session.durationMins))}${mobileMetric(t("totalRevenue"), money(session.totalRevenue))}${mobileMetric("ROI", `${session.roi.toFixed(2)}×`)}</div>${actions ? `<div class="mobile-session-actions">${actions}</div>` : ""}${validationMeta(session)}${historyEditMeta(session)}<details class="mobile-session-details"><summary>${t("viewDetails")}</summary><div class="mobile-session-detail-grid">${mobileMetric(t("audience"), String(session.audience))}${mobileMetric("Verena", money(session.verenaSales))}${mobileMetric("Aff.", money(session.affiliateSales))}${mobileMetric(t("commission"), money(session.commission))}${mobileMetric(t("laborCost"), money(session.ot))}<div class="mobile-session-detail-wide">${t("orderNumbers")}: <strong>${session.orderNumbers ? esc(session.orderNumbers) : "—"}</strong></div>${session.liveUrl ? `<div class="mobile-session-detail-wide"><a class="link link-primary" href="${esc(session.liveUrl)}" target="_blank" rel="noopener noreferrer">🔗 ${t("liveUrl")}</a></div>` : ""}<div class="mobile-session-detail-wide">${historyShowcase(session, products)}</div><div class="mobile-session-detail-wide">${session.startNote ? `▶ ${esc(session.startNote)}` : ""}${session.startNote && session.endNote ? "<br>" : ""}${session.endNote ? `⏹ ${esc(session.endNote)}` : ""}${!session.startNote && !session.endNote ? "—" : ""}</div><div class="mobile-session-detail-wide">${t("startPhotos")}: ${imageCell([session.startFaceImage, ...session.startImages], t("startPhotos"))}</div><div class="mobile-session-detail-wide">${t("endPhotos")}: ${imageCell([session.endFaceImage, ...session.endImages], t("endPhotos"))}</div></div></details></article>`;
  }).join("");
  return `<div class="mobile-session-list">${cards || `<p class="rounded-box border border-base-300 p-4 text-base-content/60">${t("noData")}</p>`}</div>`;
}
function historyFilterForm(sessions: ReportSession[]): string {
  const selectedYear = Number(historyMonth.slice(0, 4));
  const selectedMonth = Number(historyMonth.slice(5, 7));
  const currentYear = Number(new Intl.DateTimeFormat("en", { timeZone:"Asia/Bangkok", year:"numeric" }).format(new Date()));
  const years = Array.from({ length:4 }, (_, index) => currentYear - 3 + index)
    .map((year) => `<option value="${year}" ${year === selectedYear ? "selected" : ""}>${currentLanguage() === "th" ? year + 543 : year}</option>`)
    .join("");
  const months = Array.from({ length:12 }, (_, index) => index + 1)
    .map((month) => `<option value="${month}" ${month === selectedMonth ? "selected" : ""}>${esc(new Intl.DateTimeFormat(currentLanguage() === "th" ? "th-TH" : "en-US", { month:"long" }).format(new Date(2020, month - 1, 1)))}</option>`)
    .join("");
  const staff = new Map<string,string>();
  const platforms = new Map<string,string>();
  sessions.forEach((session) => {
    staff.set(session.staffId, session.staffName);
    platforms.set(session.platformId, `${session.platformIcon ?? ""} ${session.platformName}`.trim());
  });
  const currentUser = getUser();
  if (currentUser?.role === "streamer") staff.set(currentUser.id, currentUser.name);
  const staffOptions = [...staff.entries()].sort(([, left], [, right]) => left.localeCompare(right, currentLanguage() === "th" ? "th" : "en"))
    .map(([id, name]) => `<option value="${esc(id)}" ${historyFilters.staffId === id ? "selected" : ""}>${esc(name)}</option>`).join("");
  const platformOptions = [...platforms.entries()].sort(([, left], [, right]) => left.localeCompare(right, currentLanguage() === "th" ? "th" : "en"))
    .map(([id, name]) => `<option value="${esc(id)}" ${historyFilters.platformId === id ? "selected" : ""}>${esc(name)}</option>`).join("");
  return `<form id="history-filter-form" class="mb-4 grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-10"><label class="form-control"><span class="label-text">${t("year")}</span><select id="history-year" class="select select-bordered">${years}</select></label><label class="form-control"><span class="label-text">${t("month")}</span><select id="history-month" class="select select-bordered">${months}</select></label><label class="form-control"><span class="label-text">${t("from")}</span><input name="dateFrom" type="date" value="${esc(historyFilters.dateFrom)}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("to")}</span><input name="dateTo" type="date" value="${esc(historyFilters.dateTo)}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("streamer")}</span><select name="staffId" class="select select-bordered"><option value="">${t("allStreamers")}</option>${staffOptions}</select></label><label class="form-control"><span class="label-text">${t("platform")}</span><select name="platformId" class="select select-bordered"><option value="">${t("allPlatforms")}</option>${platformOptions}</select></label><label class="form-control"><span class="label-text">${t("employment")}</span><select name="employmentType" class="select select-bordered"><option value="">${t("allEmployment")}</option><option value="Full-time" ${historyFilters.employmentType === "Full-time" ? "selected" : ""}>${t("fullTime")}</option><option value="Part-time" ${historyFilters.employmentType === "Part-time" ? "selected" : ""}>${t("partTime")}</option></select></label><label class="form-control"><span class="label-text">${t("status")}</span><select name="status" class="select select-bordered"><option value="">${t("allStatuses")}</option><option value="ACTIVE" ${historyFilters.status === "ACTIVE" ? "selected" : ""}>${t("activeSession")}</option><option value="COMPLETED" ${historyFilters.status === "COMPLETED" ? "selected" : ""}>${t("completed")}</option></select></label><label class="form-control"><span class="label-text">${t("rowsPerPage")}</span><select name="pageSize" class="select select-bordered">${([25,50,100] as const).map((size) => `<option value="${size}" ${historyPageSize === size ? "selected" : ""}>${size}</option>`).join("")}</select></label><button type="submit" class="btn btn-primary">🔍 ${t("applyFilters")}</button></form>`;
}
function bangkokDateTimeInput(value: string): string {
  const values = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes): string => values.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
}
function sessionEditDialog(session: ReportSession, products: Product[], admin: boolean): string {
  const id = `session-edit-${session.sessionId}`;
  return `<dialog id="${esc(id)}" class="modal"><div class="modal-box max-w-3xl"><h3 class="text-lg font-bold">${t("editSession")}</h3><form id="session-edit-form" data-admin="${admin ? "true" : "false"}" class="mt-4 grid gap-3 md:grid-cols-2"><input type="hidden" name="id" value="${esc(session.sessionId)}">${admin ? `<label class="form-control"><span class="label-text">${t("startTime")}</span><input required name="startTime" type="datetime-local" value="${esc(bangkokDateTimeInput(session.startTime))}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("endTime")}</span><input required name="endTime" type="datetime-local" value="${esc(session.endTime ? bangkokDateTimeInput(session.endTime) : "")}" class="input input-bordered"></label>` : ""}<label class="form-control"><span class="label-text">${t("verenaSales")}</span><input required name="verena" type="number" min="0" step="0.01" value="${session.verenaSales}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("affiliateSales")}</span><input required name="affiliate" type="number" min="0" step="0.01" value="${session.affiliateSales}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("audience")}</span><input required name="audience" type="number" min="0" step="1" value="${session.audience}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("orderNumbers")}</span><textarea name="orderNumbers" maxlength="500" rows="2" class="textarea textarea-bordered">${esc(session.orderNumbers)}</textarea></label><div class="md:col-span-2 grid gap-3 md:grid-cols-4"><p class="md:col-span-4 font-semibold">${t("actualShowcase")}</p>${showcaseInputs("edit-showcase", products, session.showcase)}</div>${admin ? `<label class="form-control md:col-span-2"><span class="label-text">${t("endNote")}</span><textarea name="endNote" maxlength="150" rows="2" class="textarea textarea-bordered">${esc(session.endNote)}</textarea></label>` : ""}<div class="modal-action md:col-span-2"><button type="button" class="btn" data-close-dialog>${t("cancel")}</button><button type="submit" class="btn btn-primary">${t("save")}</button></div></form></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}
function validationDialog(session: ReportSession, products: Product[]): string {
  const id = `session-validation-${session.sessionId}`;
  const actionLabel = session.validated ? t("revalidateSession") : t("validateSession");
  return `<dialog id="${esc(id)}" class="modal"><div class="modal-box max-w-3xl"><form method="dialog"><button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" aria-label="${esc(t("cancel"))}">✕</button></form><h3 class="text-lg font-bold">${t("validateLiveSession")}</h3><p class="mt-2 text-sm text-base-content/60">${t("validationEditHint")}</p><form id="session-validation-form" class="mt-4 grid gap-3 md:grid-cols-2"><input type="hidden" name="id" value="${esc(session.sessionId)}"><label class="form-control"><span class="label-text">${t("startTime")}</span><input required name="startTime" type="datetime-local" value="${esc(bangkokDateTimeInput(session.startTime))}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("endTime")}</span><input required name="endTime" type="datetime-local" value="${esc(session.endTime ? bangkokDateTimeInput(session.endTime) : "")}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("verenaSales")}</span><input required name="verena" type="number" min="0" step="0.01" value="${session.verenaSales}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("affiliateSales")}</span><input required name="affiliate" type="number" min="0" step="0.01" value="${session.affiliateSales}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("audience")}</span><input required name="audience" type="number" min="0" step="1" value="${session.audience}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("endNote")}</span><textarea name="endNote" maxlength="150" rows="2" class="textarea textarea-bordered">${esc(session.endNote)}</textarea></label><label class="form-control md:col-span-2"><span class="label-text">${t("orderNumbers")}</span><textarea name="orderNumbers" maxlength="500" rows="2" class="textarea textarea-bordered">${esc(session.orderNumbers)}</textarea></label><div class="md:col-span-2 grid gap-3 md:grid-cols-4"><p class="md:col-span-4 font-semibold">${t("actualShowcase")}</p>${showcaseInputs("validation-showcase", products, session.showcase)}</div><section class="validation-preview md:col-span-2"><h4>${t("recalculationPreview")}</h4><p data-validation-preview class="text-sm text-base-content/70">…</p></section><aside class="validation-action-hints md:col-span-2"><p>${t("validationSaveHint")}</p><p>${t("validationConfirmHint")}</p></aside><p data-validation-message class="hidden rounded-box p-3 text-sm md:col-span-2" role="alert"></p><div class="modal-action md:col-span-2"><button type="button" class="btn" data-close-dialog>${t("cancel")}</button><button type="button" class="btn" data-save-validation>${t("save")}</button><button type="button" class="btn btn-success" data-submit-validation>${actionLabel}</button></div></form></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}
function historyTotalsCard(sessions: ReportSession[]): string {
  const sum = (field: keyof Pick<ReportSession, "durationMins" | "ot" | "verenaSales" | "affiliateSales" | "commission" | "totalRevenue">): number => sessions.reduce((total, session) => total + session[field], 0);
  return `<section class="mt-4 rounded-box border border-base-300 bg-base-200/50 p-4"><h2 class="mb-3 text-sm font-semibold">${t("historyFilterTotals")}</h2><div class="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7"><div><p class="text-xs text-base-content/60">${t("totalSessions")}</p><p class="font-semibold">${sessions.length.toLocaleString()}</p></div><div><p class="text-xs text-base-content/60">${t("duration")}</p><p class="font-semibold">${duration(sum("durationMins"))}</p></div><div><p class="text-xs text-base-content/60">${t("laborCost")}</p><p class="font-semibold">${money(sum("ot"))}</p></div><div><p class="text-xs text-base-content/60">Verena</p><p class="font-semibold">${money(sum("verenaSales"))}</p></div><div><p class="text-xs text-base-content/60">Aff.</p><p class="font-semibold">${money(sum("affiliateSales"))}</p></div><div><p class="text-xs text-base-content/60">Comm.</p><p class="font-semibold">${money(sum("commission"))}</p></div><div><p class="text-xs text-base-content/60">${t("totalRevenue")}</p><p class="font-semibold">${money(sum("totalRevenue"))}</p></div></div></section>`;
}
function historyTable(sessions: ReportSession[], products: Product[], mine: boolean): string {
  const admin = getUser()?.role === "admin";
  const sort = historySort;
  const sortedSessions = sort
    ? [...sessions].sort((left, right) => {
        const leftValue = sort.key === "date" ? `${left.date} ${left.startTime}` : left[sort.key];
        const rightValue = sort.key === "date" ? `${right.date} ${right.startTime}` : right[sort.key];
        const comparison = typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), currentLanguage() === "th" ? "th" : "en", { numeric: true, sensitivity: "base" });
        return sort.direction === "asc" ? comparison : -comparison;
      })
    : [...sessions];
  const sortableHeader = (key: HistorySortKey, label: string): string => {
    const active = historySort?.key === key;
    const arrow = active ? (historySort?.direction === "asc" ? "▲" : "▼") : "↕";
    const nextAction = !active
      ? t("sortAscending")
      : historySort?.direction === "asc"
        ? t("sortDescending")
        : t("resetSorting");
    return `<button type="button" class="history-sort" data-history-sort="${key}" aria-label="${esc(label)} - ${nextAction}"><span>${label}</span><span aria-hidden="true">${arrow}</span></button>`;
  };
  const pageCount = Math.max(1, Math.ceil(sortedSessions.length / historyPageSize));
  historyPage = Math.min(Math.max(1, historyPage), pageCount);
  const visibleSessions = sortedSessions.slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize);
  historyVisibleSessions = new Map(visibleSessions.map((session) => [session.sessionId, session]));
  historyDialogProducts = products;
  const rows = visibleSessions
    .map(
      (session) =>
        `<tr><td class="history-sticky history-col-date">${esc(session.date)}${autoEndBadge(session) ? `<br>${autoEndBadge(session)}` : ""}</td><td class="history-sticky history-col-platform">${esc(session.platformIcon ?? "")} ${esc(session.platformName)}</td><td class="history-col-streamer">${esc(session.staffName)}${session.substituteForName ? `<small class="manager-substitute">${t("managerSubstituteFor")}: ${esc(session.substituteForName)}</small>` : ""}</td><td>${session.liveUrl ? `<a class="btn btn-ghost btn-xs" href="${esc(session.liveUrl)}" target="_blank" rel="noopener noreferrer">🔗</a>` : "—"}</td><td>${esc(new Date(session.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }))}</td><td>${session.endTime ? esc(new Date(session.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })) : "—"}</td><td>${session.durationMins ? duration(session.durationMins) : "—"}</td><td>${money(session.ot)}</td><td>${money(session.verenaSales)}</td><td>${money(session.affiliateSales)}</td><td>${money(session.commission)}</td><td><strong>${money(session.totalRevenue)}</strong></td><td>${session.roi.toFixed(2)}×</td><td>${imageCell([session.startFaceImage, ...session.startImages], t("startPhotos"))}</td><td>${imageCell([session.endFaceImage, ...session.endImages], t("endPhotos"))}</td><td>${historyShowcase(session, products)}</td><td class="min-w-40 text-xs">${session.startNote ? `▶ ${esc(session.startNote)}` : ""}${session.startNote && session.endNote ? "<br>" : ""}${session.endNote ? `⏹ ${esc(session.endNote)}` : ""}${!session.startNote && !session.endNote ? "—" : ""}</td><td class="history-sticky history-col-status">${session.status === "ACTIVE" ? t("activeSession") : t("completed")}${session.validated ? `<br>${validationSummary(session)}` : ""}${historyEditMeta(session)}${!mine && admin && session.status === "ACTIVE" ? `<br>${btn("admin-end-session", t("endLive"), session.sessionId)}` : ""}${session.status === "COMPLETED" && session.editable ? `<br>${sessionEditAction(session.sessionId)}` : ""}</td></tr>`,
    )
    .join("");
  const dialogs = [
    ...visibleSessions.filter((session) => admin && !mine && session.status === "ACTIVE").map((session) => adminEndSessionDialog(session, products)),
  ].join("");
  const summary = t("historyPageSummary").replace("{page}", String(historyPage)).replace("{pages}", String(pageCount)).replace("{total}", String(sortedSessions.length));
  const pager = `<div class="mt-4 flex flex-wrap items-center justify-between gap-3"><span class="text-sm text-base-content/60">${esc(summary)}</span><div class="join"><button type="button" class="btn join-item" data-action="history-page" data-id="${Math.max(1, historyPage - 1)}" ${historyPage <= 1 ? "disabled" : ""}>← ${t("previousPage")}</button><button type="button" class="btn join-item" data-action="history-page" data-id="${Math.min(pageCount, historyPage + 1)}" ${historyPage >= pageCount ? "disabled" : ""}>${t("nextPage")} →</button></div></div>`;
  const desktopTable = `<div class="history-scroll-frame"><div class="history-scroll"><table class="history-table table table-sm"><thead><tr><th class="history-sticky history-col-date">${sortableHeader("date", t("date"))}</th><th class="history-sticky history-col-platform">${sortableHeader("platformName", t("platform"))}</th><th class="history-col-streamer">${sortableHeader("staffName", t("streamer"))}</th><th>${t("liveUrl")}</th><th>${t("startTime")}</th><th>${t("endTime")}</th><th>${sortableHeader("durationMins", t("duration"))}</th><th>${t("laborCost")} (฿)</th><th>${sortableHeader("verenaSales", "Verena (฿)")}</th><th>${sortableHeader("affiliateSales", "Aff. (฿)")}</th><th>${sortableHeader("commission", "Comm. (฿)")}</th><th>${sortableHeader("totalRevenue", t("totalRevenue"))}</th><th>${sortableHeader("roi", "ROI")}</th><th>${t("startPhotos")}</th><th>${t("endPhotos")}</th><th>${t("actualShowcase")}</th><th>${t("note")}</th><th class="history-sticky history-col-status">${t("status")}</th></tr></thead><tbody>${rows || `<tr><td colspan="18">${t("noData")}</td></tr>`}</tbody></table></div></div>`;
  return `<div class="desktop-session-data">${desktopTable}</div><div class="mobile-session-data">${mobileHistoryCards(visibleSessions, products, mine)}</div>${pager}${dialogs}`;
}
function historyPdfDialog(): string {
  return `<dialog id="history-pdf-dialog" class="modal"><div class="modal-box"><form method="dialog"><button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" aria-label="${t("cancel")}">✕</button></form><h3 class="text-lg font-bold">${t("pdfLanguageTitle")}</h3><p class="mt-3 text-sm text-base-content/70">${t("pdfLanguageHint")}</p><p class="mt-2 text-xs text-base-content/60">${t("pdfSaveHint")}</p><div class="mt-5 grid grid-cols-2 gap-3"><button type="button" class="btn btn-primary" data-action="export-history-pdf" data-id="th">${t("thaiPdf")}</button><button type="button" class="btn" data-action="export-history-pdf" data-id="en">${t("englishPdf")}</button></div></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}
function pdfNumber(value: number, language: Language): string {
  return new Intl.NumberFormat(language === "th" ? "th-TH" : "en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}
function pdfMoney(value: number, language: Language): string {
  return new Intl.NumberFormat(language === "th" ? "th-TH" : "en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}
function pdfTime(value: string | null, language: Language): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(language === "th" ? "th-TH" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}
function pdfShowcase(session: ReportSession, products: Product[], language: Language): string {
  const names = new Map(products.map((product) => [product.id, product.name]));
  const value = (items: string[]): string =>
    items.map((item) => names.get(item) ?? item).filter(Boolean).map(esc).join(", ") || "—";
  return [
    `${tFor(language, "categoryFoodSupplement1")}: ${value(session.showcase.supp1)}`,
    `${tFor(language, "categoryFoodSupplement2")}: ${value(session.showcase.supp2)}`,
    `${tFor(language, "categoryCosmetic")}: ${value(session.showcase.cosmetic)}`,
    `${tFor(language, "categoryOtherBrand")}: ${value(session.showcase.other)}`,
  ].join("<br>");
}
function pdfDailyChart(sessions: ReportSession[], language: Language): string {
  const rows = new Map<string, { verena: number; roiRevenue: number; ot: number }>();
  sessions.forEach((session) => {
    const current = rows.get(session.date) ?? { verena: 0, roiRevenue: 0, ot: 0 };
    current.verena += session.verenaSales;
    current.roiRevenue += session.totalRevenue;
    current.ot += session.ot;
    rows.set(session.date, current);
  });
  const daily = [...rows.entries()].sort(([left], [right]) => left.localeCompare(right));
  if (!daily.length) return `<p class="empty">${tFor(language, "noData")}</p>`;
  const maxSales = Math.max(...daily.map(([, row]) => row.verena), 1);
  const maxRoi = Math.max(...daily.map(([, row]) => row.roiRevenue / (row.ot || 1)), 1);
  const width = 960;
  const height = 300;
  const left = 48;
  const plotHeight = height - 70;
  const step = (width - left - 22) / daily.length;
  const points = daily
    .map(([, row], index) => {
      const roi = row.roiRevenue / (row.ot || 1);
      const x = left + step * index + step / 2;
      const y = 18 + plotHeight - (roi / maxRoi) * plotHeight;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const bars = daily
    .map(([date, row], index) => {
      const barHeight = (row.verena / maxSales) * plotHeight;
      const x = left + step * index + step * 0.18;
      const day = date.slice(-2);
      return `<rect x="${x.toFixed(1)}" y="${(18 + plotHeight - barHeight).toFixed(1)}" width="${Math.max(6, step * 0.64).toFixed(1)}" height="${barHeight.toFixed(1)}" rx="3" fill="#3b82f6"/><text x="${(left + step * index + step / 2).toFixed(1)}" y="${height - 16}" text-anchor="middle" class="axis">${esc(day)}</text>`;
    })
    .join("");
  return `<svg viewBox="0 0 ${width} ${height}" role="img" aria-label="${esc(tFor(language, "dailySalesRoi"))}" class="pdf-chart"><line x1="${left}" y1="${18 + plotHeight}" x2="${width - 22}" y2="${18 + plotHeight}" class="grid"/><line x1="${left}" y1="18" x2="${left}" y2="${18 + plotHeight}" class="grid"/><text x="${left}" y="12" class="legend blue">■ ${esc(tFor(language, "verenaSales"))}</text><text x="${left + 190}" y="12" class="legend pink">● ROI</text><polyline points="${points}" fill="none" stroke="#ec4899" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>${bars}</svg>`;
}
function legacyExportHistoryPdf(language: Language): void {
  const sessions = historyPdfSessions.filter((session) => session.status === "COMPLETED");
  if (!sessions.length) {
    notice = t("noData");
    void render();
    return;
  }
  const reportWindow = window.open("", "_blank");
  if (!reportWindow) {
    notice = t("pdfPopupBlocked");
    void render();
    return;
  }
  document
    .querySelector<HTMLDialogElement>("#history-pdf-dialog")
    ?.close();
  const total = (key: keyof Pick<ReportSession, "verenaSales" | "affiliateSales" | "commission" | "totalRevenue" | "ot" | "durationMins">): number => sessions.reduce((sum, session) => sum + session[key], 0);
  const totalRevenue = total("totalRevenue");
  const totalOt = total("ot");
  const totalMins = total("durationMins");
  const overallRoi = totalOt ? totalRevenue / totalOt : 0;
  const title = `${tFor(language, "history")} · ${historyMonth}`;
  const rows = sessions
    .map((session) => `<tr><td>${esc(session.date)}</td><td>${esc(session.platformIcon ?? "")} ${esc(session.platformName)}</td><td>${esc(session.staffName)}</td><td>${session.liveUrl ? esc(session.liveUrl) : "—"}</td><td>${pdfTime(session.startTime, language)}</td><td>${pdfTime(session.endTime, language)}</td><td>${session.durationMins ? duration(session.durationMins) : "—"}</td><td>${pdfMoney(session.ot, language)}</td><td>${pdfMoney(session.verenaSales, language)}</td><td>${pdfMoney(session.affiliateSales, language)}</td><td>${pdfMoney(session.commission, language)}</td><td>${pdfMoney(session.totalRevenue, language)}</td><td>${session.roi.toFixed(2)}×</td><td>${pdfShowcase(session, historyPdfProducts, language)}</td><td>${session.startNote ? `▶ ${esc(session.startNote)}` : ""}${session.startNote && session.endNote ? "<br>" : ""}${session.endNote ? `⏹ ${esc(session.endNote)}` : ""}${!session.startNote && !session.endNote ? "—" : ""}</td><td>${session.status === "ACTIVE" ? tFor(language, "activeSession") : tFor(language, "completed")}</td></tr>`)
    .join("");
  const header = ["date", "platform", "streamer", "liveUrl", "startTime", "endTime", "duration"].map((key) => tFor(language, key as keyof typeof import("./i18n/th").th));
  const html = `<!doctype html><html lang="${language}"><head><meta charset="utf-8"><title>${esc(title)}</title><style>@page{size:A4 landscape;margin:10mm}*{box-sizing:border-box}body{font-family:Sarabun,"Noto Sans Thai",Arial,sans-serif;color:#2f3b52;margin:0;font-size:9px}.banner{background:#ef6b3a;color:#fff;padding:18px 22px;border-radius:10px}.banner h1{margin:0;font-size:23px}.banner p{margin:5px 0 0;font-size:11px}.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:16px 0}.kpi{border:1px solid #eadbcc;border-radius:8px;padding:10px;background:#fffaf4}.kpi b{display:block;font-size:16px;color:#ef6b3a;margin-top:4px}.section{margin-top:18px;break-inside:avoid}.section h2{font-size:15px;margin:0 0 8px}.pdf-chart{width:100%;height:auto;border:1px solid #eadbcc;border-radius:8px;padding:8px}.grid{stroke:#e8ded2;stroke-width:1}.axis{font-size:11px;fill:#536176}.legend{font-size:12px;font-weight:700}.blue{fill:#2563eb}.pink{fill:#db2777}table{width:100%;border-collapse:collapse;table-layout:fixed}th{background:#ef6b3a;color:#fff;font-size:7px}th,td{border:1px solid #eadbcc;padding:5px;vertical-align:top;overflow-wrap:anywhere}td{font-size:6.8px;line-height:1.32}tr:nth-child(even){background:#fffaf4}.page-break{break-before:page}.muted{color:#7c8798}@media print{.no-print{display:none}}</style></head><body><header class="banner"><h1>${esc(tFor(language, "appName"))} · ROI</h1><p>${esc(title)} · ${pdfNumber(sessions.length, language)} ${esc(tFor(language, "sessions"))}</p></header><section class="kpis"><div class="kpi">${esc(tFor(language, "verenaSales"))}<b>฿${pdfMoney(total("verenaSales"), language)}</b></div><div class="kpi">${esc(tFor(language, "affiliateSales"))}<b>฿${pdfMoney(total("affiliateSales"), language)}</b></div><div class="kpi">${esc(tFor(language, "totalRevenue"))}<b>฿${pdfMoney(totalRevenue, language)}</b></div><div class="kpi">ROI<b>${overallRoi.toFixed(2)}×</b></div><div class="kpi">${esc(tFor(language, "totalOt"))}<b>฿${pdfMoney(totalOt, language)}</b></div><div class="kpi">${esc(tFor(language, "totalHours"))}<b>${(totalMins / 60).toFixed(1)}</b></div><div class="kpi">${esc(tFor(language, "commission"))}<b>฿${pdfMoney(total("commission"), language)}</b></div><div class="kpi">${esc(tFor(language, "sessions"))}<b>${pdfNumber(sessions.length, language)}</b></div></section><section class="section"><h2>${esc(tFor(language, "dailySalesRoi"))}</h2>${pdfDailyChart(sessions, language)}</section><section class="section page-break"><h2>${esc(tFor(language, "history"))}</h2><table><thead><tr>${header.map((label) => `<th>${esc(label)}</th>`).join("")}<th>${esc(tFor(language, "laborCost"))}</th><th>Verena</th><th>Aff.</th><th>Comm.</th><th>${esc(tFor(language, "totalRevenue"))}</th><th>ROI</th><th>${esc(tFor(language, "actualShowcase"))}</th><th>${esc(tFor(language, "note"))}</th><th>${esc(tFor(language, "status"))}</th></tr></thead><tbody>${rows}</tbody></table></section><script>window.onload=()=>window.print()</script></body></html>`;
  reportWindow.document.open();
  reportWindow.document.write(html);
  reportWindow.document.close();
}
void legacyExportHistoryPdf;

async function exportHistoryPdf(language: Language): Promise<void> {
  const sessions = historyPdfSessions.map<HistoryPdfSession>((session) => ({
    date: session.date,
    staffName: session.staffName,
    platformName: session.platformName,
    startTime: session.startTime,
    endTime: session.endTime,
    durationMins: session.durationMins,
    ot: session.ot,
    verenaSales: session.verenaSales,
    affiliateSales: session.affiliateSales,
    commission: session.commission,
    totalRevenue: session.totalRevenue,
    roi: session.roi,
    startNote: session.startNote,
    endNote: session.endNote,
    status: session.status,
  }));
  if (!sessions.some((session) => session.status === "COMPLETED")) {
    notice = t("noData");
    await render();
    return;
  }
  document.querySelector<HTMLDialogElement>("#history-pdf-dialog")?.close();
  try {
    await downloadHistoryPdf({
      language,
      month: historyMonth,
      sessions,
      settings: historyPdfSettings,
    });
  } catch (error: unknown) {
    notice = error instanceof Error ? error.message : t("requestFailed");
    await render();
  }
}

async function hydrateHistoryImages(): Promise<void> {
  const token = getToken();
  if (!token) return;
  const load = async (button: HTMLButtonElement): Promise<void> => {
    const key = button.dataset.historyImage;
    if (!key || !button.isConnected || button.dataset.imageLoadState) return;
    button.dataset.imageLoadState = "loading";
    let response: Response;
    try {
      response = await fetch(
        `${env.apiUrl}/images/view?key=${encodeURIComponent(key)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      if (button.isConnected) {
        button.dataset.imageLoadState = "failed";
        button.textContent = "⚠";
        button.title = t("imageUnavailable");
      }
      return;
    }
    if (!response.ok) {
      if (response.status === 401 && getToken())
        window.dispatchEvent(new Event(authExpiredEvent));
      button.dataset.imageLoadState = "failed";
      button.textContent = "⚠";
      button.title = t("imageUnavailable");
      return;
    }
    let blob: Blob;
    try {
      blob = await response.blob();
    } catch {
      button.dataset.imageLoadState = "failed";
      button.textContent = "⚠";
      button.title = t("imageUnavailable");
      return;
    }
    const url = URL.createObjectURL(blob);
    if (!button.isConnected) {
      URL.revokeObjectURL(url);
      return;
    }
    historyImageUrls.push(url);
    const image = document.createElement("img");
    image.src = url;
    image.alt = button.getAttribute("aria-label") ?? t("photo");
    image.className = "h-12 w-12 rounded object-cover";
    image.loading = "lazy";
    image.decoding = "async";
    button.replaceChildren(image);
    button.dataset.imageLoadState = "loaded";
    button.addEventListener("click", () =>
      window.open(url, "_blank", "noopener,noreferrer"),
    );
  };
  const buttons = [...document.querySelectorAll<HTMLButtonElement>("[data-history-image]")];
  const runLimited = async <T>(items: T[], concurrency: number, task: (item: T) => Promise<void>): Promise<void> => {
    const queue = [...items];
    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (queue.length) {
        const item = queue.shift();
        if (item !== undefined) await task(item);
      }
    }));
  };
  const eagerImages = managerExportLanguage !== null && page === "manager-report";
  if (eagerImages) {
    await runLimited(buttons, 2, load);
  } else if ("IntersectionObserver" in window) {
    const queue: HTMLButtonElement[] = [];
    let active = 0;
    const pump = (): void => {
      while (active < 2 && queue.length) {
        const button = queue.shift();
        if (!button) continue;
        active += 1;
        void load(button).finally(() => {
          active -= 1;
          pump();
        });
      }
    };
    historyImageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || !(entry.target instanceof HTMLButtonElement)) return;
        observer.unobserve(entry.target);
        queue.push(entry.target);
      });
      pump();
    }, { rootMargin: "160px" });
    buttons.forEach((button) => historyImageObserver?.observe(button));
  } else {
    await runLimited(buttons.slice(0, 8), 1, load);
  }
  const profiles = [...document.querySelectorAll<HTMLElement>("[data-profile-image]")];
  await runLimited(profiles, 2, async (container) => {
    const key = container.dataset.profileImage;
    if (!key || !container.isConnected) return;
    let response: Response;
    try {
      response = await fetch(`${env.apiUrl}/images/view?key=${encodeURIComponent(key)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      return;
    }
    if (!response.ok) {
      if (response.status === 401 && getToken())
        window.dispatchEvent(new Event(authExpiredEvent));
      return;
    }
    let blob: Blob;
    try {
      blob = await response.blob();
    } catch {
      return;
    }
    const url = URL.createObjectURL(blob);
    if (!container.isConnected) {
      URL.revokeObjectURL(url);
      return;
    }
    historyImageUrls.push(url);
    const image = document.createElement("img");
    image.src = url;
    image.alt = t("profileImage");
    image.className = "h-full w-full object-cover";
    container.replaceChildren(image);
  });
}
function dashboardBars(
  rows: Array<{
    label: string;
    value: number;
    detail: string;
    commission?: number;
    roi?: number;
  }>,
  color: string,
): string {
  if (
    rows.length &&
    rows[0].commission !== undefined &&
    rows[0].roi !== undefined
  )
    return dashboardDailyChart(
      rows.map((row) => ({
        label: row.label,
        verena: row.value,
        commission: row.commission ?? 0,
        roi: row.roi ?? 0,
      })),
    );
  const max = Math.max(1, ...rows.map((row) => row.value));
  if (!rows.length) return `<p class="text-base-content/60">${t("noData")}</p>`;
  return `<div class="h-60"><div class="flex h-48 items-end gap-2 border-b border-base-300 pt-4">${rows.map((row) => `<div class="group flex h-full min-w-0 flex-1 flex-col justify-end" title="${esc(`${row.label}: ${row.detail}`)}"><span class="mb-1 text-center text-xs">${esc(row.detail)}</span><div class="shrink-0 rounded-t" style="height:${Math.max(3, Math.round((row.value / max) * 78))}%;background:${color}"></div></div>`).join("")}</div><div class="flex gap-2 pt-2">${rows.map((row) => `<span class="min-w-0 flex-1 break-words text-center text-xs leading-tight">${esc(row.label)}</span>`).join("")}</div></div>`;
}
interface DailyChartRow {
  label: string;
  verena: number;
  commission: number;
  roi: number;
}
function dashboardDailyChart(rows: DailyChartRow[]): string {
  if (!rows.length) return `<p class="text-base-content/60">${t("noData")}</p>`;
  const salesMax = Math.max(
    1,
    ...rows.map((row) => Math.max(row.verena, row.commission)),
  );
  const roiMax = Math.max(1, ...rows.map((row) => row.roi));
  const width = 1000;
  const left = 48;
  const right = 48;
  const top = 24;
  const bottom = 218;
  const group = (width - left - right) / rows.length;
  const barWidth = Math.min(17, group * 0.28);
  const salesY = (value: number) =>
    bottom - (value / salesMax) * (bottom - top);
  const roiY = (value: number) => bottom - (value / roiMax) * (bottom - top);
  const points = rows
    .map((row, index) => `${left + group * (index + 0.5)},${roiY(row.roi)}`)
    .join(" ");
  return `<div class="mb-2 flex flex-wrap justify-center gap-4 text-xs"><span><i class="mr-1 inline-block h-3 w-3 rounded-sm bg-blue-500"></i>${t("verenaSales")}</span><span><i class="mr-1 inline-block h-3 w-3 rounded-sm bg-amber-500"></i>${t("commission")}</span><span><i class="mr-1 inline-block h-3 w-3 rounded-full border-2 border-pink-500"></i>ROI</span></div><svg class="h-64 w-full" viewBox="0 0 ${width} 260" role="img" aria-label="${esc(t("dailySalesRoi"))}"><line x1="${left}" y1="${bottom}" x2="${width - right}" y2="${bottom}" stroke="#d8d1c4"/><line x1="${left}" y1="${top}" x2="${left}" y2="${bottom}" stroke="#d8d1c4"/><text x="2" y="${top + 5}" font-size="11" fill="#8b7866">${money(salesMax)}</text><text x="12" y="${bottom}" font-size="11" fill="#8b7866">${money(0)}</text><text x="${width - right + 5}" y="${top + 5}" font-size="11" fill="#ec4899">${roiMax.toFixed(1)}×</text><text x="${width - right + 5}" y="${bottom}" font-size="11" fill="#ec4899">0×</text>${rows
    .map((row, index) => {
      const center = left + group * (index + 0.5);
      return `<g><title>${esc(`${row.label}: ${money(row.verena)} · ${t("commission")} ${money(row.commission)} · ROI ${row.roi.toFixed(2)}×`)}</title><rect x="${center - barWidth - 2}" y="${salesY(row.verena)}" width="${barWidth}" height="${Math.max(1, bottom - salesY(row.verena))}" rx="3" fill="#3b82f6"/><rect x="${center + 2}" y="${salesY(row.commission)}" width="${barWidth}" height="${Math.max(1, bottom - salesY(row.commission))}" rx="3" fill="#f59e0b"/><text x="${center}" y="${bottom + 20}" text-anchor="middle" font-size="11" fill="#6b7280">${esc(row.label)}</text></g>`;
    })
    .join(
      "",
    )}<polyline points="${points}" fill="none" stroke="#ec4899" stroke-width="3"/>${rows
    .map((row, index) => {
      const x = left + group * (index + 0.5);
      const y = roiY(row.roi);
      return `<circle cx="${x}" cy="${y}" r="4" fill="#fff" stroke="#ec4899" stroke-width="2"/>`;
    })
    .join("")}</svg>`;
}
async function dashboard(): Promise<string> {
  const [result, action] = await Promise.all([
    api<DashboardResult>(`/reports/dashboard?month=${monthValue()}`),
    api<ActionCenterResult>("/reports/action-center"),
  ]);
  if (!result.ok)
    return card(
      t("dashboard"),
      `<p class="text-error">${esc(result.error ?? t("requestFailed"))}</p>`,
    );
  const stats = result.stats;
  const completed = result.sessions.filter(
    (session) => session.status === "COMPLETED" && !session.autoClosed,
  );
  const laborCosts = completed.reduce(
    (costs, session) => {
      costs[(session.payEmploymentType ?? session.employmentType) === "Part-time" ? "partTime" : "fullTime"] += session.ot;
      return costs;
    },
    { fullTime: 0, partTime: 0 },
  );
  const kpis: Array<[string, string]> = [
    [t("verenaSales"), money(stats.verenaSales)],
    [t("affiliateSales"), money(stats.affiliateSales)],
    [t("commission"), money(stats.commission)],
    [t("totalRevenue"), money(stats.totalRevenue)],
    [t("fullTimeLaborCost"), money(laborCosts.fullTime)],
    [t("partTimeLaborCost"), money(laborCosts.partTime)],
    [t("totalOt"), money(stats.ot)],
    ["ROI", `${stats.roi.toFixed(2)}×`],
    [t("totalHours"), stats.hours.toFixed(2)],
    [t("sessions"), String(stats.sessionCount)],
  ];
  const topStaff = result.staffStats[0];
  const topPlatform = result.platformStats[0];
  const advanced: Array<[string, string]> = [
    [t("revenuePerHour"), money(stats.revenuePerHour)],
    [t("averageRevenue"), money(stats.avgSessionRev)],
    [t("averageDuration"), duration(Math.round(stats.avgSessionDur))],
    [
      t("topRoiStreamer"),
      topStaff ? `${topStaff.name} · ${topStaff.roi.toFixed(2)}×` : "—",
    ],
    [
      t("highestRevenueHour"),
      topStaff ? `${topStaff.name} · ${money(topStaff.revPerHour)}` : "—",
    ],
    [
      t("bestPlatformRoi"),
      topPlatform
        ? `${topPlatform.platform} · ${topPlatform.roi.toFixed(2)}×`
        : "—",
    ],
  ];
  const platformRows = result.platformStats
    .map(
      (row, index) =>
        `<tr><td>${index + 1}</td><td>${esc(row.platform)}</td><td>${row.roi.toFixed(2)}×</td><td>${row.sessions}</td><td>${money(row.revPerHour)}</td></tr>`,
    )
    .join("");
  const staffRows = result.staffStats
    .map(
      (row, index) =>
        `<tr><td>${index + 1}</td><td>${esc(row.name)}</td><td>${row.roi.toFixed(2)}×</td><td>${money(row.revPerHour)}</td><td>${row.issueRate}%</td></tr>`,
    )
    .join("");
  const daily = new Map<
    string,
    { revenue: number; commission: number; ot: number }
  >();
  for (const session of completed) {
    const item = daily.get(session.date) ?? {
      revenue: 0,
      commission: 0,
      ot: 0,
    };
    item.revenue += session.verenaSales;
    item.commission += session.commission;
    item.ot += session.ot;
    daily.set(session.date, item);
  }
  const dailyRows = [...daily.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, item]) => ({
      label: date.slice(8),
      value: item.revenue,
      commission: item.commission,
      roi: item.ot ? item.revenue / item.ot : 0,
      detail: `${money(item.revenue)} · ${(item.ot ? item.revenue / item.ot : 0).toFixed(2)}×`,
    }));
  const streamerRows = result.staffStats.map((row) => ({
    label: row.name,
    value: row.revenue,
    detail: money(row.revenue),
  }));
  const slotRows = [
    {
      label: t("before2200"),
      value: result.slotStats.early.roi,
      detail: `${result.slotStats.early.roi.toFixed(2)}×`,
    },
    {
      label: t("after2200"),
      value: result.slotStats.late.roi,
      detail: `${result.slotStats.late.roi.toFixed(2)}×`,
    },
  ];
  const best = [...completed].sort((a, b) => b.roi - a.roi)[0];
  const issueSessions = completed
    .filter((session) => Boolean(session.startNote || session.endNote));
  const actionCards = action.ok
    ? `<div class="grid gap-3 md:grid-cols-2 xl:grid-cols-5"><div class="rounded-box border border-warning/30 bg-warning/10 p-3"><strong>${t("liveNow")}</strong><p class="mt-1 text-lg font-semibold">${action.active.length} ${t("sessions")}</p><p class="text-sm">${action.active.map((row) => `${esc(row.platformIcon ?? "")} ${esc(row.staffName)}`).join(", ") || "—"}</p></div><div class="rounded-box border border-error/30 bg-error/10 p-3"><strong>${t("belowTarget7d")}</strong><p class="mt-1 text-lg font-semibold">${action.belowTarget.length} ${t("sessions")}</p><p class="text-sm">${
        action.belowTarget
          .slice(0, 3)
          .map((row) => `${esc(row.staffName)} ${row.roi.toFixed(2)}×`)
          .join(", ") || "—"
      }</p></div><div class="rounded-box border border-warning/30 bg-warning/10 p-3"><strong>${t("repeatedIssues7d")}</strong><p class="mt-1 text-lg font-semibold">${action.repeatedIssue.reduce((sum, row) => sum + row.issues, 0)} ${t("issues")}</p><p class="text-sm">${
        action.repeatedIssue
          .slice(0, 3)
          .map((row) => `${esc(row.name)} ${row.issues}`)
          .join(", ") || "—"
      }</p></div><div class="rounded-box border border-success/30 bg-success/10 p-3"><strong>${t("bestPlatformWeek")}</strong><p class="mt-1 text-lg font-semibold">${esc(action.bestPlatform?.platform ?? "—")}</p><p class="text-sm">${action.bestPlatform ? `${action.bestPlatform.roi.toFixed(2)}× · ${action.bestPlatform.sessions} ${t("sessions")}` : "—"}</p></div><div class="rounded-box border border-secondary/30 bg-secondary/10 p-3"><strong>${t("topStreamerToday")}</strong><p class="mt-1 text-lg font-semibold">${esc(action.topStreamerToday?.name ?? "—")}</p><p class="text-sm">${action.topStreamerToday ? `${money(action.topStreamerToday.revenue)} · ${action.topStreamerToday.sessions} ${t("sessions")}` : "—"}</p></div></div>`
    : "";
  return `${card(t("actionCenter"), actionCards || `<p class="text-base-content/60">${t("noData")}</p>`)}<div class="grid gap-4 md:grid-cols-4 mt-5">${kpis.map(([label, value]) => `<section class="stat bg-base-100 shadow rounded-box"><div class="stat-title">${esc(label)}</div><div class="stat-value text-primary text-2xl">${esc(value)}</div></section>`).join("")}</div><div class="grid gap-4 mt-5 md:grid-cols-3">${advanced.map(([label, value]) => `<section class="stat bg-base-100 shadow rounded-box"><div class="stat-title">${esc(label)}</div><div class="stat-value text-secondary text-xl">${esc(value)}</div></section>`).join("")}</div><div class="grid gap-4 mt-5 lg:grid-cols-2">${card(t("streamerEfficiency"), `<div class="overflow-x-auto"><table class="table table-sm"><thead><tr><th>#</th><th>${t("streamer")}</th><th>ROI</th><th>${t("revenuePerHour")}</th><th>${t("issues")}</th></tr></thead><tbody>${staffRows || `<tr><td colspan="5">${t("noData")}</td></tr>`}</tbody></table></div>`)}${card(t("platformEfficiency"), `<div class="overflow-x-auto"><table class="table table-sm"><thead><tr><th>#</th><th>${t("platform")}</th><th>ROI</th><th>${t("sessions")}</th><th>${t("revenuePerHour")}</th></tr></thead><tbody>${platformRows || `<tr><td colspan="5">${t("noData")}</td></tr>`}</tbody></table></div>`)}</div><div class="mt-5">${card(t("dailySalesRoi"), dashboardBars(dailyRows, "#3b82f6"))}</div><div class="grid gap-4 mt-5 lg:grid-cols-2">${card(t("salesByStreamer"), dashboardBars(streamerRows, "#8b5cf6"))}${card(t("roiBySlot"), dashboardBars(slotRows, "#06b6d4"))}</div><div class="grid gap-4 mt-5 lg:grid-cols-2">${card(t("bestLive"), best ? `<p><strong>${esc(best.date)} · ${esc(best.staffName)}</strong> — ${money(best.totalRevenue)} · ROI ${best.roi.toFixed(2)}×</p>` : `<p class="text-base-content/60">${t("noData")}</p>`)}${card(`${t("issueReports")} (${issueSessions.length})`, issueSessions.length ? `<ul class="space-y-2">${issueSessions.map((session) => `<li class="border-b border-base-200 pb-2"><strong>${esc(session.date)} ${esc(session.staffName)}</strong>: ${esc(session.endNote || session.startNote)}</li>`).join("")}</ul>` : `<p class="text-base-content/60">${t("noData")}</p>`)}</div>${card(t("history"), sessionTable(result.sessions))}`;
}
async function history(mine: boolean): Promise<string> {
  const historyQuery = new URLSearchParams({ month: monthValue(), ...Object.fromEntries(Object.entries(historyFilters).filter(([, value]) => Boolean(value))) });
  if (mine) historyQuery.delete('staffId');
  const [result, products, settingsResult] = await Promise.all([
    api<SessionsResult>(
      mine
        ? `/sessions/mine?${historyQuery}`
        : `/sessions?${historyQuery}`,
    ),
    productsWithOfflineCache(false),
    mine ? Promise.resolve<SettingsResult | null>(null) : api<SettingsResult>("/settings"),
  ]);
  if (!result.ok)
    return card(
      mine ? t("myHistory") : t("history"),
      `<p class="text-error">${esc(result.error ?? t("requestFailed"))}</p>`,
    );
  const productList = products.ok ? products.products : [];
  const filteredSessions = result.sessions.filter((session) =>
    (!historyFilters.dateFrom || session.date >= historyFilters.dateFrom) &&
    (!historyFilters.dateTo || session.date <= historyFilters.dateTo) &&
    (!historyFilters.staffId || session.staffId === historyFilters.staffId) &&
    (!historyFilters.platformId || session.platformId === historyFilters.platformId) &&
    (!historyFilters.employmentType || session.employmentType === historyFilters.employmentType) &&
    (!historyFilters.status || session.status === historyFilters.status),
  );
  historyPdfSessions = filteredSessions;
  historyPdfProducts = productList;
  if (!mine) {
    if (settingsResult?.ok) {
      const value=(key:string,fallback:number):number=>{const number=Number(settingsResult.settings[key]);return Number.isFinite(number)?number:fallback;};
      historyPdfSettings = {
        partTimeRate: value("part_time_rate",100),
        fullTimeRate: value("full_time_rate",50),
        commRate: value("comm_rate",5),
        roiGood: value("roi_good",4),
        roiOk: value("roi_ok",2),
      };
    }
  } else if (result.pdfSettings) {
    historyPdfSettings = result.pdfSettings;
  }
  return `${card(
    mine ? t("myHistory") : t("history"),
    `${historyFilterForm(result.sessions)}${historyTotalsCard(filteredSessions)}<div class="mb-3 mt-4 flex items-center justify-between gap-3"><span class="text-sm text-base-content/60">${filteredSessions.length.toLocaleString()} ${t("sessions")}</span><button type="button" class="btn btn-primary btn-sm" data-action="open-history-pdf">${t("exportPdf")}</button></div>${historyTable(filteredSessions, productList, mine)}`,
  )}${historyPdfDialog()}`;
}
async function managerReport(): Promise<string> {
  const query = new URLSearchParams(
    Object.entries(managerFilters).filter(([, value]) => value),
  );
  const [usersResult, platformsResult, report] = await Promise.all([
    api<UsersResult>("/users"),
    api<PlatformsResult>("/platforms"),
    api<ManagerResult>(`/reports/manager?${query}`),
  ]);
  if (!report.ok)
    return card(
      t("managerReport"),
      `<p class="text-error">${esc(report.error ?? t("requestFailed"))}</p>`,
    );
  managerReportSnapshot = report;
  managerReportUsers = usersResult.ok ? usersResult.users : [];
  managerReportPlatforms = platformsResult.ok
    ? platformsResult.platforms.filter((platform) => platform.active)
    : [];
  const userOptions = usersResult.ok
    ? usersResult.users
        .filter((user) => user.active)
        .map(
          (user) =>
            `<option value="${user.id}" ${managerFilters.staffId === user.id ? "selected" : ""}>${esc(user.name)}</option>`,
        )
        .join("")
    : "";
  const platformOptions = platformsResult.ok
    ? platformsResult.platforms
        .filter((platform) => platform.active)
        .map(
          (platform) =>
            `<option value="${platform.id}" ${managerFilters.platformId === platform.id ? "selected" : ""}>${esc(platform.name)}</option>`,
        )
        .join("")
    : "";
  const selectedYear = Number(managerFilters.dateFrom.slice(0, 4));
  const selectedMonth = Number(managerFilters.dateFrom.slice(5, 7));
  const currentYear = Number(new Intl.DateTimeFormat("en", { timeZone: "Asia/Bangkok", year: "numeric" }).format(new Date()));
  const years = Array.from({ length: 4 }, (_, index) => currentYear - 3 + index)
    .map((year) => `<option value="${year}" ${year === selectedYear ? "selected" : ""}>${currentLanguage() === "th" ? year + 543 : year}</option>`)
    .join("");
  const months = Array.from({ length: 12 }, (_, index) => index + 1)
    .map((month) => `<option value="${month}" ${month === selectedMonth ? "selected" : ""}>${esc(new Intl.DateTimeFormat(currentLanguage() === "th" ? "th-TH" : "en-US", { month: "long" }).format(new Date(2020, month - 1, 1)))}</option>`)
    .join("");
  const streamerName = managerFilters.staffId
    ? managerReportUsers.find((user) => user.id === managerFilters.staffId)?.name ?? "—"
    : t("allStreamers");
  const platformName = managerFilters.platformId
    ? managerReportPlatforms.find((platform) => platform.id === managerFilters.platformId)?.name ?? "—"
    : t("allPlatforms");
  const employment = managerFilters.employmentType ? ` · ${managerFilters.employmentType}` : "";
  const filters = `<form id="manager-report-form" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-8"><label class="form-control"><span class="label-text">${t("year")}</span><select id="manager-year" class="select select-bordered">${years}</select></label><label class="form-control"><span class="label-text">${t("month")}</span><select id="manager-month" class="select select-bordered">${months}</select></label><label class="form-control"><span class="label-text">${t("from")}</span><input name="dateFrom" type="date" value="${managerFilters.dateFrom}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("to")}</span><input name="dateTo" type="date" value="${managerFilters.dateTo}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("streamer")}</span><select name="staffId" class="select select-bordered"><option value="">${t("allStreamers")}</option>${userOptions}</select></label><label class="form-control"><span class="label-text">${t("platform")}</span><select name="platformId" class="select select-bordered"><option value="">${t("allPlatforms")}</option>${platformOptions}</select></label><label class="form-control"><span class="label-text">${t("employment")}</span><select name="employmentType" class="select select-bordered"><option value="">${t("allEmployment")}</option><option value="Full-time" ${managerFilters.employmentType === "Full-time" ? "selected" : ""}>${t("fullTime")}</option><option value="Part-time" ${managerFilters.employmentType === "Part-time" ? "selected" : ""}>${t("partTime")}</option></select></label><button type="submit" class="btn btn-primary self-end">🔍 ${t("generateReport")}</button></form>`;
  const heading = `<section class="rounded-box border border-primary/30 bg-primary/10 p-4"><p class="text-sm text-base-content/60">${esc(formatManagerDate(managerFilters.dateFrom, currentLanguage()))} - ${esc(formatManagerDate(managerFilters.dateTo, currentLanguage()))}</p><h2 class="mt-1 text-xl font-bold text-primary">${t("reportOf")} ${esc(streamerName)} · ${esc(platformName)}${esc(employment)}</h2></section>`;
  const toolbar = `<div class="mb-4 flex flex-wrap items-start justify-between gap-3"><div><h1 class="text-3xl font-bold">${t("managerReport")}</h1><p class="text-base-content/60">${report.sessions.length.toLocaleString()} ${t("sessions")}</p></div><div class="flex gap-2"><button type="button" class="btn btn-outline" data-action="refresh-manager-report">↻ ${t("refresh")}</button><button type="button" class="btn btn-primary" data-action="open-manager-export">${t("exportReport")}</button></div></div>`;
  return `${toolbar}${card(t("reportPeriod"), filters)}<div class="mt-4">${heading}</div><div class="mt-4">${managerReportTable(report.sessions, report.totals, currentLanguage(), false)}</div>${managerPerformance(report.sessions.length, report.totals, currentLanguage())}${managerExportDialog()}`;
}

function formatManagerDate(value: string, language: Language): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat(language === "th" ? "th-TH" : "en-GB", { day: "numeric", month: "numeric", year: "numeric" }).format(new Date(Date.UTC(year, month - 1, day)));
}

function mobileManagerCards(sessions: ReportSession[], language: Language): string {
  const label = (key: keyof typeof import("./i18n/th").th): string => tFor(language, key);
  const cards = sessions.map((session) => {
    const validation = validationStatus(session, language);
    const action = validationAction(session, language);
    return `<article class="mobile-session-card"><header class="mobile-session-header"><div><p class="mobile-session-date">${esc(formatManagerDate(session.date, language))}</p>${autoEndBadge(session, language)}<h3>${esc(session.platformName)}</h3></div><div class="mobile-session-badges">${validation}</div></header><p class="mobile-session-streamer">${label("streamer")}: <strong>${esc(session.staffName)}</strong>${session.substituteForName ? `<small>${label("managerSubstituteFor")}: ${esc(session.substituteForName)}</small>` : ""}</p><div class="mobile-session-metrics">${mobileMetric(label("time"), sessionTimeRange(session, language))}${mobileMetric(label("duration"), `${Math.floor(session.durationMins / 60)}:${String(session.durationMins % 60).padStart(2, "0")}`)}${mobileMetric(label("audience"), pdfNumber(session.audience, language))}${mobileMetric(label("totalRevenue"), pdfMoney(session.totalRevenue, language))}${mobileMetric("ROI", `${session.roi.toFixed(2)}×`)}</div>${action ? `<div class="mobile-session-actions">${action}</div>` : ""}${validationMeta(session, language)}${historyEditMeta(session, language)}<details class="mobile-session-details"><summary>${label("viewDetails")}</summary><div class="mobile-session-detail-grid">${mobileMetric("Verena", pdfMoney(session.verenaSales, language))}${mobileMetric("Affiliate", pdfMoney(session.affiliateSales, language))}${mobileMetric(label("commission"), pdfMoney(session.commission, language))}${mobileMetric(label("laborCost"), pdfMoney(session.ot, language))}<div class="mobile-session-detail-wide">${label("orderNumbers")}: <strong>${session.orderNumbers ? esc(session.orderNumbers) : "—"}</strong></div><div class="mobile-session-detail-wide">${label("managerStartFace")}: ${imageCell([session.startFaceImage], label("managerStartFace"))}</div><div class="mobile-session-detail-wide">${label("managerEndFace")}: ${imageCell([session.endFaceImage], label("managerEndFace"))}</div></div></details></article>`;
  }).join("");
  return `<div class="mobile-session-list">${cards || `<p class="rounded-box border border-base-300 p-4 text-base-content/60">${label("noData")}</p>`}</div>`;
}

function mobileManagerTotals(totals: ReportTotals, language: Language): string {
  const label = (key: keyof typeof import("./i18n/th").th): string => tFor(language, key);
  return `<section class="mobile-report-totals"><h2>${label("total")}</h2><div class="mobile-session-detail-grid">${mobileMetric(label("duration"), `${Math.floor(totals.durationMins / 60)}:${String(totals.durationMins % 60).padStart(2, "0")}`)}${mobileMetric(label("audience"), pdfNumber(totals.audience, language))}${mobileMetric("Verena", pdfMoney(totals.verenaSales, language))}${mobileMetric("Affiliate", pdfMoney(totals.affiliateSales, language))}${mobileMetric(label("commission"), pdfMoney(totals.commission, language))}${mobileMetric(label("laborCost"), pdfMoney(totals.ot, language))}${mobileMetric(label("totalRevenue"), pdfMoney(totals.totalRevenue, language))}${mobileMetric("ROI", `${totals.roi.toFixed(2)}×`)}</div></section>`;
}

function managerReportTable(sessions: ReportSession[], totals: ReportTotals, language: Language, printable: boolean): string {
  const label = (key: keyof typeof import("./i18n/th").th): string => tFor(language, key);
  const rows = sessions.map((session) => {
    const start = pdfTime(session.startTime, language);
    const end = pdfTime(session.endTime, language);
    const validation = printable
      ? (session.validated ? label("validated") : "—")
      : `<div class="manager-validation">${validationSummary(session, language)}${historyEditMeta(session, language)}${validationAction(session, language)}</div>`;
    return `<tr><td class="manager-sticky manager-col-date">${esc(formatManagerDate(session.date, language))}${autoEndBadge(session, language) ? `<br>${autoEndBadge(session, language)}` : ""}</td><td class="manager-sticky manager-col-platform"><span class="manager-platform">${esc(session.platformName)}</span></td><td class="manager-sticky manager-col-streamer">${esc(session.staffName)}${session.substituteForName ? `<small class="manager-substitute">${esc(label("managerSubstituteFor"))}: ${esc(session.substituteForName)}</small>` : ""}</td><td class="whitespace-nowrap">${start} - ${end}</td><td>${Math.floor(session.durationMins / 60)}:${String(session.durationMins % 60).padStart(2, "0")}</td><td>${pdfNumber(session.audience, language)}</td><td>${pdfMoney(session.verenaSales, language)}</td><td>${pdfMoney(session.affiliateSales, language)}</td><td>${pdfMoney(session.commission, language)}</td><td>${pdfMoney(session.ot, language)}</td><td><strong>${pdfMoney(session.totalRevenue, language)}</strong></td><td>${session.roi.toFixed(2)}×</td><td class="max-w-40 whitespace-normal">${session.orderNumbers ? esc(session.orderNumbers) : "—"}</td><td>${imageCell([session.startFaceImage], label("managerStartFace"))}</td><td>${imageCell([session.endFaceImage], label("managerEndFace"))}</td>${printable ? "" : `<td class="manager-sticky manager-col-validation manager-validation-cell">${validation}</td>`}</tr>`;
  }).join("");
  const validationHeader = printable ? "" : label("validation");
  const totalRow = `<tr class="font-bold"><td class="manager-sticky manager-col-date">${label("total")}</td><td class="manager-sticky manager-col-platform"></td><td class="manager-sticky manager-col-streamer"></td><td></td><td>${Math.floor(totals.durationMins / 60)}:${String(totals.durationMins % 60).padStart(2, "0")}</td><td>${pdfNumber(totals.audience, language)}</td><td>${pdfMoney(totals.verenaSales, language)}</td><td>${pdfMoney(totals.affiliateSales, language)}</td><td>${pdfMoney(totals.commission, language)}</td><td>${pdfMoney(totals.ot, language)}</td><td>${pdfMoney(totals.totalRevenue, language)}</td><td>${totals.roi.toFixed(2)}×</td><td></td><td></td><td></td>${printable ? "" : "<td class=\"manager-sticky manager-col-validation\"></td>"}</tr>`;
  const table = `<div class="manager-report-table overflow-x-auto rounded-box border border-base-300 bg-base-100"><table class="manager-report-data table table-sm"><thead><tr><th class="manager-sticky manager-col-date">${label("date")}</th><th class="manager-sticky manager-col-platform">${label("platform")}</th><th class="manager-sticky manager-col-streamer">${label("streamer")}</th><th>${label("time")}</th><th>${label("duration")}</th><th>${label("audience")}</th><th>Verena (฿)</th><th>Affiliate (฿)</th><th>${label("commission")} (฿)</th><th>${label("laborCost")} (฿)</th><th>${label("totalRevenue")} (฿)</th><th>ROI</th><th>${label("orderNumbers")}</th><th>${label("managerStartFace")}</th><th>${label("managerEndFace")}</th>${printable ? "" : `<th class="manager-sticky manager-col-validation">${validationHeader}</th>`}</tr></thead><tbody>${rows || `<tr><td colspan="${printable ? 15 : 16}">${label("noData")}</td></tr>`}</tbody>${sessions.length ? `<tfoot>${totalRow}</tfoot>` : ""}</table></div>`;
  return printable ? table : `<div class="desktop-session-data">${table}</div><div class="mobile-session-data">${mobileManagerCards(sessions, language)}${mobileManagerTotals(totals, language)}</div>`;
}

function managerPerformance(sessionCount: number, totals: ReportTotals, language: Language): string {
  const stat = (title: string, value: string): string => `<div class="stat rounded-box bg-base-100 shadow"><div class="stat-title">${esc(title)}</div><div class="stat-value text-xl text-primary">${esc(value)}</div></div>`;
  return `<section class="mt-4"><h2 class="mb-3 text-xl font-bold">${esc(tFor(language, "performanceSummary"))}</h2><div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">${stat(tFor(language, "revenuePerHour"), `฿${pdfMoney(totals.revenuePerHour, language)}`)}${stat(tFor(language, "audiencePerHour"), pdfNumber(totals.audiencePerHour, language))}${stat(tFor(language, "totalHours"), totals.hours.toFixed(2))}${stat(tFor(language, "sessions"), pdfNumber(sessionCount, language))}</div></section>`;
}

function managerExportDialog(): string {
  return `<dialog id="manager-export-dialog" class="modal"><div class="modal-box"><form method="dialog"><button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" aria-label="${t("cancel")}">✕</button></form><h3 class="text-lg font-bold">${t("chooseReportLanguage")}</h3><p class="mt-3 text-sm text-base-content/70">${t("reportLanguageHint")}</p><div class="mt-5 grid grid-cols-2 gap-3"><button type="button" class="btn btn-primary" data-action="export-manager-html" data-id="th">${t("thaiPdf")}</button><button type="button" class="btn" data-action="export-manager-html" data-id="en">${t("englishPdf")}</button></div></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}

function managerExportPage(language: Language): string {
  if (!managerReportSnapshot) return `<p>${esc(tFor(language, "noData"))}</p>`;
  const streamerName = managerFilters.staffId ? managerReportUsers.find((user) => user.id === managerFilters.staffId)?.name ?? "—" : tFor(language, "allStreamers");
  const platformName = managerFilters.platformId ? managerReportPlatforms.find((platform) => platform.id === managerFilters.platformId)?.name ?? "—" : tFor(language, "allPlatforms");
  return `<main class="manager-export min-h-screen bg-white p-4 text-neutral sm:p-8"><div class="no-print mb-5 flex flex-wrap items-center justify-between gap-3 rounded-box border border-base-300 bg-base-100 p-4 shadow"><div><strong>${esc(tFor(language, "printOrSavePdf"))}</strong><p class="text-sm text-base-content/60">${esc(tFor(language, "printHelp"))}</p></div><div class="flex gap-2"><button type="button" class="btn" data-action="back-manager-report">← ${esc(tFor(language, "backToReport"))}</button><button type="button" class="btn btn-primary" data-action="print-manager-report">${esc(tFor(language, "printOrSavePdf"))}</button></div></div><header class="mb-5"><h1 class="text-2xl font-bold">${esc(tFor(language, "managerReport"))} - Verena International</h1><p>${esc(formatManagerDate(managerFilters.dateFrom, language))} - ${esc(formatManagerDate(managerFilters.dateTo, language))}</p><p>${esc(tFor(language, "streamer"))}: ${esc(streamerName)} · ${esc(tFor(language, "platform"))}: ${esc(platformName)}${managerFilters.employmentType ? ` · ${esc(managerFilters.employmentType)}` : ""}</p></header>${managerReportTable(managerReportSnapshot.sessions, managerReportSnapshot.totals, language, true)}${managerPerformance(managerReportSnapshot.sessions.length, managerReportSnapshot.totals, language)}</main>`;
}

function scheduleRows(schedules: Schedule[], admin: boolean): string {
  return schedules.map((schedule) => `<tr class="${schedule.locked ? "opacity-60" : ""}"><td>${esc(schedule.date)}</td><td>${esc(schedule.staffName)}</td><td>${esc(schedule.platformIcon ?? "")} ${esc(schedule.platformName)}</td><td>${esc(schedule.startTime)}–${esc(schedule.endTime)}${schedule.endsNextDay ? ` <span class="badge badge-outline badge-xs">${t("nextDayShort")}</span>` : ""}</td><td>${schedule.hours}</td><td>${esc(schedule.note || "—")}</td><td>${schedule.locked ? `🔒 ${t("pastSchedule")}` : admin ? `<button type="button" class="btn btn-xs" data-action="open-schedule-dialog" data-id="schedule-edit-${schedule.scheduleId}">✏️ ${t("edit")}</button> ${btn("cancel-schedule", t("cancelSchedule"), schedule.scheduleId)}` : ""}</td></tr>`).join("");
}
function scheduleTable(schedules: Schedule[], admin: boolean): string {
  return `<div class="overflow-x-auto rounded-box border border-base-300"><table class="table table-sm"><thead><tr><th>${t("date")}</th><th>${t("streamer")}</th><th>${t("platform")}</th><th>${t("time")}</th><th>${t("hours")}</th><th>${t("note")}</th><th>${t("actions")}</th></tr></thead><tbody>${scheduleRows(schedules, admin) || `<tr><td colspan="7">${t("noSchedules")}</td></tr>`}</tbody></table></div>`;
}
function scheduleYearOptions(month: string): string {
  const selected = Number(month.slice(0, 4));
  const current = Number(bangkokMonth.slice(0, 4));
  return [current - 1, current, current + 1].map((year) => `<option value="${year}" ${year === selected ? "selected" : ""}>${currentLanguage() === "th" ? year + 543 : year}</option>`).join("");
}
function scheduleMonthOptions(month: string): string {
  const selected = Number(month.slice(5, 7));
  return Array.from({ length: 12 }, (_, index) => index + 1).map((value) => `<option value="${value}" ${value === selected ? "selected" : ""}>${esc(new Intl.DateTimeFormat(currentLanguage() === "th" ? "th-TH" : "en-US", { month: "long" }).format(new Date(2020, value - 1, 1)))}</option>`).join("");
}
function scheduleSelectOptions<T extends { id: string; name: string }>(items: T[], selected: string, allLabel?: string): string {
  return `${allLabel ? `<option value="">${allLabel}</option>` : `<option value="">${t("streamer")}</option>`}${items.map((item) => `<option value="${item.id}" ${item.id === selected ? "selected" : ""}>${esc(item.name)}</option>`).join("")}`;
}
function scheduleHours(startTime: string, endTime: string, endsNextDay = false): number {
  const minutes = (value: string): number => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));
  return Math.round(((minutes(endTime) - minutes(startTime) + (endsNextDay ? 1440 : 0)) / 60) * 100) / 100;
}
function builderTimeInput(dateValue: string, index: number, field: "startTime" | "endTime", value: string): string {
  const open = scheduleTimePicker?.date === dateValue && scheduleTimePicker.index === index && scheduleTimePicker.field === field;
  return `<div class="relative">${`<button type="button" class="input input-bordered input-sm w-full text-left" data-action="open-builder-time-picker" data-id="${dateValue}|${index}|${field}">${esc(value)} 🕘</button>`}${open ? `<div class="absolute left-0 top-full z-30 pt-2">${builderTimePicker()}</div>` : ""}</div>`;
}
function builderTimePicker(): string {
  if (!scheduleTimePicker) return "";
  const selected = scheduleTimePicker;
  const options = (count: number, value: string) => Array.from({ length: count }, (_, item) => String(item).padStart(2, "0")).map((item) => `<option value="${item}" ${item === value ? "selected" : ""}>${item}</option>`).join("");
  return `<div data-builder-time-picker tabindex="0" class="flex w-max max-w-[calc(100vw-2rem)] items-end gap-2 rounded-box border border-primary/30 bg-base-100 p-2 shadow-lg"><label class="form-control"><span class="label-text text-xs">${t("hour")}</span><select size="5" class="select select-bordered select-sm h-28 w-20" data-builder-time-select="hour">${options(24,selected.hour)}</select></label><label class="form-control"><span class="label-text text-xs">${t("minute")}</span><select size="5" class="select select-bordered select-sm h-28 w-20" data-builder-time-select="minute">${options(60,selected.minute)}</select></label><button type="button" class="btn btn-sm btn-primary" data-action="apply-builder-time-picker">${t("confirm")}</button><span class="sr-only">${t("timePickerHint")}</span></div>`;
}
function applyBuilderTimePicker(): void {
  if (!scheduleTimePicker) return;
  const slot = scheduleBuilder?.days[scheduleTimePicker.date]?.[scheduleTimePicker.index];
  if (slot) slot[scheduleTimePicker.field] = `${scheduleTimePicker.hour}:${scheduleTimePicker.minute}`;
  scheduleTimePicker = null;
  scheduleWarningsConfirmed = false;
  void renderScheduleBuilderAtScroll();
}
async function renderScheduleBuilderAtScroll(): Promise<void> {
  const top = window.scrollY;
  await render();
  window.requestAnimationFrame(() => window.scrollTo({ top, behavior: "auto" }));
}
interface ScheduleIssue { severity: "error" | "warning"; message: string; }
function validateScheduleBuilder(): Map<string, ScheduleIssue[]> {
  const issues = new Map<string, ScheduleIssue[]>();
  if (!scheduleBuilder) return issues;
  const add = (key: string, issue: ScheduleIssue): void => {
    issues.set(key, [...(issues.get(key) ?? []), issue]);
  };
  const minute = (value: string): number => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));
  const overlap = (a: BuilderSlot, b: Pick<Schedule, "startTime" | "endTime"> | BuilderSlot): boolean => a.startTime < b.endTime && a.endTime > b.startTime;
  const editableIds = new Set(Object.values(scheduleBuilder.days).flat().filter((slot) => !slot.locked && slot.scheduleId).map((slot) => slot.scheduleId));
  for (const [dateValue, daySlots] of Object.entries(scheduleBuilder.days)) {
    const editable = daySlots.map((slot, index) => ({ slot, index })).filter(({ slot }) => !slot.locked);
    const dailyHours = editable.reduce((sum, { slot }) => sum + scheduleHours(slot.startTime, slot.endTime), 0);
    for (const { slot, index } of editable) {
      const key = `${dateValue}|${index}`;
      const platformName = savedPlatforms.find((platform) => platform.id === slot.platformId)?.name ?? t("platform");
      if (slot.endsNextDay ? slot.endTime >= slot.startTime : slot.endTime <= slot.startTime) add(key, { severity:"error", message:currentLanguage() === "th" ? "เวลาข้ามวันต้องกด +1 วัน และเวลาจบต้องอยู่ก่อนเวลาเริ่ม" : "For an overnight slot, select +1 day and an end time before the start time." });
      for (const other of editable) {
        if (other.index >= index || !overlap(slot, other.slot)) continue;
        add(key, { severity:"error", message:currentLanguage() === "th" ? `สตรีมเมอร์มีรอบ ${other.slot.startTime}–${other.slot.endTime} ซ้อนกัน กรุณาเลือกเวลาก่อน ${other.slot.startTime} หรือหลัง ${other.slot.endTime}` : `Streamer already has ${other.slot.startTime}–${other.slot.endTime}. Choose a time before ${other.slot.startTime} or after ${other.slot.endTime}.` });
        add(`${dateValue}|${other.index}`, { severity:"error", message:currentLanguage() === "th" ? `สตรีมเมอร์มีรอบ ${slot.startTime}–${slot.endTime} ซ้อนกัน` : `Streamer has an overlapping ${slot.startTime}–${slot.endTime} slot.` });
      }
      for (const existing of scheduleReferenceRows.filter((row) => row.date === dateValue && !editableIds.has(row.scheduleId) && overlap(slot, row))) {
        if (existing.staffId === scheduleBuilder.staffId) add(key, { severity:"error", message:currentLanguage() === "th" ? `สตรีมเมอร์มีไลฟ์ ${existing.startTime}–${existing.endTime} อยู่แล้ว กรุณาเลือกเวลาก่อน ${existing.startTime} หรือหลัง ${existing.endTime}` : `Streamer already has ${existing.startTime}–${existing.endTime}. Choose a time before ${existing.startTime} or after ${existing.endTime}.` });
        else if (existing.platformId === slot.platformId) add(key, { severity:"error", message:currentLanguage() === "th" ? `${platformName} ถูกใช้โดย ${existing.staffName} เวลา ${existing.startTime}–${existing.endTime} กรุณาเลือกแพลตฟอร์มอื่นหรือเปลี่ยนเวลา` : `${platformName} is used by ${existing.staffName} at ${existing.startTime}–${existing.endTime}. Choose another platform or time.` });
      }
      const hours = scheduleHours(slot.startTime, slot.endTime, slot.endsNextDay);
      if (hours > 0 && hours < 0.5) add(key, { severity:"warning", message:currentLanguage() === "th" ? `รอบนี้สั้นเพียง ${hours} ชั่วโมง กรุณาตรวจสอบเวลา` : `This slot is only ${hours} hours. Check the times.` });
      if (hours > 6) add(key, { severity:"warning", message:currentLanguage() === "th" ? `รอบนี้ยาว ${hours} ชั่วโมง ซึ่งนานกว่าปกติ` : `This slot is ${hours} hours, longer than usual.` });
      if (dailyHours > 8) add(key, { severity:"warning", message:currentLanguage() === "th" ? `วันนี้มีตารางรวม ${dailyHours} ชั่วโมง กรุณาตรวจสอบภาระงาน` : `This day totals ${dailyHours} hours. Check the workload.` });
      const nearestGap = editable.filter((other) => other.index !== index && !overlap(slot, other.slot)).map((other) => Math.min(Math.abs(minute(slot.startTime)-minute(other.slot.endTime)),Math.abs(minute(other.slot.startTime)-minute(slot.endTime)))).filter((gap) => gap >= 0 && gap < 30)[0];
      if (nearestGap !== undefined) add(key, { severity:"warning", message:currentLanguage() === "th" ? `มีเวลาพักเพียง ${nearestGap} นาที แนะนำให้เว้นอย่างน้อย 30 นาที` : `Only ${nearestGap} minutes between slots. A 30-minute break is recommended.` });
    }
  }
  return issues;
}
function scheduleEntryDialog(schedule: Schedule | null, users: User[], platforms: Platform[]): string {
  const edit = Boolean(schedule);
  const id = edit ? `schedule-edit-${schedule?.scheduleId ?? ""}` : "schedule-add-dialog";
  return `<dialog id="${id}" class="modal"><div class="modal-box"><h3 class="text-lg font-bold">${edit ? t("editSchedule") : t("addSchedule")}</h3><form id="${edit ? "schedule-edit-form" : "schedule-create-form"}" class="mt-4 grid gap-3 md:grid-cols-2"><input type="hidden" name="id" value="${esc(schedule?.scheduleId ?? "")}"><label class="form-control"><span class="label-text">${t("streamer")}</span><select required name="staffId" class="select select-bordered">${scheduleSelectOptions(users, schedule?.staffId ?? "")}</select></label><label class="form-control"><span class="label-text">${t("platform")}</span><select required name="platformId" class="select select-bordered"><option value="">${t("platform")}</option>${platforms.map((platform) => `<option value="${platform.id}" ${platform.id === schedule?.platformId ? "selected" : ""}>${esc(platform.name)}</option>`).join("")}</select></label><label class="form-control"><span class="label-text">${t("date")}</span><input required name="date" type="date" value="${esc(schedule?.date ?? "")}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("note")}</span><input name="note" maxlength="500" value="${esc(schedule?.note ?? "")}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("startTime")}</span><input required name="startTime" type="time" value="${esc(schedule?.startTime ?? "14:00")}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("endTime")}</span><input required name="endTime" type="time" value="${esc(schedule?.endTime ?? "16:00")}" class="input input-bordered"></label><label class="label cursor-pointer justify-start gap-2 md:col-span-2"><input name="endsNextDay" type="checkbox" class="checkbox checkbox-sm" ${schedule?.endsNextDay ? "checked" : ""}><span class="label-text">${t("endsNextDay")}</span></label><div class="modal-action md:col-span-2"><button type="button" class="btn" data-close-dialog>${t("cancel")}</button><button type="submit" class="btn btn-primary">${t("save")}</button></div></form></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}
function scheduleBuilderView(platforms: Platform[]): string {
  if (scheduleBuilderError) return `<p class="rounded-box border border-error/30 bg-error/10 p-4 text-error">${esc(scheduleBuilderError)}</p>`;
  if (!scheduleBuilder) return `<p class="rounded-box border border-dashed border-base-300 p-8 text-center text-base-content/60">${scheduleBuildStaffId ? t("loading") : t("selectStreamerFirst")}</p>`;
  const validation = validateScheduleBuilder();
  const allIssues = [...validation.values()].flat();
  const errorCount = allIssues.filter((issue) => issue.severity === "error").length;
  const warningCount = allIssues.filter((issue) => issue.severity === "warning").length;
  const errorDetails = [...validation.entries()]
    .flatMap(([key, issues]) => issues.filter((issue) => issue.severity === "error").map((issue) => ({ date:key.slice(0, 10), message:issue.message })))
    .slice(0, 3);
  const dayNames = currentLanguage() === "th" ? ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"] : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const cards = Object.entries(scheduleBuilder.days).map(([dateValue, slots]) => {
    const day = Number(dateValue.slice(8, 10));
    const weekday = dayNames[new Date(`${dateValue}T00:00:00Z`).getUTCDay()];
    const isDayOff = slots.length === 0;
    const canClear = slots.some((slot) => !slot.locked);
    const rows = slots.length ? slots.map((slot, index) => {
      if (slot.locked) return `<div class="flex flex-wrap items-center gap-2 py-2 text-sm opacity-60">🔒 <strong>${esc(platforms.find((platform) => platform.id === slot.platformId)?.name ?? "—")}</strong> · ${slot.startTime}–${slot.endTime} · ${slot.note ? esc(slot.note) : "—"}</div>`;
      const slotIssues = validation.get(`${dateValue}|${index}`) ?? [];
      const issueHtml = slotIssues.map((issue) => `<p class="mt-1 text-sm ${issue.severity === "error" ? "text-error" : "text-warning"}">${issue.severity === "error" ? "⛔" : "⚠️"} ${esc(issue.message)}</p>`).join("");
      const nextDayButton = `<button type="button" class="btn btn-sm whitespace-nowrap ${slot.endsNextDay ? "btn-primary" : ""}" data-action="toggle-builder-next-day" data-id="${dateValue}|${index}">${t("endsNextDay")}</button>`;
      return `<div class="py-2"><div class="grid gap-2 sm:grid-cols-[minmax(160px,1fr)_110px_110px_150px_64px_minmax(140px,1fr)_42px]"><select class="select select-bordered select-sm ${slotIssues.some((issue) => issue.severity === "error") ? "select-error" : ""}" data-builder-date="${dateValue}" data-builder-index="${index}" data-builder-field="platformId">${platforms.map((platform) => `<option value="${platform.id}" ${platform.id === slot.platformId ? "selected" : ""}>${esc(platform.name)}</option>`).join("")}</select>${builderTimeInput(dateValue,index,"startTime",slot.startTime)}${builderTimeInput(dateValue,index,"endTime",slot.endTime)}${nextDayButton}<span class="self-center text-xs text-base-content/60" data-builder-duration="${dateValue}|${index}">${scheduleHours(slot.startTime, slot.endTime, slot.endsNextDay)}h</span><input value="${esc(slot.note)}" maxlength="500" placeholder="${t("note")}" class="input input-bordered input-sm" data-builder-date="${dateValue}" data-builder-index="${index}" data-builder-field="note"><button type="button" class="btn btn-sm" data-action="remove-builder-slot" data-id="${dateValue}|${index}">✕</button></div>${issueHtml}</div>`;
    }).join("") : `<p class="py-2 text-sm text-base-content/50">${t("dayOffNoLive")}</p>`;
    return `<section class="card mb-3 border border-base-200 bg-base-100 shadow-sm"><div class="card-body p-4"><div class="flex flex-wrap items-center justify-between gap-2"><div class="flex items-center gap-2"><h3 class="font-bold">${day} · ${weekday}</h3>${isDayOff ? `<span class="badge badge-ghost">${t("dayOffNoLive")}</span>` : ""}</div><div class="flex gap-2"><button type="button" class="btn btn-sm" data-action="add-builder-slot" data-id="${dateValue}">＋ ${t("addSlot")}</button>${canClear ? `<button type="button" class="btn btn-sm" data-action="builder-day-off" data-id="${dateValue}">${t("dayOff")}</button>` : ""}</div></div>${rows}</div></section>`;
  }).join("");
  const summary = errorCount ? `<div class="text-error"><strong>${currentLanguage() === "th" ? `พบ ${errorCount} ข้อผิดพลาด กรุณาแก้ไขก่อนบันทึก` : `${errorCount} errors found. Fix them before saving.`}</strong><ul class="mt-1 list-inside list-disc text-sm">${errorDetails.map((issue) => `<li>${esc(issue.date)}: ${esc(issue.message)}</li>`).join("")}</ul></div>` : warningCount ? `<strong class="text-warning">${currentLanguage() === "th" ? `พบ ${warningCount} คำเตือน กรุณาตรวจสอบก่อนบันทึก` : `${warningCount} warnings found. Review before saving.`}</strong>` : `<span class="text-sm text-base-content/60">${t("scheduleBuildHint")}</span>`;
  const saveLabel = warningCount && scheduleWarningsConfirmed ? (currentLanguage() === "th" ? "ยืนยันบันทึกต่อ" : "Confirm save") : t("saveSchedule");
  const saveBar = `<div class="flex flex-wrap items-center justify-between gap-3 rounded-box border border-base-300 bg-base-100 p-4 shadow-lg">${summary}<button type="button" class="btn btn-primary" data-action="save-schedule-builder" ${errorCount ? "disabled" : ""}>💾 ${saveLabel}</button></div>`;
  return `<div class="mb-4">${saveBar}</div>${cards}<div class="sticky bottom-2 z-10">${saveBar}</div>`;
}
async function schedulesPage(): Promise<string> {
  const [usersResult, platformsResult] = await Promise.all([api<UsersResult>("/users"), api<PlatformsResult>("/platforms?activeOnly=true")]);
  if (!usersResult.ok || !platformsResult.ok) return card(t("schedules"), `<p class="text-error">${t("requestFailed")}</p>`);
  const users = usersResult.users.filter((user) => user.active && user.role === "streamer");
  const platforms = platformsResult.platforms.filter((platform) => platform.active);
  savedPlatforms = platforms;
  let planned: Schedule[] = [];
  if (scheduleMode === "planned") {
    const query = new URLSearchParams({ month: schedulePlannedMonth });
    if (schedulePlannedStaffId) query.set("staffId", schedulePlannedStaffId);
    if (schedulePlannedPlatformId) query.set("platformId", schedulePlannedPlatformId);
    const result = await api<SchedulesResult>(`/schedules?${query}`);
    if (!result.ok) return card(t("schedules"), `<p class="text-error">${esc(result.error ?? t("requestFailed"))}</p>`);
    planned = result.schedules;
  } else if (scheduleBuildRequested && scheduleBuildStaffId && !scheduleBuilder) {
    const [result, referenceResult] = await Promise.all([
      api<SchedulesResult>(`/schedules/month?ym=${scheduleBuildMonth}&staffId=${scheduleBuildStaffId}`),
      api<SchedulesResult>(`/schedules?month=${scheduleBuildMonth}`),
    ]);
    if (result.ok && referenceResult.ok) {
      scheduleBuilderError = "";
      scheduleReferenceRows = referenceResult.schedules;
      scheduleWarningsConfirmed = false;
      const [year, month] = scheduleBuildMonth.split("-").map(Number);
      const dayCount = new Date(year, month, 0).getDate();
      const days: Record<string, BuilderSlot[]> = {};
      for (let day = 1; day <= dayCount; day++) days[`${scheduleBuildMonth}-${String(day).padStart(2, "0")}`] = [];
      for (const schedule of result.schedules) days[schedule.date]?.push({ scheduleId:schedule.scheduleId, platformId:schedule.platformId, startTime:schedule.startTime, endTime:schedule.endTime, endsNextDay:schedule.endsNextDay, note:schedule.note, locked:schedule.locked });
      scheduleBuilder = { staffId:scheduleBuildStaffId, month:scheduleBuildMonth, days };
      scheduleBuildRequested = false;
    } else {
      scheduleBuilderError = result.ok ? (referenceResult.error ?? t("requestFailed")) : (result.error ?? t("requestFailed"));
      scheduleBuildRequested = false;
    }
  }
  const tabs = `<div class="mb-4 flex flex-wrap gap-2"><button type="button" class="btn ${scheduleMode === "build" ? "btn-primary" : ""}" data-action="schedule-mode" data-id="build">🗓️ ${t("buildSchedule")}</button><button type="button" class="btn ${scheduleMode === "planned" ? "btn-primary" : ""}" data-action="schedule-mode" data-id="planned">📋 ${t("plannedSchedules")}</button></div>`;
  const heading = `<div class="mb-4"><h1 class="text-3xl font-bold">${t("schedules")}</h1><p class="text-base-content/60">${t("scheduleSubtitle")}</p></div>`;
  if (scheduleMode === "build") {
    const filter = `<form id="schedule-build-filter" class="grid gap-3 sm:grid-cols-4"><label class="form-control"><span class="label-text">${t("year")}</span><select name="year" class="select select-bordered">${scheduleYearOptions(scheduleBuildMonth)}</select></label><label class="form-control"><span class="label-text">${t("month")}</span><select name="month" class="select select-bordered">${scheduleMonthOptions(scheduleBuildMonth)}</select></label><label class="form-control"><span class="label-text">${t("streamer")}</span><select required name="staffId" class="select select-bordered">${scheduleSelectOptions(users, scheduleBuildStaffId)}</select></label><button class="btn btn-primary self-end" type="submit">${t("loadMonth")}</button></form>`;
    return `${heading}${tabs}${card(t("buildSchedule"), filter)}<div class="mt-4">${scheduleBuilderView(platforms)}</div>`;
  }
  const filter = `<form id="schedule-planned-filter" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-6"><label class="form-control"><span class="label-text">${t("year")}</span><select name="year" class="select select-bordered">${scheduleYearOptions(schedulePlannedMonth)}</select></label><label class="form-control"><span class="label-text">${t("month")}</span><select name="month" class="select select-bordered">${scheduleMonthOptions(schedulePlannedMonth)}</select></label><label class="form-control"><span class="label-text">${t("streamer")}</span><select name="staffId" class="select select-bordered">${scheduleSelectOptions(users, schedulePlannedStaffId, t("allStreamers"))}</select></label><label class="form-control"><span class="label-text">${t("platform")}</span><select name="platformId" class="select select-bordered"><option value="">${t("allPlatforms")}</option>${platforms.map((platform) => `<option value="${platform.id}" ${platform.id === schedulePlannedPlatformId ? "selected" : ""}>${esc(platform.name)}</option>`).join("")}</select></label><button class="btn btn-primary self-end" type="submit">🔍 ${t("search")}</button><button class="btn self-end" type="button" data-action="open-schedule-dialog" data-id="schedule-add-dialog">＋ ${t("addEntry")}</button></form>`;
  return `${heading}${tabs}${card(t("plannedSchedules"), filter)}<div class="mt-4">${scheduleTable(planned, true)}</div>${scheduleEntryDialog(null, users, platforms)}${planned.filter((schedule) => !schedule.locked).map((schedule) => scheduleEntryDialog(schedule, users, platforms)).join("")}`;
}
async function mySchedule(): Promise<string> {
  const result = await api<SchedulesResult>("/schedules/mine");
  return card(
    t("upcomingSchedules"),
    result.ok
      ? scheduleTable(result.schedules, false)
      : `<p class="text-error">${esc(result.error ?? t("requestFailed"))}</p>`,
  );
}

function profileAvatar(key:string|null|undefined,size="h-10 w-10",preview=false):string {
  return `<span class="grid ${size} shrink-0 place-items-center overflow-hidden rounded-full bg-base-300 text-base-content/40 shadow-sm" ${key?`data-profile-image="${esc(key)}"`:""} ${preview?"data-profile-preview":""} aria-hidden="true"><svg width="55%" height="55%" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="8" r="4"></circle><path d="M4 21a8 8 0 0 1 16 0"></path></svg></span>`;
}
function profileImageField(userId:string,key:string|null):string {
  const inputId=`profile-image-${userId}`;
  return `<div class="form-control"><span class="label-text mb-2">${t("profileImage")}</span><div class="flex items-center gap-3">${profileAvatar(key,"h-16 w-16",true)}<div class="min-w-0 flex-1"><input id="${esc(inputId)}" data-profile-picker name="profileImage" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" class="sr-only"><label for="${esc(inputId)}" class="btn btn-sm">${t("changeProfileImage")}</label><p class="mt-1 text-xs text-base-content/60">${t("profileImageHint")}</p><p class="mt-1 truncate text-xs" data-profile-file-name></p></div></div></div>`;
}
function platformAvatar(key:string|null|undefined,preview=false):string {
  return `<span class="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl bg-base-200 text-2xl shadow-sm" ${key?`data-profile-image="${esc(key)}"`:""} ${preview?"data-platform-image-preview":""} aria-hidden="true">🏪</span>`;
}
function platformImageField(platformId:string,key:string|null):string {
  const inputId=`platform-image-${platformId}`;
  return `<div class="form-control"><span class="label-text mb-2">${t("shopProfilePhoto")}</span><div class="flex items-center gap-3">${platformAvatar(key,true)}<div class="min-w-0 flex-1"><input id="${esc(inputId)}" data-platform-image-picker name="platformImage" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" class="sr-only"><label for="${esc(inputId)}" class="btn btn-sm">${t("changeProfileImage")}</label><p class="mt-1 text-xs text-base-content/60">${t("shopProfilePhotoHint")}</p><p class="mt-1 truncate text-xs" data-platform-image-file-name></p></div></div></div>`;
}
function platformImageFile(form:HTMLFormElement):File|null {
  const input=form.elements.namedItem("platformImage");
  return input instanceof HTMLInputElement ? input.files?.[0] ?? null : null;
}
function choicePicker(name:string,label:string,options:Array<{value:string;label:string}>,selected:string):string {
  return `<fieldset class="form-control"><legend class="label-text mb-2">${esc(label)}</legend><div class="grid grid-cols-2 gap-2">${options.map((option)=>`<label class="cursor-pointer"><input class="peer sr-only" type="radio" name="${esc(name)}" value="${esc(option.value)}" ${option.value===selected?"checked":""}><span class="btn h-auto min-h-12 w-full border-base-300 bg-base-100 peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-content peer-focus-visible:outline">${esc(option.label)}</span></label>`).join("")}</div></fieldset>`;
}
function userEditDialog(user: User): string {
  return `<dialog id="user-edit-${esc(user.id)}" class="modal"><div class="modal-box"><h3 class="text-lg font-bold">${t("editUser")}</h3><form id="user-edit-form" class="mt-4 grid gap-3"><input type="hidden" name="id" value="${esc(user.id)}">${profileImageField(user.id,user.profileImageKey)}<label class="form-control"><span class="label-text">${t("name")}</span><input required name="name" value="${esc(user.name)}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("email")}</span><input required name="email" type="email" value="${esc(user.email)}" class="input input-bordered"></label>${choicePicker("role",t("role"),[{value:"streamer",label:t("streamer")},{value:"admin",label:t("admin")}],user.role)}${choicePicker("employmentType",t("employment"),[{value:"Full-time",label:t("fullTime")},{value:"Part-time",label:t("partTime")}],user.employmentType)}<div class="modal-action"><button type="button" class="btn" data-close-dialog>${t("cancel")}</button><button type="submit" class="btn btn-primary">${t("save")}</button></div></form></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}
function statusBadge(active:boolean,pending=false):string {
  const activationPending=pending;
  const label=activationPending?t("pendingActivationStatus"):t(active?"active":"inactive");
  return `<span class="badge user-status-badge ${activationPending?"badge-warning":active?"badge-success":"badge-ghost"}">${label}</span>`;
}
async function users(): Promise<string> {
  const r = await api<UsersResult>("/users");
  if (!r.ok)
    return card(
      t("users"),
      `<p class="text-error">${esc(r.error ?? t("requestFailed"))}</p>`,
    );
  savedUsers = r.users;
  const rows = r.users
    .map(
      (u) =>
        `<tr data-management-row data-status="${u.activationPending?"pending":u.active?"active":"inactive"}" data-search="${esc(`${u.name} ${u.email}`.toLowerCase())}"><td data-label="${t("name")}"><div class="flex items-center gap-3">${profileAvatar(u.profileImageKey)}<strong>${esc(u.name)}</strong></div></td><td data-label="${t("email")}" class="break-all">${esc(u.email)}</td><td data-label="${t("role")}"><span class="badge user-role-badge badge-outline">${t(u.role === "admin" ? "admin" : "streamer")}</span></td><td data-label="${t("employment")}">${t(u.employmentType === "Full-time" ? "fullTime" : "partTime")}</td><td data-label="${t("status")}">${statusBadge(u.active,u.activationPending)}</td><td data-label="${t("actions")}"><div class="flex flex-wrap gap-2">${btn("edit-user", t("edit"), u.id)} ${u.activationPending?btn("resend-activation",t("resendActivation"),u.id):btn(`${u.active ? "deactivate" : "activate"}-user`,u.active?t("disabled"):t("enabled"),u.id)}</div></td></tr>`,
    )
    .join("");
  return `${card(t("users"), `<div class="flex flex-wrap items-center justify-between gap-2"><p class="text-sm text-base-content/60">${r.users.length} ${t("users")}</p><input data-management-search class="input input-bordered input-sm max-w-xs" placeholder="${t("searchManagement")}"></div><form id="user-form" class="mt-4 grid gap-2 md:grid-cols-5"><div class="md:col-span-5">${profileImageField("new-user",null)}</div><input required name="name" class="input input-bordered" placeholder="${t("name")}"><input required name="email" type="email" class="input input-bordered" placeholder="${t("email")}"><select name="role" class="select select-bordered"><option value="streamer">${t("streamer")}</option><option value="admin">${t("admin")}</option></select><select name="employmentType" class="select select-bordered"><option value="Full-time">${t("fullTime")}</option><option value="Part-time">${t("partTime")}</option></select><button class="btn btn-primary" type="submit">${t("addUser")}</button></form><p class="mt-2 text-xs text-base-content/60">${t("createUserHint")}</p><div class="overflow-x-auto mt-4"><table class="management-table table table-sm"><thead><tr><th>${t("name")}</th><th>${t("email")}</th><th>${t("role")}</th><th>${t("employment")}</th><th>${t("status")}</th><th>${t("actions")}</th></tr></thead><tbody>${rows || `<tr><td colspan="6">${t("noData")}</td></tr>`}</tbody></table></div><p class="hidden py-6 text-center text-base-content/60" data-management-empty>${t("noData")}</p>`)}${savedUsers.map(userEditDialog).join("")}`;
}
function platformEditDialog(platform: Platform): string {
  return `<dialog id="platform-edit-${esc(platform.id)}" class="modal"><div class="modal-box"><h3 class="text-lg font-bold">${t("editPlatform")}</h3><form id="platform-edit-form-${esc(platform.id)}" data-form="platform-edit" data-platform-form class="mt-4 grid gap-3"><input type="hidden" name="id" value="${esc(platform.id)}">${platformImageField(platform.id,platform.profileImageKey)}<div class="rounded-box border border-base-300 p-3" data-platform-preview><span style="color:${esc(platform.color)}" class="mr-2 text-2xl">${esc(platform.icon??"●")}</span><strong>${esc(platform.name)}</strong></div><label class="form-control"><span class="label-text">${t("name")}</span><input required name="name" value="${esc(platform.name)}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("url")}</span><input name="url" type="url" value="${esc(platform.url)}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("icon")}</span><input name="icon" maxlength="32" value="${esc(platform.icon ?? "")}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("color")}</span><div class="flex gap-2"><input name="color" type="color" value="${esc(platform.color)}" class="input input-bordered w-20 p-1"><input data-color-hex value="${esc(platform.color)}" maxlength="7" class="input input-bordered font-mono"></div></label><div class="modal-action"><button type="button" class="btn" data-close-dialog>${t("cancel")}</button><button type="submit" class="btn btn-primary">${t("save")}</button></div></form></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}
async function platforms(): Promise<string> {
  const r = await api<PlatformsResult>("/platforms");
  if (!r.ok)
    return card(
      t("platforms"),
      `<p class="text-error">${esc(r.error ?? t("requestFailed"))}</p>`,
    );
  savedPlatforms = r.platforms;
  const cards = r.platforms
    .map(
      (p) =>
        `<article data-management-row data-status="${p.active?"active":"inactive"}" data-search="${esc(`${p.name} ${p.url}`.toLowerCase())}" class="rounded-box border border-base-300 bg-base-100 p-4 shadow-sm" style="border-top:4px solid ${esc(p.color)}"><div class="flex items-start gap-3">${p.profileImageKey?platformAvatar(p.profileImageKey):`<span class="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-base-200 text-2xl" style="color:${esc(p.color)}">${esc(p.icon??"●")}</span>`}<div class="min-w-0 flex-1"><div class="flex flex-wrap items-center justify-between gap-2"><h3 class="font-bold">${esc(p.name)}</h3>${statusBadge(p.active)}</div><p class="mt-1 truncate text-sm text-base-content/60" title="${esc(p.url)}">${esc(p.url||"—")}</p></div></div><div class="mt-4 flex flex-wrap gap-2">${btn("edit-platform",t("edit"),p.id)} ${btn(`${p.active?"deactivate":"activate"}-platform`,p.active?t("disabled"):t("enabled"),p.id)}</div></article>`,
    )
    .join("");
  return `${card(t("platforms"), `<div class="flex flex-wrap items-center justify-between gap-2"><p class="text-sm text-base-content/60">${r.platforms.length} ${t("platforms")}</p><input data-management-search class="input input-bordered input-sm max-w-xs" placeholder="${t("search")}"></div><form id="platform-form" data-platform-form class="mt-4 grid gap-2 md:grid-cols-5"><div class="md:col-span-5">${platformImageField("new-platform",null)}</div><input required name="name" class="input input-bordered" placeholder="${t("name")}"><input name="url" type="url" class="input input-bordered" placeholder="${t("url")}"><div class="flex gap-2"><input name="color" type="color" value="#888888" class="input input-bordered w-20 p-1"><input data-color-hex value="#888888" maxlength="7" class="input input-bordered font-mono"></div><input name="icon" maxlength="32" class="input input-bordered" placeholder="${t("icon")}"><button class="btn btn-primary" type="submit">${t("addPlatform")}</button><div class="rounded-box border border-base-300 p-3 md:col-span-5" data-platform-preview><span class="mr-2 text-2xl" style="color:#888888">●</span><strong>${t("platformPreview")}</strong></div></form><div class="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">${cards||`<p>${t("noData")}</p>`}</div><p class="hidden py-6 text-center text-base-content/60" data-management-empty>${t("noData")}</p>`)}${savedPlatforms.map(platformEditDialog).join("")}`;
}
function productEditDialog(product: Product): string {
  return `<dialog id="product-edit-${esc(product.id)}" class="modal"><div class="modal-box"><h3 class="text-lg font-bold">${t("edit")} ${t("products")}</h3><form id="product-edit-form" class="mt-4 grid gap-3"><input type="hidden" name="id" value="${esc(product.id)}"><label class="form-control"><span class="label-text">${t("category")}</span><select name="category" class="select select-bordered">${categories.map((category) => `<option value="${esc(category)}" ${product.category === category ? "selected" : ""}>${categoryLabel(category)}</option>`).join("")}</select></label><label class="form-control"><span class="label-text">${t("productName")}</span><input required name="name" value="${esc(product.name)}" class="input input-bordered"></label><div class="modal-action"><button type="button" class="btn" data-close-dialog>${t("cancel")}</button><button type="submit" class="btn btn-primary">${t("save")}</button></div></form></div><form method="dialog" class="modal-backdrop"><button>${t("cancel")}</button></form></dialog>`;
}
async function products(): Promise<string> {
  const r = await api<ProductsResult>("/products");
  if (!r.ok)
    return card(
      t("products"),
      `<p class="text-error">${esc(r.error ?? t("requestFailed"))}</p>`,
    );
  savedProducts=r.products;
  const rows = r.products
    .map(
      (p) =>
        `<tr data-management-row data-category="${esc(p.category)}" data-status="${p.active?"active":"inactive"}" data-search="${esc(p.name.toLowerCase())}"><td data-label="${t("category")}">${categoryLabel(p.category)}</td><td data-label="${t("productName")}"><strong>${esc(p.name)}</strong></td><td data-label="${t("productStatus")}">${statusBadge(p.active)}</td><td data-label="${t("actions")}"><div class="flex flex-wrap gap-2">${btn("edit-product", t("edit"), p.id)} ${btn(`${p.active ? "deactivate" : "activate"}-product`, p.active ? t("disabled") : t("enabled"), p.id)}</div></td></tr>`,
    )
    .join("");
  const counts = categories
    .map(
      (c) =>
        `<span class="badge">${categoryLabel(c)}: ${r.activeCounts[c]}/50</span>`,
    )
    .join(" ");
  const filters=`<div class="mt-4 flex flex-wrap gap-2"><button type="button" class="btn btn-primary btn-sm" data-product-filter="">${t("allCategories")}</button>${categories.map((category)=>`<button type="button" class="btn btn-sm" data-product-filter="${esc(category)}">${categoryLabel(category)} · ${r.activeCounts[category]}/50</button>`).join("")}</div><div class="mt-3 grid gap-2 sm:grid-cols-2"><input data-management-search class="input input-bordered input-sm" placeholder="${t("search")}"><select data-management-status class="select select-bordered select-sm"><option value="">${t("allStatuses")}</option><option value="active">${t("active")}</option><option value="inactive">${t("inactive")}</option></select></div>`;
  return `${card(t("products"), `<p class="text-sm text-base-content/60">${t("productLimit")}</p><p class="mt-2 flex flex-wrap gap-2">${counts}</p><form id="product-form" class="mt-3 flex flex-wrap gap-2"><select required name="category" class="select select-bordered max-w-xs">${categories.map((c) => `<option value="${esc(c)}">${categoryLabel(c)}</option>`).join("")}</select><input required maxlength="100" name="name" class="input input-bordered min-w-48 flex-1" placeholder="${t("productName")}"><button class="btn btn-primary" type="submit">${t("addProduct")}</button></form>${filters}<div class="overflow-x-auto mt-4"><table class="management-table table table-sm"><thead><tr><th>${t("category")}</th><th>${t("productName")}</th><th>${t("productStatus")}</th><th>${t("actions")}</th></tr></thead><tbody>${rows || `<tr><td colspan="4">${t("noData")}</td></tr>`}</tbody></table></div><p class="hidden py-6 text-center text-base-content/60" data-management-empty>${t("noData")}</p>`)}${r.products.map(productEditDialog).join("")}`;
}
function setting(s:Record<string,string>,name:string,key:string,label:string,min:number,max:number|undefined,step:number,hint:string):string {
  return `<label class="form-control"><span class="label-text">${esc(label)}</span><input required min="${min}" ${max===undefined?"":`max="${max}"`} step="${step}" type="number" name="${name}" value="${esc(s[key]??"")}" class="input input-bordered"><span class="label-text-alt mt-1 text-base-content/60">${esc(hint)}</span></label>`;
}
function parseReminders(value: string | undefined): Reminder[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry) => {
      if (
        typeof entry !== "object" ||
        entry === null ||
        !("title" in entry) ||
        !("items" in entry)
      )
        return [];
      const title = entry.title;
      const items = entry.items;
      return typeof title === "string" &&
        Array.isArray(items) &&
        items.every((item) => typeof item === "string")
        ? [{ title, items }]
        : [];
    });
  } catch {
    return [];
  }
}
function reminderEditorValue(form: HTMLFormElement): Reminder[] {
  return savedReminders
    .map((reminder, categoryIndex) => {
      const title =
        form
          .querySelector<HTMLInputElement>(
            `[data-reminder-title="${categoryIndex}"]`,
          )
          ?.value.trim()
          .slice(0, 80) ?? reminder.title;
      const items = reminder.items
        .map(
          (item, itemIndex) =>
            form
              .querySelector<HTMLInputElement>(
                `[data-reminder-item="${categoryIndex}-${itemIndex}"]`,
              )
              ?.value.trim()
              .slice(0, 200) ?? item,
        )
        .filter(Boolean);
      return { title, items };
    })
    .filter(
      (reminder) => reminder.title.length > 0 || reminder.items.length > 0,
    );
}
function facePhotoError(error: string | undefined, validation = false): string {
  if (error === "NETWORK_OFFLINE" || error === "NETWORK_TIMEOUT") return t("uploadFailed");
  if (error === "FACE_PHOTO_REQUIRED")
    return t(validation ? "validationFacePhotoRequired" : "facePhotoRequired");
  if (error === "FACE_PHOTO_INVALID") return t("facePhotoUploadInvalid");
  if (error === "AUTO_CLOSE_EST_UNCHANGED") return t("autoCloseActualEndRequired");
  return error ?? t("requestFailed");
}
function reminderEditor(): string {
  const categories = savedReminders
    .map(
      (reminder, categoryIndex) =>
        `<fieldset class="rounded-box border border-base-300 p-4"><div class="flex gap-2"><label class="form-control flex-1"><span class="label-text">${t("reminderTitle")}</span><input required data-reminder-title="${categoryIndex}" value="${esc(reminder.title)}" maxlength="80" class="input input-bordered"></label><button type="button" class="btn btn-ghost btn-sm self-end" data-action="delete-reminder-category" data-id="${categoryIndex}">${t("delete")}</button></div><div class="mt-3 grid gap-2">${reminder.items.map((item, itemIndex) => `<div class="flex gap-2"><input required data-reminder-item="${categoryIndex}-${itemIndex}" value="${esc(item)}" maxlength="200" class="input input-bordered flex-1"><button type="button" class="btn btn-ghost btn-sm" data-action="delete-reminder-item" data-id="${categoryIndex}-${itemIndex}">${t("remove")}</button></div>`).join("")}</div><button type="button" class="btn btn-outline btn-sm mt-3" ${reminder.items.length >= 20 ? "disabled" : ""} data-action="add-reminder-item" data-id="${categoryIndex}">${t("addReminderItem")}</button></fieldset>`,
    )
    .join("");
  return `<form id="reminder-editor-form" class="mt-3 grid gap-4"><p class="text-sm text-base-content/60">${t("reminderEditorHint")}</p>${categories || `<p class="text-base-content/60">${t("emptyReminders")}</p>`}<div class="flex flex-wrap gap-2"><button type="button" class="btn btn-secondary" ${savedReminders.length >= 10 ? "disabled" : ""} data-action="add-reminder-category">${t("addReminderCategory")}</button><button class="btn btn-primary" type="submit">${t("saveReminders")}</button></div></form>`;
}
async function settings(): Promise<string> {
  const r = await api<SettingsResult>("/settings");
  if (!r.ok)
    return card(
      t("settings"),
      `<p class="text-error">${esc(r.error ?? t("requestFailed"))}</p>`,
    );
  savedSettings={...r.settings};
  savedReminders = parseReminders(r.settings.reminders);
  const rates=card(t("settingsRatesTitle"),`<div class="grid gap-3 md:grid-cols-2">${setting(r.settings,"partTimeRate","part_time_rate",t("partTimeRate"),0.01,undefined,0.01,t("partTimeRateHint"))}${setting(r.settings,"fullTimeRate","full_time_rate",t("fullTimeRate"),0.01,undefined,0.01,t("fullTimeRateHint"))}${setting(r.settings,"commRate","comm_rate",t("commissionRate"),0,100,0.01,t("commissionRateHint"))}${setting(r.settings,"autoCloseHours","auto_close_hours",t("autoCloseHours"),1,12,0.5,t("autoCloseHoursHint"))}</div>`);
  const roi=card(t("settingsRoiTitle"),`<div class="grid gap-3 md:grid-cols-2">${setting(r.settings,"roiGood","roi_good",t("roiGood"),0,undefined,0.01,t("roiGoodHint"))}${setting(r.settings,"roiOk","roi_ok",t("roiOk"),0,undefined,0.01,t("roiOkHint"))}</div><div class="mt-4 flex flex-wrap gap-2"><span class="badge badge-success" data-settings-roi-good></span><span class="badge badge-warning" data-settings-roi-ok></span><span class="badge badge-error" data-settings-roi-bad></span></div>`);
  const preview=card(t("settingsPreviewTitle"),`<p class="mb-3 text-sm text-base-content/60">${t("settingsPreviewScenario")}</p><div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-5"><div class="stat rounded-box bg-base-200 p-4"><div class="stat-title">${t("settingsPreviewPartTime")}</div><div class="stat-value text-xl" data-settings-preview-part-time></div></div><div class="stat rounded-box bg-base-200 p-4"><div class="stat-title">${t("settingsPreviewFullTime")}</div><div class="stat-value text-xl" data-settings-preview-full-time></div></div><div class="stat rounded-box bg-base-200 p-4"><div class="stat-title">${t("settingsPreviewCommission")}</div><div class="stat-value text-xl" data-settings-preview-commission></div></div><div class="stat rounded-box bg-base-200 p-4"><div class="stat-title">${t("settingsPreviewRevenue")}</div><div class="stat-value text-xl" data-settings-preview-revenue></div></div><div class="stat rounded-box bg-base-200 p-4"><div class="stat-title">ROI</div><div class="stat-value text-xl" data-settings-preview-roi></div></div></div>`);
  const telegramIds=(r.settings.tg_ids??"").split(",").join("\n");
  const telegram=card(t("settingsTelegramTitle"),`<label class="form-control"><span class="label-text">${t("telegramChatIds")}</span><textarea name="telegramChatIds" rows="4" class="textarea textarea-bordered font-mono" placeholder="-5154690504\n8660249219">${esc(telegramIds)}</textarea><span class="label-text-alt mt-1 text-base-content/60">${t("telegramChatIdsHint")}</span></label><button type="button" class="btn btn-secondary btn-sm mt-3" data-test-telegram>📣 ${t("testTelegram")}</button>`);
  const actions=`<div class="flex justify-end gap-2"><button class="btn" type="reset">↩ ${t("reset")}</button><button class="btn btn-primary" type="submit">💾 ${t("save")}</button></div>`;
  return `<div class="mb-4"><h1 class="text-3xl font-bold">${t("settings")}</h1><p class="text-sm text-base-content/60">${t("settingsHelp")}</p></div><form id="settings-form" class="grid gap-4">${rates}${roi}${preview}${telegram}${actions}</form><div class="mt-4">${card(t("reminders"),reminderEditor())}</div>`;
}
async function auditLogs(): Promise<string> {
  const query = new URLSearchParams({ dateFrom:auditFilters.dateFrom, dateTo:auditFilters.dateTo, page:String(auditFilters.page), pageSize:"50" });
  if (auditFilters.userId) query.set("userId",auditFilters.userId);
  if (auditFilters.action) query.set("action",auditFilters.action);
  if (auditFilters.success) query.set("success",auditFilters.success);
  const [result,usersResult] = await Promise.all([api<AuditResult>(`/audit?${query}`),api<UsersResult>("/users")]);
  if (!result.ok || !usersResult.ok) return card(t("audit"),`<p class="text-error">${esc(result.error ?? usersResult.error ?? t("requestFailed"))}</p>`);
  const pageCount = Math.max(1,Math.ceil(result.total/result.pageSize));
  const formatTime = (value:string):string => new Intl.DateTimeFormat(currentLanguage()==="th"?"th-TH":"en-GB",{timeZone:"Asia/Bangkok",dateStyle:"medium",timeStyle:"medium"}).format(new Date(value));
  const rows = result.logs.map((log)=>{
    const securityRejection = !log.success && (log.action === "LOGIN_FAIL" || log.action === "LOGIN_BLOCKED");
    const resultClass = log.success ? "badge-success" : securityRejection ? "badge-warning" : "badge-error";
    const resultLabel = log.success ? t("auditSuccess") : securityRejection ? t("auditSecurityRejected") : t("auditFailure");
    return `<tr><td class="whitespace-nowrap">${esc(formatTime(log.timestamp))}</td><td><span class="badge badge-outline whitespace-nowrap">${esc(log.action)}</span></td><td>${esc(log.userName)}</td><td class="min-w-64 break-words font-mono text-xs">${esc(log.detail||"—")}</td><td><span class="badge whitespace-nowrap ${resultClass}">${resultLabel}</span></td></tr>`;
  }).join("");
  const filter = `<form id="audit-filter" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-6"><label class="form-control"><span class="label-text">${t("from")}</span><input required name="dateFrom" type="date" value="${auditFilters.dateFrom}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("to")}</span><input required name="dateTo" type="date" value="${auditFilters.dateTo}" class="input input-bordered"></label><label class="form-control"><span class="label-text">${t("auditActor")}</span><select name="userId" class="select select-bordered"><option value="">${t("auditAllActors")}</option>${usersResult.users.map((user)=>`<option value="${user.id}" ${user.id===auditFilters.userId?"selected":""}>${esc(user.name)}</option>`).join("")}</select></label><label class="form-control"><span class="label-text">${t("auditAction")}</span><select name="action" class="select select-bordered"><option value="">${t("allActions")}</option>${result.actions.map((action)=>`<option value="${esc(action)}" ${action===auditFilters.action?"selected":""}>${esc(action)}</option>`).join("")}</select></label><label class="form-control"><span class="label-text">${t("auditResult")}</span><select name="success" class="select select-bordered"><option value="">${t("allResults")}</option><option value="true" ${auditFilters.success==="true"?"selected":""}>${t("auditSuccess")}</option><option value="false" ${auditFilters.success==="false"?"selected":""}>${t("auditFailure")}</option></select></label><button type="submit" class="btn btn-primary self-end">🔍 ${t("search")}</button></form>`;
  const summary = t("auditPageSummary").replace("{page}",String(result.page)).replace("{pages}",String(pageCount)).replace("{total}",String(result.total));
  const pager = `<div class="mt-4 flex flex-wrap items-center justify-between gap-3"><span class="text-sm text-base-content/60">${esc(summary)}</span><div class="join"><button type="button" class="btn join-item" data-action="audit-page" data-id="${Math.max(1,result.page-1)}" ${result.page<=1?"disabled":""}>← ${t("previousPage")}</button><button type="button" class="btn join-item" data-action="audit-page" data-id="${Math.min(pageCount,result.page+1)}" ${result.page>=pageCount?"disabled":""}>${t("nextPage")} →</button></div></div>`;
  return `${card(t("audit"),`<p class="mb-4 text-sm text-base-content/60">${t("auditHelp")}</p>${filter}`)}<div class="mt-4">${card(t("audit"),`<div class="overflow-x-auto"><table class="table table-sm"><thead><tr><th>${t("date")}</th><th>${t("auditAction")}</th><th>${t("auditActor")}</th><th>${t("auditDetail")}</th><th>${t("auditResult")}</th></tr></thead><tbody>${rows||`<tr><td colspan="5">${t("noData")}</td></tr>`}</tbody></table></div>${pager}`)}</div>`;
}
async function render(): Promise<void> {
  historyImageObserver?.disconnect();
  historyImageObserver = null;
  if (liveElapsedTimer !== undefined) {
    window.clearInterval(liveElapsedTimer);
    liveElapsedTimer = undefined;
  }
  if (calculationTimer !== undefined) {
    window.clearTimeout(calculationTimer);
    calculationTimer = undefined;
  }
  historyImageUrls.forEach((url) => URL.revokeObjectURL(url));
  historyImageUrls = [];
  document.querySelectorAll<HTMLDialogElement>("[data-history-lazy-dialog]").forEach((dialog) => dialog.remove());
  if (!getToken()) {
    root.innerHTML = authPage();
    bind();
    return;
  }
  const user = getUser();
  if (!user) {
    clearSession();
    root.innerHTML = authPage();
    bind();
    return;
  }
  const route = requestedPage();
  page = route && allowedPages(user).includes(route) ? route : "live";
  const canonicalHash = `#/${page}`;
  if (window.location.hash !== canonicalHash)
    window.history.replaceState(null, "", canonicalHash);
  if (managerExportLanguage && page === "manager-report") {
    document.documentElement.lang = managerExportLanguage;
    root.innerHTML = managerExportPage(managerExportLanguage);
    bind();
    imageHydrationPromise = hydrateHistoryImages();
    return;
  }
  document.documentElement.lang = currentLanguage();
  root.innerHTML = shell(
    card(t("loading"), '<span class="loading loading-spinner"></span>'),
  );
  const view =
    page === "live"
      ? await live()
      : page === "dashboard"
        ? await dashboard()
        : page === "history"
          ? await history(false)
          : page === "my-history"
            ? await history(true)
            : page === "manager-report"
              ? await managerReport()
              : page === "schedules"
                ? await schedulesPage()
                : page === "my-schedule"
                  ? await mySchedule()
                  : page === "users"
                    ? await users()
                    : page === "platforms"
                      ? await platforms()
                      : page === "products"
                        ? await products()
                        : page === "settings"
                          ? await settings()
                          : await auditLogs();
  root.innerHTML = shell(
    `${notice ? `<div class="alert alert-info mb-4">${esc(notice)}</div>` : ""}${view}`,
  );
  bind();
  imageHydrationPromise = hydrateHistoryImages();
}
async function files(
  form: HTMLFormElement,
  name: string,
  kind: "face" | "session",
  limit: number,
  required = true,
): Promise<string[] | null> {
  const input = form.elements.namedItem(name);
  if (!(input instanceof HTMLInputElement)) return null;
  const selected = name === "endFace" || name === "endImages"
    ? selectedEndDraftFiles(form, name)
    : name === "startFace" || name === "startImages"
      ? selectedStartDraftFiles(form, name)
      : [...(input.files ?? [])];
  if ((kind === "face" && (selected.length > 1 || (required && selected.length !== 1))) || selected.length > limit)
    return null;
  const keys: Array<string | null> = [];
  for (const file of selected) {
    const prepared = kind === "face" ? await stampFacePhoto(file) : file;
    keys.push(prepared ? await uploadImage(prepared, kind) : null);
  }
  return keys.every((key): key is string => key !== null) ? keys : null;
}
function profileFile(form:HTMLFormElement):File|null {
  const input=form.elements.namedItem("profileImage");
  return input instanceof HTMLInputElement ? input.files?.[0] ?? null : null;
}
async function saveSelfProfile(form: HTMLFormElement): Promise<void> {
  const current = getUser();
  const file = profileFile(form);
  const message = form.querySelector<HTMLElement>("[data-profile-message]");
  const showMessage = (text: string, success: boolean): void => {
    if (!message) return;
    message.textContent = text;
    message.className = `rounded-box p-3 text-sm ${success ? "bg-success/15 text-success" : "bg-error/15 text-error"}`;
  };
  if (!current || !file) {
    showMessage(t("invalidProfileImage"), false);
    return;
  }
  showUploadProgress(0);
  try {
    const uploaded = await uploadProfileImage(file, current.id);
    hideUploadProgress();
    if (!uploaded.ok || !uploaded.key) {
      showMessage(managementError(uploaded), false);
      return;
    }
    const token = getToken();
    if (token) saveSession(token, { ...current, profileImageKey: uploaded.key });
    notice = t("profileImageSaved");
    await render();
  } catch {
    hideUploadProgress();
    showMessage(t("uploadFailed"), false);
  }
}
function bangkokInputToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}:00+07:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}
async function saveSessionEdit(form: HTMLFormElement): Promise<void> {
  const dialog = form.closest<HTMLDialogElement>("dialog");
  const button = form.querySelector<HTMLButtonElement>("[data-save-session-edit]");
  const message = form.querySelector<HTMLElement>("[data-session-edit-message]");
  const showError = (text: string): void => {
    if (!message) return;
    message.textContent = text;
    message.className = "rounded-box bg-error/15 p-3 text-sm text-error md:col-span-2";
  };
  if (!form.checkValidity()) {
    showError(t("requestFailed"));
    form.reportValidity();
    return;
  }
  const showcase = collectShowcase(form, "edit-showcase");
  if (!showcase) {
    showError(t("requestFailed"));
    return;
  }
  const admin = form.dataset.admin === "true";
  const startTime = admin ? bangkokInputToIso(field(form, "startTime")) : null;
  const endTime = admin ? bangkokInputToIso(field(form, "endTime")) : null;
  if (admin && (!startTime || !endTime || Date.parse(endTime) <= Date.parse(startTime))) {
    showError(t("requestFailed"));
    return;
  }
  const spinner = document.createElement("span");
  spinner.className = "loading loading-spinner loading-sm";
  button?.prepend(spinner);
  if (button) button.disabled = true;
  form.setAttribute("aria-busy", "true");
  try {
    const result = await api<ApiResult>(`/sessions/${field(form, "id")}`, {
      method: "PATCH",
      body: JSON.stringify({
        ...(admin ? { startTime, endTime } : {}),
        verena: Number(field(form, "verena")),
        affiliate: Number(field(form, "affiliate")),
        audience: Number(field(form, "audience")),
        orderNumbers: field(form, "orderNumbers"),
        ...(admin ? { endNote: field(form, "endNote") } : {}),
        showcase,
      }),
    });
    if (!result.ok) {
      showError(managementError(result));
      return;
    }
    dialog?.close();
    notice = t("saveSuccess");
    await render();
  } catch {
    showError(t("requestFailed"));
  } finally {
    spinner.remove();
    if (button?.isConnected) button.disabled = false;
    if (form.isConnected) form.removeAttribute("aria-busy");
  }
}
function validationPayload(form: HTMLFormElement): { startTime: string; endTime: string; verena: number; affiliate: number; audience: number; orderNumbers: string; endNote: string; showcase: Showcase } | null {
  if (!form.checkValidity()) { form.reportValidity(); return null; }
  const showcase = collectShowcase(form, "validation-showcase");
  const startTime = bangkokInputToIso(field(form, "startTime"));
  const endTime = bangkokInputToIso(field(form, "endTime"));
  if (!showcase || !startTime || !endTime || Date.parse(endTime) <= Date.parse(startTime)) return null;
  return { startTime, endTime, verena: Number(field(form, "verena")), affiliate: Number(field(form, "affiliate")), audience: Number(field(form, "audience")), orderNumbers: field(form, "orderNumbers"), endNote: field(form, "endNote"), showcase };
}
function validationMessage(form: HTMLFormElement, text: string, success = false): void {
  const message = form.querySelector<HTMLElement>("[data-validation-message]");
  if (!message) return;
  message.textContent = text;
  message.className = `rounded-box p-3 text-sm md:col-span-2 ${success ? "bg-success/15 text-success" : "bg-error/15 text-error"}`;
}
async function previewValidation(form: HTMLFormElement): Promise<void> {
  const preview = form.querySelector<HTMLElement>("[data-validation-preview]");
  const payload = validationPayload(form);
  if (!preview || !payload) return;
  try {
    const result = await api<ValidationPreviewResult>(`/sessions/${field(form, "id")}/validation-preview`, { method: "POST", body: JSON.stringify(payload) });
    if (!result.ok || !result.derived) { preview.textContent = result.error ?? t("requestFailed"); return; }
    const totals = result.derived;
    preview.textContent = `${t("duration")}: ${duration(totals.durationMins)} · ${t("laborCost")}: ${money(totals.ot)} · ${t("commission")}: ${money(totals.commission)} · ${t("totalRevenue")}: ${money(totals.totalRevenue)} · ROI: ${totals.roi.toFixed(2)}×`;
  } catch { preview.textContent = t("requestFailed"); }
}
async function submitValidation(form: HTMLFormElement, validate: boolean): Promise<void> {
  const payload = validationPayload(form);
  if (!payload) { validationMessage(form, t("requestFailed")); return; }
  const selector = validate ? "[data-submit-validation]" : "[data-save-validation]";
  const button = form.querySelector<HTMLButtonElement>(selector);
  const dialog = form.closest<HTMLDialogElement>("dialog");
  const spinner = document.createElement("span"); spinner.className = "loading loading-spinner loading-sm";
  button?.prepend(spinner); if (button) button.disabled = true; form.setAttribute("aria-busy", "true");
  try {
    const result = await api<ApiResult>(`/sessions/${field(form, "id")}${validate ? "/validate" : ""}`, { method: validate ? "POST" : "PATCH", body: JSON.stringify(payload) });
    if (!result.ok) { validationMessage(form, facePhotoError(result.error, validate)); return; }
    dialog?.close(); notice = t("saveSuccess"); await render();
  } catch { validationMessage(form, t("requestFailed")); }
  finally { spinner.remove(); if (button?.isConnected) button.disabled = false; if (form.isConnected) form.removeAttribute("aria-busy"); }
}
function bindValidationForm(form: HTMLFormElement): void {
  if (form.dataset.validationBound === "true") return;
  form.dataset.validationBound = "true";
  form.querySelector<HTMLButtonElement>("[data-save-validation]")?.addEventListener("click", () => void submitValidation(form, false));
  form.querySelector<HTMLButtonElement>("[data-submit-validation]")?.addEventListener("click", () => void submitValidation(form, true));
  const queuePreview = () => {
    if (validationPreviewTimer !== undefined) window.clearTimeout(validationPreviewTimer);
    validationPreviewTimer = window.setTimeout(() => void previewValidation(form), 200);
  };
  form.addEventListener("input", queuePreview); form.addEventListener("change", queuePreview);
  void previewValidation(form);
}
function bindSessionEditForm(form: HTMLFormElement): void {
  if (form.dataset.sessionEditBound === "true") return;
  form.dataset.sessionEditBound = "true";
  const action = form.querySelector<HTMLElement>(".modal-action");
  const button = action?.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (button) {
    button.type = "button";
    button.dataset.saveSessionEdit = "";
    button.addEventListener("click", () => void saveSessionEdit(form));
  }
  const message = document.createElement("p");
  message.dataset.sessionEditMessage = "";
  message.className = "hidden rounded-box p-3 text-sm md:col-span-2";
  message.setAttribute("role", "alert");
  action?.before(message);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    void saveSessionEdit(form);
  });
}
async function finishSession(form: HTMLFormElement): Promise<void> {
  const admin = form.dataset.admin === "true";
  const sessionId = form.dataset.sessionId;
  const button = form.querySelector<HTMLButtonElement>("[data-end-session-submit]");
  const message = form.querySelector<HTMLElement>("[data-end-session-message]");
  const dialog = form.closest<HTMLDialogElement>("dialog");
  const showError = (text: string): void => {
    if (!message) {
      notice = text;
      void render();
      return;
    }
    message.textContent = text;
    message.className = "rounded-box bg-error/15 p-3 text-sm text-error md:col-span-2";
  };
  if (!sessionId || !form.checkValidity()) {
    showError(t("requestFailed"));
    form.reportValidity();
    return;
  }
  const showcase = collectShowcase(form, "showcase");
  if (!showcase) {
    showError(t("requestFailed"));
    return;
  }
  const endTime = admin ? bangkokInputToIso(field(form, "endTime")) : null;
  if (admin && !endTime) {
    showError(t("requestFailed"));
    return;
  }
  await saveEndDraft(form);
  const spinner = document.createElement("span");
  spinner.className = "loading loading-spinner loading-sm";
  button?.prepend(spinner);
  if (button) button.disabled = true;
  form.setAttribute("aria-busy", "true");
  try {
    const face = await files(form, "endFace", "face", 1, !admin);
    const endImages = await files(form, "endImages", "session", 5);
    if (!face || !endImages || (!admin && face.length !== 1)) {
      showError(t("uploadFailed"));
      return;
    }
    const result = await api<ApiResult>(`/sessions/${sessionId}/end`, {
      method: "POST",
      body: JSON.stringify({
        ...(admin && endTime ? { endTime } : {}),
        verena: Number(field(form, "verena")),
        affiliate: Number(field(form, "affiliate")),
        audience: Number(field(form, "audience")),
        orderNumbers: field(form, "orderNumbers"),
        endNote: field(form, "endNote"),
        showcase,
        ...(face[0] ? { endFaceImageKey: face[0] } : {}),
        endImages,
      }),
    });
    if (!result.ok) {
      showError(facePhotoError(result.error));
      return;
    }
    await deleteEndDraft(sessionId);
    if (sessionId === idleActiveSessionId) setIdleActiveSession(null);
    dialog?.close();
    notice = t("liveSaved");
    await render();
  } catch {
    showError(t("requestFailed"));
  } finally {
    hideUploadProgress();
    spinner.remove();
    if (button?.isConnected) button.disabled = false;
    if (form.isConnected) form.removeAttribute("aria-busy");
  }
}
function verifySavedStatus(result:StatusResult,expected:boolean):StatusResult {
  return result.ok && result.active !== expected
    ? {ok:false,error:"STATUS_NOT_SAVED",code:500}
    : result;
}
async function submit(e: SubmitEvent): Promise<void> {
  e.preventDefault();
  const f = e.currentTarget as HTMLFormElement;
  let r: ApiResult;
  let logoutAfterSave=false;
  if (f.id === "login-form") {
    const x = await api<LoginResult>("/auth/login", {
      method: "POST",
      body: JSON.stringify({
        email: field(f, "email"),
        password: field(f, "password"),
      }),
    });
    if (x.ok) {
      saveSession(x.token, x.user);
      idleActiveSessionId = null;
      recordIdleActivity(true);
      notice = "";
      await render();
      return;
    }
    r = x;
  } else if (f.id === "self-profile-form") {
    await saveSelfProfile(f);
    return;
  } else if (f.id === "change-password-form") {
    const current = getUser();
    const currentPassword = field(f, "currentPassword");
    const newPassword = field(f, "newPassword");
    if (!current || newPassword.length < 8) r = { ok: false, error: "PASSWORD_TOO_SHORT" };
    else if (newPassword !== field(f, "confirmPassword")) r = { ok: false, error: "PASSWORD_CONFIRM_MISMATCH" };
    else {
      const changed = await api<PasswordChangeResult>("/auth/password", {
        method: "PATCH",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      r = changed;
      if (changed.ok && changed.token) saveSession(changed.token, current);
    }
  } else if (f.id === "user-edit-form") {
    const current=getUser();
    logoutAfterSave=current?.id===field(f,"id")&&(current.email!==field(f,"email")||current.role!==field(f,"role"));
    const updated = await api<ApiResult>(`/users/${field(f, "id")}`, {
      method: "PATCH",
      body: JSON.stringify({
        name: field(f, "name"),
        email: field(f, "email"),
        role: field(f, "role"),
        employmentType: field(f, "employmentType"),
      }),
    });
    r=updated;
    const file=profileFile(f);
    if(r.ok&&file){
      const uploaded=await uploadProfileImage(file,field(f,"id"));
      r=uploaded;
      if(uploaded.ok&&uploaded.key&&current?.id===field(f,"id")){
        const token=getToken();
        if(token)saveSession(token,{...current,profileImageKey:uploaded.key});
      }
    }
  } else if (f.dataset.form === "platform-edit") {
    r = await api<ApiResult>(`/platforms/${field(f, "id")}`, {
      method: "PATCH",
      body: JSON.stringify({
        name: field(f, "name"),
        url: field(f, "url"),
        icon: field(f, "icon") || null,
        color: field(f, "color"),
      }),
    });
    const file = platformImageFile(f);
    if (r.ok && file) r = await uploadPlatformImage(file, field(f, "id"));
  } else if (f.id === "product-edit-form") {
    const product=savedProducts.find((item)=>item.id===field(f,"id"));
    const nextCategory=field(f,"category") as Category;
    if(product&&product.category!==nextCategory&&!window.confirm(t("confirmMoveProduct")))return;
    r = await api<ProductActionResult>(`/products/${field(f, "id")}`, {
      method: "PATCH",
      body: JSON.stringify({
        name: field(f, "name"),
        category: field(f, "category"),
      }),
    });
  } else if (f.id === "start-live-form") {
    const showStartError = (message: string): void => {
      const target = f.querySelector<HTMLElement>("[data-start-session-message]");
      if (target) {
        target.textContent = message;
        target.className = "rounded-box bg-error/15 p-3 text-sm text-error";
      } else {
        notice = message;
        void render();
      }
    };
    const showcasePlan = collectShowcase(f, "showcasePlan");
    if (!showcasePlan) {
      showStartError(t("requestFailed"));
      return;
    }
    await saveStartDraft(f);
    const face = await files(f, "startFace", "face", 1);
    const startImages = await files(f, "startImages", "session", 5);
    if (!face || !startImages) {
      hideUploadProgress();
      showStartError(t("uploadFailed"));
      return;
    }
    const reminders = [
      ...f.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked'),
    ].map((input) => input.value);
    const started = await api<StartSessionResult>("/sessions/start", {
      method: "POST",
      body: JSON.stringify({
        platformId: field(f, "platformId"),
        liveUrl: field(f, "liveUrl"),
        showcase: showcasePlan,
        showcasePlan,
        startFaceImageKey: face[0],
        startImages,
        startNote: field(f, "startNote"),
        substituteForId: field(f, "substituteForId") || undefined,
        remindersTicked: reminders,
      }),
    });
    r = started;
    if (started.ok) {
      await deleteStartDraft();
      setIdleActiveSession(started.sessionId ?? "active");
    }
  } else if (f.id === "user-form") {
    const created = await api<UserCreateResult>("/users", {
      method: "POST",
      body: JSON.stringify({
        name: field(f, "name"),
        email: field(f, "email"),
        role: field(f, "role"),
        employmentType: field(f, "employmentType"),
      }),
    });
    r=created;
    const file=profileFile(f);
    if(created.ok&&created.user?.id&&file)r=await uploadProfileImage(file,created.user.id);
  }
  else if (f.id === "platform-form") {
    const created = await api<PlatformCreateResult>("/platforms", {
      method: "POST",
      body: JSON.stringify({
        name: field(f, "name"),
        url: field(f, "url"),
        color: field(f, "color"),
        icon: field(f, "icon") || null,
      }),
    });
    r = created;
    const file = platformImageFile(f);
    if (created.ok && created.platformId && file) r = await uploadPlatformImage(file, created.platformId);
  }
  else if (f.id === "product-form")
    r = await api<ApiResult>("/products", {
      method: "POST",
      body: JSON.stringify({
        name: field(f, "name"),
        category: field(f, "category"),
      }),
    });
  else if (f.id === "reminder-editor-form") {
    savedReminders = reminderEditorValue(f);
    r = await api<ApiResult>("/settings", {
      method: "PATCH",
      body: JSON.stringify({ reminders: savedReminders }),
    });
  } else if (f.id === "settings-form") {
    const oldCommission=Number(savedSettings.comm_rate);
    const nextCommission=Number(field(f,"commRate"));
    r = await api<ApiResult>("/settings", {
      method: "PATCH",
      body: JSON.stringify({
        partTimeRate: Number(field(f, "partTimeRate")),
        fullTimeRate: Number(field(f, "fullTimeRate")),
        commRate: Number(field(f, "commRate")),
        roiGood: Number(field(f, "roiGood")),
        roiOk: Number(field(f, "roiOk")),
        autoCloseHours: Number(field(f, "autoCloseHours")),
        telegramChatIds: field(f, "telegramChatIds"),
      }),
    });
    if(r.ok&&oldCommission!==nextCommission){
      if(window.confirm(t("confirmRecalculateCurrentMonth"))){
        const parts=new Intl.DateTimeFormat("en-CA",{timeZone:"Asia/Bangkok",year:"numeric",month:"2-digit"}).formatToParts(new Date());
        const ym=`${parts.find((part)=>part.type==="year")?.value}-${parts.find((part)=>part.type==="month")?.value}`;
        const recalculated=await api<RecalculateResult>(`/reports/recalculate?ym=${encodeURIComponent(ym)}`,{method:"POST"});
        notice=recalculated.ok?t("recalculatedSessions").replace("{count}",String(recalculated.updated??0)):managementError(recalculated);
      }else notice=t("savedWithoutRecalculation");
      await render();return;
    }
  } else return;
  if(r.ok&&logoutAfterSave){clearSession();window.location.hash="#/login";notice=t("saveSuccess");await render();return;}
  hideUploadProgress();
  if (f.id === "start-live-form" && !r.ok) {
    const message = f.querySelector<HTMLElement>("[data-start-session-message]");
    if (message) {
      message.textContent = managementError(r);
      message.className = "rounded-box bg-error/15 p-3 text-sm text-error";
      return;
    }
  }
  notice = r.ok
    ? f.id === "change-password-form"
        ? t("passwordChanged")
      : f.id === "start-live-form"
        ? t("liveStarted")
        : t("saveSuccess")
    : managementError(r);
  await render();
}
async function act(b: HTMLButtonElement): Promise<void> {
  const id = b.dataset.id,
    task = b.dataset.action;
  if (!task) return;
  if (task === "resend-activation" && id) {
    const result = await api<ActivationResendResult>(`/users/${id}/resend-activation`, { method: "POST" });
    notice = result.ok
      ? result.delivery === "queued"
        ? t("activationQueued")
        : t("activationResent")
      : managementError(result);
    await render();
    return;
  }
  if (task === "open-history-pdf") {
    document
      .querySelector<HTMLDialogElement>("#history-pdf-dialog")
      ?.showModal();
    return;
  }
  if (task === "export-history-pdf" && (id === "th" || id === "en")) {
    await exportHistoryPdf(id);
    return;
  }
  if (task === "admin-end-session" && id) {
    const dialog = document.querySelector<HTMLDialogElement>(
      `#admin-end-session-${id}`,
    );
    const message = dialog?.querySelector<HTMLElement>("[data-end-session-message]");
    if (message) {
      message.textContent = "";
      message.className = "hidden rounded-box p-3 text-sm md:col-span-2";
    }
    dialog?.showModal();
    return;
  }
  if (task === "edit-session" && id) {
    let dialog = document.getElementById(`session-edit-${id}`) as HTMLDialogElement | null;
    if (!dialog) {
      const session = historyVisibleSessions.get(id);
      const admin = getUser()?.role === "admin";
      if (!session) return;
      document.body.insertAdjacentHTML("beforeend", sessionEditDialog(session, historyDialogProducts, Boolean(admin)));
      dialog = document.getElementById(`session-edit-${id}`) as HTMLDialogElement | null;
      if (dialog) dialog.dataset.historyLazyDialog = "true";
      const createdForm = dialog?.querySelector<HTMLFormElement>("#session-edit-form");
      if (createdForm) bindSessionEditForm(createdForm);
      dialog?.querySelectorAll<HTMLButtonElement>("[data-close-dialog]").forEach((button) =>
        button.addEventListener("click", () => dialog?.close()),
      );
    }
    const form = dialog?.querySelector<HTMLFormElement>("#session-edit-form");
    const message = form?.querySelector<HTMLElement>("[data-session-edit-message]");
    if (message) {
      message.textContent = "";
      message.className = "hidden rounded-box p-3 text-sm md:col-span-2";
    }
    dialog?.showModal();
    return;
  }
  if (
    task.startsWith("reminder-") ||
    task === "add-reminder-category" ||
    task === "delete-reminder-category" ||
    task === "delete-reminder-item"
  ) {
    const form = document.querySelector<HTMLFormElement>(
      "#reminder-editor-form",
    );
    if (!form) return;
    savedReminders = reminderEditorValue(form);
    if (task === "add-reminder-category" && savedReminders.length < 10)
      savedReminders.push({ title: "", items: [""] });
    if (task === "add-reminder-item" && id !== undefined) {
      const category = savedReminders[Number(id)];
      if (category && category.items.length < 20) category.items.push("");
    }
    if (task === "delete-reminder-category" && id !== undefined) {
      if (!window.confirm(t("confirmDelete"))) return;
      savedReminders = savedReminders.filter(
        (_, index) => index !== Number(id),
      );
    }
    if (task === "delete-reminder-item" && id !== undefined) {
      if (!window.confirm(t("confirmDelete"))) return;
      const [categoryIndex, itemIndex] = id.split("-").map(Number);
      const category = savedReminders[categoryIndex];
      if (category)
        category.items = category.items.filter(
          (_, index) => index !== itemIndex,
        );
    }
    await render();
    return;
  }
  if(task==="deactivate-product"&&!window.confirm(t("confirmDeactivateProduct")))return;
  if (!id || (task.startsWith("delete") && !window.confirm(t("confirmDelete"))))
    return;
  if (
    task === "edit-user" ||
    task === "edit-platform" ||
    task === "edit-product"
  ) {
    const prefix =
      task === "edit-user"
        ? "user-edit"
        : task === "edit-platform"
          ? "platform-edit"
          : "product-edit";
    (
      document.getElementById(`${prefix}-${id}`) as HTMLDialogElement | null
    )?.showModal();
    return;
  }
  const [verb, resource] = task.split("-");
  const path =
    resource === "user"
      ? "/users"
      : resource === "platform"
        ? "/platforms"
        : "/products";
  const expectedActive=verb === "activate";
  const response = await api<StatusResult>(
    `${path}/${id}`,
    verb === "delete"
      ? { method: "DELETE" }
      : {
          method: "PATCH",
          body: JSON.stringify({ active: expectedActive }),
        },
  );
  const r=verifySavedStatus(response,expectedActive);
  if(r.ok&&resource==="user"&&id===getUser()?.id&&verb==="deactivate"){clearSession();window.location.hash="#/login";notice=t("saveSuccess");await render();return;}
  notice = r.ok ? t("saveSuccess") : managementError(r);
  await render();
}

function managementError(result:ApiResult):string {
  if(result.error===t("uploadFailed"))return t("uploadFailed");
  if(result.error==="PASSWORD_TOO_SHORT"||result.error==="Invalid password")return t("passwordMinLength");
  if(result.error==="PASSWORD_CONFIRM_MISMATCH")return t("passwordConfirmationMismatch");
  if(result.error==="Current password is incorrect")return t("currentPasswordIncorrect");
  if(result.error==="New password must differ from current password")return t("newPasswordMustDiffer");
  if(result.error==="STATUS_NOT_SAVED")return t("statusSaveFailed");
  if(result.error==="LAST_ACTIVE_ADMIN")return t("lastActiveAdmin");
  if(result.error==="INVALID_SETTINGS")return t("invalidSettings");
  if(result.error==="INVALID_ROI_THRESHOLDS")return t("invalidRoiThresholds");
  if(result.error==="INVALID_TELEGRAM_CHAT_IDS")return t("invalidTelegramChatIds");
  if(result.error==="TELEGRAM_TEST_FAILED"){
    const reason=(result as TelegramTestResult).reason;
    if(reason==="BOT_TOKEN_MISSING")return t("telegramTestBotTokenMissing");
    if(reason==="BOT_TOKEN_INVALID")return t("telegramTestBotTokenInvalid");
    if(reason==="CHAT_NOT_FOUND")return t("telegramTestChatNotFound");
    if(reason==="BOT_FORBIDDEN")return t("telegramTestBotForbidden");
    if(reason==="RATE_LIMITED")return t("telegramTestRateLimited");
    if(reason==="TELEGRAM_UNREACHABLE")return t("telegramTestUnavailable");
    return t("telegramTestFailed");
  }
  if(result.error==="CANNOT_MOVE_LIVE"){
    const detail=result as ProductActionResult;
    return t("productMoveBlocked").replace("{count}",String(detail.liveCount??0)).replace("{names}",(detail.liveNames??[]).join(", ")||"—");
  }
  return facePhotoError(result.error);
}
function updateElapsed(): void {
  document
    .querySelectorAll<HTMLElement>("[data-live-elapsed]")
    .forEach((element) => {
      const start = element.dataset.startTime;
      if (!start) return;
      const seconds = Math.max(
        0,
        Math.floor((Date.now() - new Date(start).getTime()) / 1000),
      );
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainder = seconds % 60;
      element.textContent = `${t("liveElapsed")} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
    });
}
async function previewCalculation(form: HTMLFormElement): Promise<void> {
  const output = form.querySelector<HTMLElement>("[data-end-calculation]");
  const sessionId = form.dataset.sessionId;
  if (!output || !sessionId) return;
  const result = await api<SessionPreviewResult>(
    `/sessions/${sessionId}/preview`,
    {
      method: "POST",
      body: JSON.stringify({
        verena: Number(field(form, "verena")) || 0,
        affiliate: Number(field(form, "affiliate")) || 0,
        ...(form.dataset.admin === "true" && bangkokInputToIso(field(form, "endTime")) ? { endTime:bangkokInputToIso(field(form, "endTime")) } : {}),
      }),
    },
  );
  if (!result.ok) {
    output.textContent = t("calculationPreview");
    return;
  }
  output.textContent = `${t("totalRevenue")}: ${money(result.totalRevenue)} · ${t("totalOt")}: ${money(result.ot)} · ROI: ${result.roi.toFixed(2)}×`;
}
function mountMonthFilter(): void {
  if (page !== "dashboard") return;
  const nav = document.querySelector("nav");
  if (!nav) return;
  const form = document.createElement("form");
  form.id = "month-filter";
  form.className = "mb-5 flex flex-wrap items-end gap-3";
  const value = dashboardMonth;
  form.innerHTML = `<label class="form-control"><span class="label-text">${t("month")}</span><input required name="month" type="month" value="${esc(value)}" class="input input-bordered"></label><button class="btn btn-primary" type="submit">${t("applyFilters")}</button>`;
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const selected = field(form, "month");
    if (!/^\d{4}-\d{2}$/.test(selected)) return;
    dashboardMonth = selected;
    notice = "";
    void render();
  });
  nav.insertAdjacentElement("afterend", form);
}
function mountHistoryFilters(): void {
  const form = document.querySelector<HTMLFormElement>("#history-filter-form");
  const year = form?.querySelector<HTMLSelectElement>("#history-year");
  const month = form?.querySelector<HTMLSelectElement>("#history-month");
  if (!form || !year || !month) return;
  const syncDates = (): void => {
    const selectedYear = Number(year.value);
    const selectedMonth = Number(month.value);
    if (!selectedYear || !selectedMonth) return;
    const dateFrom = form.elements.namedItem("dateFrom");
    const dateTo = form.elements.namedItem("dateTo");
    const monthText = String(selectedMonth).padStart(2,"0");
    const lastDay = new Date(Date.UTC(selectedYear, selectedMonth, 0)).getUTCDate();
    if (dateFrom instanceof HTMLInputElement) dateFrom.value = `${selectedYear}-${monthText}-01`;
    if (dateTo instanceof HTMLInputElement) dateTo.value = `${selectedYear}-${monthText}-${String(lastDay).padStart(2,"0")}`;
  };
  year.addEventListener("change", syncDates);
  month.addEventListener("change", syncDates);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    const selectedYear = Number(year.value);
    const selectedMonth = Number(month.value);
    if (!selectedYear || !selectedMonth) return;
    const pageSize = Number(field(form,"pageSize"));
    historyMonth = `${selectedYear}-${String(selectedMonth).padStart(2,"0")}`;
    historyFilters = {
      dateFrom:field(form,"dateFrom"),
      dateTo:field(form,"dateTo"),
      staffId:field(form,"staffId"),
      platformId:field(form,"platformId"),
      employmentType:field(form,"employmentType"),
      status:field(form,"status"),
    };
    historyPageSize = pageSize === 50 || pageSize === 100 ? pageSize : 25;
    historyPage = 1;
    notice = "";
    void render();
  });
}
function mountFacePhotoPickers(): void {
  (["startFace", "endFace"] as const).forEach((name) => {
    document.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`).forEach((input, index) => {
      if (input.dataset.facePicker !== undefined) return;
      const container = input.closest<HTMLElement>(".form-control");
      if (!container) return;
      const companion = container.nextElementSibling;
      if (companion instanceof HTMLElement) companion.classList.add("justify-end");
      const previewAttribute = name === "startFace" ? "data-start-face-preview" : "data-end-face-preview";
      container.outerHTML = facePhotoPicker(name, input.required, `${name}-${index}`, previewAttribute);
    });
  });
}
function mountManagementFilters():void {
  const search=document.querySelector<HTMLInputElement>("[data-management-search]");
  const status=document.querySelector<HTMLSelectElement>("[data-management-status]");
  const categoryButtons=[...document.querySelectorAll<HTMLButtonElement>("[data-product-filter]")];
  let category="";
  const apply=()=>{
    const query=(search?.value??"").trim().toLowerCase();
    let visible=0;
    document.querySelectorAll<HTMLElement>("[data-management-row]").forEach((row)=>{
      const show=(!query||(row.dataset.search??"").includes(query))&&(!status?.value||row.dataset.status===status.value)&&(!category||row.dataset.category===category);
      row.classList.toggle("hidden",!show);if(show)visible++;
    });
    document.querySelector<HTMLElement>("[data-management-empty]")?.classList.toggle("hidden",visible!==0);
  };
  search?.addEventListener("input",apply);status?.addEventListener("change",apply);
  categoryButtons.forEach((button)=>button.addEventListener("click",()=>{category=button.dataset.productFilter??"";categoryButtons.forEach((item)=>item.classList.toggle("btn-primary",item===button));apply();}));
}
function mountPlatformPreviews():void {
  document.querySelectorAll<HTMLFormElement>("form[data-platform-form]").forEach((form)=>{
    const name=form.elements.namedItem("name");const icon=form.elements.namedItem("icon");const color=form.elements.namedItem("color");const hex=form.querySelector<HTMLInputElement>("[data-color-hex]");const preview=form.querySelector<HTMLElement>("[data-platform-preview]");
    if(!(name instanceof HTMLInputElement)||!(icon instanceof HTMLInputElement)||!(color instanceof HTMLInputElement)||!preview)return;
    const paint=()=>{preview.innerHTML=`<span class="mr-2 text-2xl" style="color:${esc(color.value)}">${esc(icon.value||"●")}</span><strong>${esc(name.value||t("platformPreview"))}</strong>`;if(hex)hex.value=color.value;};
    [name,icon,color].forEach((input)=>input.addEventListener("input",paint));
    hex?.addEventListener("change",()=>{if(/^#[0-9a-f]{6}$/i.test(hex.value)){color.value=hex.value;paint();}else hex.value=color.value;});
  });
}
function paintImagePreviews(container: HTMLElement | null, selected: File[]): void {
  if (!container) return;
  container.replaceChildren();
  container.classList.toggle("hidden", selected.length === 0);
  if (!selected.length) return;
  const face = container.dataset.imagePreviewMode === "face";
  container.className = face
    ? "mt-3 grid max-w-sm gap-2"
    : "mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5";
  selected.forEach((file) => {
    const figure = document.createElement("figure");
    figure.className = "min-w-0 overflow-hidden rounded-box border border-base-300 bg-base-200 p-2";
    const image = document.createElement("img");
    const source = URL.createObjectURL(file);
    image.src = source;
    image.alt = file.name;
    image.className = face
      ? "aspect-[4/3] w-full rounded-lg bg-base-100 object-contain"
      : "aspect-square w-full rounded-lg bg-base-100 object-cover";
    image.addEventListener("load", () => URL.revokeObjectURL(source), { once: true });
    image.addEventListener("error", () => URL.revokeObjectURL(source), { once: true });
    const caption = document.createElement("figcaption");
    caption.className = "mt-2 truncate text-xs text-base-content/70";
    caption.textContent = file.name;
    caption.title = file.name;
    figure.append(image, caption);
    container.append(figure);
  });
}
function mountSupportingImagePickers(): void {
  document
    .querySelectorAll<HTMLInputElement>("input[data-supporting-images]")
    .forEach((input) => {
      let accumulated: File[] = [];
      const fieldContainer = input.closest<HTMLElement>(".form-control, section");
      const existingPreview = fieldContainer?.querySelector<HTMLElement>("[data-image-preview]");
      const preview = existingPreview ?? document.createElement("div");
      if (!existingPreview) {
        preview.dataset.imagePreview = "";
        preview.dataset.imagePreviewMode = "supporting";
        preview.className = "mt-3 hidden";
        preview.setAttribute("aria-live", "polite");
        fieldContainer?.append(preview);
      }
      input.addEventListener("change", () => {
        const sessionId = input.form?.dataset.sessionId;
        const draftKey = sessionId ? endDraftKey(sessionId) : null;
        const isStartDraft = input.form?.id === "start-live-form";
        if (!accumulated.length) {
          accumulated = draftKey
            ? [...(restoredEndDraftFiles.get(draftKey)?.images ?? [])]
            : isStartDraft ? [...(restoredStartDraftFiles?.images ?? [])] : [];
        }
        const additions = [...(input.files ?? [])];
        const known = new Set(
          accumulated.map((file) => `${file.name}|${file.size}|${file.lastModified}`),
        );
        for (const file of additions) {
          const key = `${file.name}|${file.size}|${file.lastModified}`;
          if (!known.has(key) && accumulated.length < 5) {
            accumulated.push(file);
            known.add(key);
          }
        }
        const transfer = new DataTransfer();
        accumulated.forEach((file) => transfer.items.add(file));
        input.files = transfer.files;
        if (draftKey) {
          const existing = restoredEndDraftFiles.get(draftKey);
          restoredEndDraftFiles.set(draftKey, { face: existing?.face ?? [], images: accumulated });
        } else if (isStartDraft) {
          restoredStartDraftFiles = { face: restoredStartDraftFiles?.face ?? [], images: accumulated };
        }
        const count = input
          .closest<HTMLElement>(".form-control, section")
          ?.querySelector<HTMLElement>("[data-supporting-images-count]");
        if (count) {
          count.textContent = currentLanguage() === "th"
            ? `เลือกแล้ว ${accumulated.length}/5 รูป`
            : `${accumulated.length}/5 images selected`;
        }
        paintImagePreviews(preview, accumulated);
      });
    });
}
function mountSettingsTools():void {
  const form=document.querySelector<HTMLFormElement>("#settings-form");
  if(!form)return;
  const number=(name:string,fallback:number):number=>{
    const value=Number(field(form,name));
    return Number.isFinite(value)?value:fallback;
  };
  const paint=()=>{
    const partTimeRate=number("partTimeRate",100);const fullTimeRate=number("fullTimeRate",50);const commissionRate=number("commRate",5);const roiGood=number("roiGood",4);const roiOk=number("roiOk",2);
    const partTimePay=Math.round(2*partTimeRate);const fullTimePay=Math.round(2*fullTimeRate);const commission=Math.round(4000*commissionRate/100);const revenue=10000+commission;const roiValue=partTimePay>0?revenue/partTimePay:0;
    const set=(selector:string,value:string)=>{const output=form.querySelector<HTMLElement>(selector);if(output)output.textContent=value;};
    set("[data-settings-preview-part-time]",money(partTimePay));set("[data-settings-preview-full-time]",money(fullTimePay));set("[data-settings-preview-commission]",money(commission));set("[data-settings-preview-revenue]",money(revenue));set("[data-settings-preview-roi]",`${roiValue.toFixed(2)}×`);
    set("[data-settings-roi-good]",`≥ ${roiGood}× ${t("roiExcellent")}`);set("[data-settings-roi-ok]",`${roiOk}–${roiGood}× ${t("roiAcceptable")}`);set("[data-settings-roi-bad]",`< ${roiOk}× ${t("roiBelowTarget")}`);
  };
  form.querySelectorAll<HTMLInputElement>('input[type="number"]').forEach((input)=>input.addEventListener("input",paint));
  form.addEventListener("reset",()=>window.setTimeout(paint,0));
  form.querySelector<HTMLButtonElement>("[data-test-telegram]")?.addEventListener("click",async(event)=>{
    const button=event.currentTarget as HTMLButtonElement;button.disabled=true;
    const result=await api<TelegramTestResult>("/settings/test-telegram",{method:"POST",body:JSON.stringify({telegramChatIds:field(form,"telegramChatIds")})});
    notice=result.ok?t("telegramTestSuccess").replace("{count}",String(result.sent??0)):managementError(result);
    await render();
  });
  paint();
}
function bind(): void {
  mountMonthFilter();
  mountHistoryFilters();
  mountFacePhotoPickers();
  mountManagementFilters();
  mountPlatformPreviews();
  mountSupportingImagePickers();
  mountSettingsTools();
  const startNote = document.querySelector<HTMLTextAreaElement>("[data-start-note]");
  const startNoteCount = document.querySelector<HTMLElement>("[data-start-note-count]");
  startNote?.addEventListener("input", () => {
    if (startNoteCount) startNoteCount.textContent = String(startNote.value.length);
  });
  document
    .querySelectorAll<HTMLInputElement>("[data-face-picker]")
    .forEach((input) =>
      input.addEventListener("change", () => {
        const fileName = input.parentElement?.querySelector<HTMLElement>(
          "[data-file-name]",
        );
        if (fileName)
          fileName.textContent = input.files?.[0]?.name ?? t("noFileChosen");
        paintImagePreviews(
          input.closest<HTMLElement>(".form-control")
            ?.querySelector<HTMLElement>("[data-image-preview]") ?? null,
          [...(input.files ?? [])],
        );
      }),
    );
  document.querySelectorAll<HTMLInputElement>("[data-profile-picker]").forEach((input) =>
    input.addEventListener("change", () => {
      const fieldContainer=input.closest<HTMLElement>(".form-control");
      const fileName=fieldContainer?.querySelector<HTMLElement>("[data-profile-file-name]");
      if(fileName)fileName.textContent=input.files?.[0]?.name??"";
      const file=input.files?.[0];
      const preview=fieldContainer?.querySelector<HTMLElement>("[data-profile-preview]");
      if(file&&preview){
        const url=URL.createObjectURL(file);
        const image=document.createElement("img");
        image.src=url;
        image.alt=t("profileImage");
        image.className="h-full w-full object-cover";
        image.addEventListener("load",()=>URL.revokeObjectURL(url),{once:true});
        image.addEventListener("error",()=>URL.revokeObjectURL(url),{once:true});
        preview.replaceChildren(image);
      }
    }),
  );
  document.querySelectorAll<HTMLInputElement>("[data-platform-image-picker]").forEach((input) =>
    input.addEventListener("change", () => {
      const fieldContainer=input.closest<HTMLElement>(".form-control");
      const fileName=fieldContainer?.querySelector<HTMLElement>("[data-platform-image-file-name]");
      if(fileName)fileName.textContent=input.files?.[0]?.name??"";
      const file=input.files?.[0];
      const preview=fieldContainer?.querySelector<HTMLElement>("[data-platform-image-preview]");
      if(file&&preview){
        const url=URL.createObjectURL(file);
        const image=document.createElement("img");
        image.src=url;
        image.alt=t("shopProfilePhoto");
        image.className="h-full w-full object-cover";
        image.addEventListener("load",()=>URL.revokeObjectURL(url),{once:true});
        image.addEventListener("error",()=>URL.revokeObjectURL(url),{once:true});
        preview.replaceChildren(image);
      }
    }),
  );
  document.querySelector<HTMLButtonElement>("#open-self-profile")?.addEventListener("click",()=>
    document.querySelector<HTMLDialogElement>("#self-profile-dialog")?.showModal(),
  );
  document.querySelector<HTMLButtonElement>("[data-save-self-profile]")?.addEventListener("click",(event) => {
    const form=(event.currentTarget as HTMLButtonElement).closest<HTMLFormElement>("form");
    if(form)void saveSelfProfile(form);
  });
  document.querySelectorAll<HTMLFormElement>("form").forEach((form) => {
    if (form.id === "month-filter" || form.id === "history-filter-form") return;
    if (form.id === "end-live-form" || form.id === "admin-end-live-form") {
      form.querySelector<HTMLButtonElement>("[data-end-session-submit]")?.addEventListener("click", () => void finishSession(form));
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        void finishSession(form);
      });
      const queuePreview = () => {
        if (calculationTimer !== undefined) window.clearTimeout(calculationTimer);
        calculationTimer = window.setTimeout(() => void previewCalculation(form), 250);
      };
      const verenaInput = form.elements.namedItem("verena");
      const affiliateInput = form.elements.namedItem("affiliate");
      const endTimeInput = form.elements.namedItem("endTime");
      if (verenaInput instanceof HTMLInputElement) verenaInput.addEventListener("input", queuePreview);
      if (affiliateInput instanceof HTMLInputElement) affiliateInput.addEventListener("input", queuePreview);
      if (endTimeInput instanceof HTMLInputElement) endTimeInput.addEventListener("change", queuePreview);
      form.addEventListener("input", () => queueEndDraftSave(form));
      form.addEventListener("change", () => queueEndDraftSave(form));
      void restoreEndDraft(form).then(() => void previewCalculation(form));
      return;
    }
    if (form.id === "start-live-form") {
      form.addEventListener("input", () => queueStartDraftSave(form));
      form.addEventListener("change", () => queueStartDraftSave(form));
      void restoreStartDraft(form);
    }
    if (form.id === "session-edit-form") {
      bindSessionEditForm(form);
      return;
    }
    if (form.id === "session-validation-form") {
      bindValidationForm(form);
      return;
    }
    if (form.id === "manager-report-form")
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        managerFilters = {
          dateFrom: field(form, "dateFrom"),
          dateTo: field(form, "dateTo"),
          staffId: field(form, "staffId"),
          platformId: field(form, "platformId"),
          employmentType: field(form, "employmentType"),
        };
        notice = "";
        void render();
      });
    if (form.id === "audit-filter")
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        auditFilters = { dateFrom:field(form,"dateFrom"), dateTo:field(form,"dateTo"), userId:field(form,"userId"), action:field(form,"action"), success:field(form,"success"), page:1 };
        notice = "";
        void render();
      });
    if (form.id === "schedule-build-filter")
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        scheduleBuildMonth = `${field(form, "year")}-${field(form, "month").padStart(2, "0")}`;
        scheduleBuildStaffId = field(form, "staffId");
        scheduleBuilder = null;
        scheduleBuilderError = "";
        scheduleBuildRequested = true;
        void render();
      });
    if (form.id === "schedule-planned-filter")
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        schedulePlannedMonth = `${field(form, "year")}-${field(form, "month").padStart(2, "0")}`;
        schedulePlannedStaffId = field(form, "staffId");
        schedulePlannedPlatformId = field(form, "platformId");
        void render();
      });
    if (form.id === "schedule-create-form" || form.id === "schedule-edit-form")
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        const endsNextDay = form.elements.namedItem("endsNextDay") instanceof HTMLInputElement && (form.elements.namedItem("endsNextDay") as HTMLInputElement).checked;
        if (endsNextDay ? field(form, "endTime") >= field(form, "startTime") : field(form, "endTime") <= field(form, "startTime")) {
          notice = t("invalidScheduleTime");
          void render();
          return;
        }
        const editId = field(form, "id");
        void api<ApiResult>(editId ? `/schedules/${editId}` : "/schedules", {
          method: editId ? "PATCH" : "POST",
          body: JSON.stringify({ staffId:field(form,"staffId"), platformId:field(form,"platformId"), date:field(form,"date"), startTime:field(form,"startTime"), endTime:field(form,"endTime"), endsNextDay, note:field(form,"note") }),
        }).then((result) => {
          notice = result.ok
            ? t("scheduleSaved")
            : (result.error ?? t("requestFailed"));
          void render();
        });
      });
    form.addEventListener("submit", submit);
  });
  if (document.querySelector("[data-live-elapsed]")) {
    updateElapsed();
    liveElapsedTimer = window.setInterval(updateElapsed, 1000);
  }
  document
    .querySelectorAll<HTMLButtonElement>("[data-page]")
    .forEach((button) =>
      button.addEventListener("click", () => {
        const nextPage = button.dataset.page as Page;
        notice = "";
        window.location.hash = `#/${nextPage}`;
      }),
    );
  const managerYear = document.querySelector<HTMLSelectElement>("#manager-year");
  const managerMonth = document.querySelector<HTMLSelectElement>("#manager-month");
  const syncManagerMonth = (): void => {
    const form = document.querySelector<HTMLFormElement>("#manager-report-form");
    if (!form || !managerYear || !managerMonth) return;
    const year = Number(managerYear.value);
    const month = Number(managerMonth.value);
    if (!year || !month) return;
    const monthText = String(month).padStart(2, "0");
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const from = form.elements.namedItem("dateFrom");
    const to = form.elements.namedItem("dateTo");
    if (from instanceof HTMLInputElement) from.value = `${year}-${monthText}-01`;
    if (to instanceof HTMLInputElement) to.value = `${year}-${monthText}-${String(lastDay).padStart(2, "0")}`;
  };
  managerYear?.addEventListener("change", syncManagerMonth);
  managerMonth?.addEventListener("change", syncManagerMonth);
  document.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-builder-field]").forEach((input) =>
    input.addEventListener("input", () => {
      const dateValue = input.dataset.builderDate;
      const index = Number(input.dataset.builderIndex);
      const fieldName = input.dataset.builderField as keyof Pick<BuilderSlot, "platformId" | "startTime" | "endTime" | "note"> | undefined;
      const slot = dateValue && scheduleBuilder?.days[dateValue]?.[index];
      if (slot && fieldName) {
        slot[fieldName] = input.value;
        if (fieldName === "startTime" || fieldName === "endTime") {
          const output = document.querySelector<HTMLElement>(`[data-builder-duration="${dateValue}|${index}"]`);
          if (output) output.textContent = `${scheduleHours(slot.startTime, slot.endTime)}h`;
        }
      }
    }),
  );
  document.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-builder-field]").forEach((input) =>
    input.addEventListener("change", () => {
      if (input.dataset.builderField !== "note") {
        scheduleWarningsConfirmed = false;
        void renderScheduleBuilderAtScroll();
      }
    }),
  );
  document.querySelectorAll<HTMLSelectElement>("[data-builder-time-select]").forEach((select) => {
    const part = select.dataset.builderTimeSelect as "hour" | "minute" | undefined;
    if (!part) return;
    select.addEventListener("change", () => {
      if (scheduleTimePicker) scheduleTimePicker[part] = select.value;
    });
    if (part === "minute") select.addEventListener("dblclick", applyBuilderTimePicker);
  });
  document
    .querySelectorAll<HTMLButtonElement>("[data-history-sort]")
    .forEach((button) =>
      button.addEventListener("click", () => {
        const key = button.dataset.historySort as HistorySortKey | undefined;
        if (!key) return;
        historySort = historySort?.key !== key
          ? { key, direction: "asc" }
          : historySort.direction === "asc"
            ? { key, direction: "desc" }
            : null;
        historyPage = 1;
        void render();
      }),
    );
  document
    .querySelectorAll<HTMLButtonElement>("[data-action]")
    .forEach((button) => {
      if (button.dataset.action === "history-page") {
        button.addEventListener("click", () => {
          historyPage = Math.max(1, Number(button.dataset.id ?? 1));
          void render();
        });
        return;
      }
      if (button.dataset.action === "audit-page") {
        button.addEventListener("click", () => {
          auditFilters.page = Math.max(1,Number(button.dataset.id ?? 1));
          void render();
        });
        return;
      }
      if (button.dataset.action === "schedule-mode") {
        button.addEventListener("click", () => {
          if (button.dataset.id === "build" || button.dataset.id === "planned") scheduleMode = button.dataset.id;
          void render();
        });
        return;
      }
      if (button.dataset.action === "open-schedule-dialog") {
        button.addEventListener("click", () => {
          const id = button.dataset.id;
          if (id) document.querySelector<HTMLDialogElement>(`#${id}`)?.showModal();
        });
        return;
      }
      if (button.dataset.action === "open-builder-time-picker") {
        button.addEventListener("click", () => {
          const [dateValue, rawIndex, field] = (button.dataset.id ?? "").split("|");
          const slot = scheduleBuilder?.days[dateValue]?.[Number(rawIndex)];
          if (!slot || (field !== "startTime" && field !== "endTime")) return;
          const [hour, minute] = slot[field].split(":");
          scheduleTimePicker = { date:dateValue, index:Number(rawIndex), field, hour, minute };
          void renderScheduleBuilderAtScroll().then(() => document.querySelector<HTMLElement>("[data-builder-time-picker]")?.focus());
        });
        return;
      }
      if (button.dataset.action === "apply-builder-time-picker") {
        button.addEventListener("click", applyBuilderTimePicker);
        return;
      }
      if (button.dataset.action === "add-builder-slot") {
        button.addEventListener("click", () => {
          const dateValue = button.dataset.id;
          const platformId = savedPlatforms.find((platform) => platform.active)?.id ?? "";
          if (dateValue && scheduleBuilder) scheduleBuilder.days[dateValue]?.push({ platformId, startTime:"14:00", endTime:"16:00", endsNextDay:false, note:"", locked:false });
          scheduleWarningsConfirmed = false;
          void renderScheduleBuilderAtScroll();
        });
        return;
      }
      if (button.dataset.action === "remove-builder-slot") {
        button.addEventListener("click", () => {
          const [dateValue, rawIndex] = (button.dataset.id ?? "").split("|");
          scheduleBuilder?.days[dateValue]?.splice(Number(rawIndex), 1);
          scheduleWarningsConfirmed = false;
          void renderScheduleBuilderAtScroll();
        });
        return;
      }
      if (button.dataset.action === "toggle-builder-next-day") {
        button.addEventListener("click", () => {
          const [dateValue, rawIndex] = (button.dataset.id ?? "").split("|");
          const slot = scheduleBuilder?.days[dateValue]?.[Number(rawIndex)];
          if (slot) slot.endsNextDay = !slot.endsNextDay;
          scheduleWarningsConfirmed = false;
          void renderScheduleBuilderAtScroll();
        });
        return;
      }
      if (button.dataset.action === "builder-day-off") {
        button.addEventListener("click", () => {
          const dateValue = button.dataset.id;
          if (dateValue && scheduleBuilder) scheduleBuilder.days[dateValue] = scheduleBuilder.days[dateValue]?.filter((slot) => slot.locked) ?? [];
          scheduleWarningsConfirmed = false;
          void renderScheduleBuilderAtScroll();
        });
        return;
      }
      if (button.dataset.action === "save-schedule-builder") {
        button.addEventListener("click", () => {
          if (!scheduleBuilder) return;
          const issues = [...validateScheduleBuilder().values()].flat();
          if (issues.some((issue) => issue.severity === "error")) { notice=currentLanguage() === "th" ? "กรุณาแก้ไขตารางที่มีกรอบและข้อความสีแดงก่อนบันทึก" : "Fix the red schedule errors before saving."; void render(); return; }
          if (issues.some((issue) => issue.severity === "warning") && !scheduleWarningsConfirmed) { scheduleWarningsConfirmed=true; notice=currentLanguage() === "th" ? "กรุณาตรวจสอบคำเตือน แล้วกด “ยืนยันบันทึกต่อ” อีกครั้ง" : "Review the warnings, then click Confirm save."; void render(); return; }
          const slots = Object.entries(scheduleBuilder.days).flatMap(([dateValue, daySlots]) => daySlots.filter((slot) => !slot.locked).map((slot) => ({ scheduleId:slot.scheduleId, date:dateValue, platformId:slot.platformId, startTime:slot.startTime, endTime:slot.endTime, endsNextDay:slot.endsNextDay, note:slot.note })));
          if (slots.some((slot) => !slot.platformId || (slot.endsNextDay ? slot.endTime >= slot.startTime : slot.endTime <= slot.startTime))) { notice=t("invalidScheduleTime"); void render(); return; }
          void api<ApiResult>("/schedules/month", { method:"POST", body:JSON.stringify({ staffId:scheduleBuilder.staffId, month:scheduleBuilder.month, slots }) }).then((result) => {
            notice = result.ok ? t("scheduleSaved") : (result.error ?? t("requestFailed"));
            if (result.ok) { scheduleBuilder=null; scheduleBuildRequested=true; }
            void render();
          });
        });
        return;
      }
      if (button.dataset.action === "open-manager-export") {
        button.addEventListener("click", () => document.querySelector<HTMLDialogElement>("#manager-export-dialog")?.showModal());
        return;
      }
      if (button.dataset.action === "export-manager-html") {
        button.addEventListener("click", () => {
          const language = button.dataset.id;
          if (language !== "th" && language !== "en") return;
          managerExportLanguage = language;
          void render();
        });
        return;
      }
      if (button.dataset.action === "back-manager-report") {
        button.addEventListener("click", () => {
          managerExportLanguage = null;
          void render();
        });
        return;
      }
      if (button.dataset.action === "print-manager-report") {
        button.addEventListener("click", () => {
          button.disabled = true;
          button.classList.add("loading");
          void imageHydrationPromise.finally(() => {
            button.disabled = false;
            button.classList.remove("loading");
            window.print();
          });
        });
        return;
      }
      if (button.dataset.action === "refresh-manager-report") {
        button.addEventListener("click", () => void render());
        return;
      }
      if (button.dataset.action === "validate-session") {
        button.addEventListener("click", async () => {
          const id = button.dataset.id;
          if (!id) return;
          const session = managerReportSnapshot?.sessions.find((row) => row.sessionId === id);
          if (!session) return;
          const productsResult = await productsWithOfflineCache(false);
          const products = productsResult.ok ? productsResult.products : savedProducts;
          const dialogId = `session-validation-${id}`;
          document.querySelector<HTMLDialogElement>(`#${dialogId}`)?.remove();
          document.body.insertAdjacentHTML("beforeend", validationDialog(session, products));
          const dialog = document.querySelector<HTMLDialogElement>(`#${dialogId}`);
          const form = dialog?.querySelector<HTMLFormElement>("#session-validation-form");
          if (form) bindValidationForm(form);
          dialog?.querySelector<HTMLElement>("[data-close-dialog]")?.addEventListener("click", () => dialog.close());
          dialog?.showModal();
        });
        return;
      }
      if (button.dataset.action === "cancel-schedule") {
        button.addEventListener("click", () => {
          const id = button.dataset.id;
          if (!id || !window.confirm(t("confirmDelete"))) return;
          void api<ApiResult>(`/schedules/${id}`, { method: "DELETE" }).then(
            (result) => {
              notice = result.ok
                ? t("saveSuccess")
                : (result.error ?? t("requestFailed"));
              void render();
            },
          );
        });
        return;
      }
      button.addEventListener("click", () => {
        void act(button);
      });
    });
  document
    .querySelectorAll<HTMLButtonElement>("[data-close-dialog]")
    .forEach((button) =>
      button.addEventListener("click", () =>
        button.closest<HTMLDialogElement>("dialog")?.close(),
      ),
    );
  document
    .querySelector<HTMLButtonElement>("#signout")
    ?.addEventListener("click", () => void exitSession("manual"));
  document
    .querySelectorAll<HTMLButtonElement>("[data-language]")
    .forEach((button) =>
      button.addEventListener("click", () => {
        const language = button.dataset.language;
        if (language === "th" || language === "en") {
          applyLang(language);
          void render();
        }
      }),
    );
  document.querySelector<HTMLElement>("[data-builder-time-picker]")?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyBuilderTimePicker();
    }
    if (event.key === "Escape") {
      scheduleTimePicker = null;
      void renderScheduleBuilderAtScroll();
    }
  });
}

async function bootstrap(): Promise<void> {
  installIdleTracking();
  void pruneEndDrafts();
  const token = getToken();
  const user = getUser();
  if (token && user) {
    try {
      const me = await api<MeResult>("/auth/me");
      if (!me.ok && me.code === 401) {
        await exitSession("expired", false);
        return;
      }
      if (me.ok) {
        saveSession(token, me.user);
        const active = await api<ActiveResult>("/sessions/active");
        if (active.ok) idleActiveSessionId = active.session?.sessionId ?? null;
      }
    } catch {
      // Keep the local session during a temporary network outage.
    }
    const previousActivity = Number(localStorage.getItem(idleLastActivityKey));
    if (
      Number.isFinite(previousActivity) &&
      previousActivity > 0 &&
      Date.now() - previousActivity >= idleLimit()
    ) {
      await exitSession("idle");
      return;
    }
    recordIdleActivity(true);
  } else {
    stopIdleTimers();
  }
  await render();
}

void bootstrap();

function forgotError(error: string | undefined): string {
  if (error === "INVALID_EMAIL") return t("invalidEmail");
  return t("requestFailed");
}
document.addEventListener(
  "submit",
  (event) => {
    const target = event.target;
    if (
      !(target instanceof HTMLFormElement) ||
      (target.id !== "forgot-form" && target.id !== "reset-form")
    )
      return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (target.id === "forgot-form") {
      void api<ApiResult>("/auth/forgot", {
        method: "POST",
        body: JSON.stringify({ email: field(target, "email") }),
      }).then((result) => {
        notice = result.ok ? t("resetSent") : forgotError(result.error);
        void render();
      });
      return;
    }
    const kind = field(target, "kind");
    const token = field(target, "token");
    const newPassword = field(target, "newPassword");
    if (newPassword.length < 8) {
      notice = t("passwordMinLength");
      void render();
      return;
    }
    void api<LoginResult>(
      kind === "activate" ? "/auth/activate" : "/auth/reset",
      { method: "POST", body: JSON.stringify({ token, newPassword }) },
    ).then((result) => {
      if (result.ok && kind === "activate") {
        saveSession(result.token, result.user);
        idleActiveSessionId = null;
        recordIdleActivity(true);
        notice = "";
        window.location.hash = "#/live";
        void render();
        return;
      }
      notice = result.ok
        ? t("saveSuccess")
        : (result.error ?? t("requestFailed"));
      if (result.ok) window.location.hash = "#/login";
      void render();
    });
  },
  true,
);
window.addEventListener("hashchange", () => {
  notice = "";
  void render();
});

function passwordEyeIcon(revealed: boolean): string {
  return revealed
    ? `<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 3 18 18"></path><path d="M10.6 10.7a2 2 0 0 0 2.7 2.7"></path><path d="M9.9 4.2A10.5 10.5 0 0 1 12 4c5 0 8.5 4 9.5 6.2a4.2 4.2 0 0 1 0 3.6 12 12 0 0 1-2 3"></path><path d="M6.6 6.6a12.3 12.3 0 0 0-4.1 5.6 4.2 4.2 0 0 0 0 3.6C3.5 18 7 22 12 22a10.5 10.5 0 0 0 4.2-.9"></path></svg>`
    : `<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 12.2C3.5 10 7 6 12 6s8.5 4 9.5 6.2a4.2 4.2 0 0 1 0 3.6C20.5 18 17 22 12 22S3.5 18 2.5 15.8a4.2 4.2 0 0 1 0-3.6Z"></path><circle cx="12" cy="14" r="3"></circle></svg>`;
}

function installPasswordToggles(): void {
  document
    .querySelectorAll<HTMLInputElement>('input[type="password"]')
    .forEach((input) => {
      if (input.dataset.eyeToggle === "true") return;
      input.dataset.eyeToggle = "true";
      const wrapper = document.createElement("div");
      wrapper.className = "relative";
      input.parentNode?.insertBefore(wrapper, input);
      wrapper.appendChild(input);
      input.classList.add("w-full", "pr-12");
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className =
        "absolute right-1 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full text-base-content/60 transition hover:bg-base-200 hover:text-base-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary";
      toggle.innerHTML = passwordEyeIcon(false);
      toggle.setAttribute("aria-label", t("showPassword"));
      toggle.title = t("showPassword");
      toggle.addEventListener("click", () => {
        const reveal = input.type === "password";
        input.type = reveal ? "text" : "password";
        toggle.innerHTML = passwordEyeIcon(reveal);
        toggle.setAttribute(
          "aria-label",
          t(reveal ? "hidePassword" : "showPassword"),
        );
        toggle.title = t(reveal ? "hidePassword" : "showPassword");
      });
      wrapper.appendChild(toggle);
    });
}
installPasswordToggles();
new MutationObserver(installPasswordToggles).observe(root, {
  childList: true,
  subtree: true,
});
